import { FormData } from 'formdata-node';
import axios from 'axios';

const constructFormDataFromObjectLegacy = (record) => {
    const formData = new FormData();
    Object.entries(record).forEach(([key, value]) => {
        if (Array.isArray(value))
            value.forEach((v) => formData.append(`${key}[]`, v));
        else
            formData.append(key, value);
    });
    return formData;
};
const constructFormDataFromObject = (obj, form = new FormData(), namespace = "") => {
    for (const property in obj) {
        if (obj[property] !== undefined) {
            const formKey = namespace ? `${namespace}[${property}]` : property;
            const value = obj[property];
            if (Array.isArray(value)) {
                // Handle arrays explicitly
                value.forEach((item) => {
                    const arrayKey = `${formKey}[]`;
                    if (typeof item === "object" &&
                        !(item instanceof File) &&
                        !(item instanceof Blob)) {
                        // Recursively handle nested objects in the array
                        constructFormDataFromObject(item, form, arrayKey);
                    }
                    else {
                        // Append simple values or File/Blob in the array
                        form.append(arrayKey, item);
                    }
                });
            }
            // Check if the value is an object and not a File/Blob (to handle nested objects)
            else if (typeof value === "object" &&
                !(value instanceof File) &&
                !(value instanceof Blob)) {
                // Recursively call the function for nested objects
                constructFormDataFromObject(value, form, formKey);
            }
            else {
                // Append the value directly for primitive types or File/Blob objects
                form.append(formKey, value);
            }
        }
    }
    return form;
};

const COMPLIANCE_PREFIX = "/lb/compliance";
const COMPLIANCE_API_END_POINT_EDIT_PHONE = "/member/natural_person/phone";
const COMPLIANCE_API_END_POINT_MEMBER_STATUS = "/member/status";
const COMPLIANCE_API_END_POINT_CUSTOMER = "/member/customer";
const COMPLIANCE_API_END_POINT_NATURAL_PERSON_DATA = "/member/natural-person";
const COMPLIANCE_API_END_POINT_JURIDICAL_PERSON_DATA = "/member/juridical-person";
const COMPLIANCE_API_END_POINT_VIDEO = "/member/video";
const COMPLIANCE_API_END_POINT_ID_DOCUMENTS = "/member/id";
const COMPLIANCE_API_END_POINT_EXTRA_DOCUMENTS = "/member/extra";
const COMPLIANCE_API_END_POINT_DOCUMENT_TYPE = "/public/national_id_type";
const COMPLIANCE_API_END_POINT_PUBLIC_GET_TERMS_AND_CONDITIONS = "/public/terms_and_conditions";
const COMPLIANCE_API_END_POINT_ADMIN_VERIFICATION_DATA = "/admin/members/verification_data";
const COMPLIANCE_API_END_POINT_ADMIN_VERIFICATION = "/admin/verification";
const COMPLIANCE_API_END_POINT_ADMIN_POST_TERMS_AND_CONDITIONS = "/admin/terms_and_conditions";
const COMPLIANCE_API_END_POINT_ADMIN_POST_TYCS_CONSTRAINS = "/admin/terms_and_conditions/constrains";
const COMPLIANCE_API_END_POINT_GET_MEMBER = "/management/members/get";
const COMPLIANCE_API_END_POINT_GET_MEMBER_INFORMATION_REQUEST = "/member/information_requests";
const COMPLIANCE_API_END_POINT_GET_MEMBER_INFORMATION_REQUEST_TEMPLATE = "/member/information_request_templates";
const COMPLIANCE_API_END_POINT_POST_MEMBER_INFORMATION_REQUEST = "/member/information_requests/reply";
const COMPLIANCE_API_END_POINT_NOTIFY_MEMBER_INFORMATION_REQUEST = "/member/information_requests/notify_admins";
const COMPLIANCE_API_END_POINT_MEMBER_APPLY_INFORMATION_REQUEST = "/member/information_requests/from_template";
const COMPLIANCE_API_END_POINT_MEMBER_GET_FLOW_NEXT = "/member/flows/next";
const COMPLIANCE_API_END_POINT_MEMBER_FLOW_NEW = "/member/flows/new";
const COMPLIANCE_API_END_POINT_MEMBER_FLOW_UPLOAD = "/member/flows/upload";
const COMPLIANCE_API_END_POINT_MEMBER_GET_TERMS_AND_CONDITIONS = "/member/terms_and_conditions";
const COMPLIANCE_API_END_POINT_MEMBER_ACCEPT_TERMS_AND_CONDITIONS = "/member/terms_and_conditions/accept";
const COMPLIANCE_API_END_POINT_MEMBER_GET_ACCEPTED_TERMS_AND_CONDITIONS = "/member/terms_and_conditions/accepted/all";
const COMPLIANCE_API_END_POINT_MEMBER_GET_UPDATED_TERMS_AND_CONDITIONS = "/member/terms_and_conditions/accepted/updates";
const COMPLIANCE_API_END_POINT_PARTNER_MEMBERS = "/partner/members";
const COMPLIANCE_API_END_POINT_MEMBER_ULTIMATE_BENEFICIAL_OWNERS = "/member/ultimate_beneficial_owners";
const COMPLIANCE_API_END_POINT_MEMBER_ULTIMATE_BENEFICIAL_OWNERS_DOCUMENT = (ultimate_beneficial_owner_id) => `/member/ultimate_beneficial_owners/${ultimate_beneficial_owner_id}/documents`;
const COMPLIANCE_API_END_POINT_MEMBER_ULTIMATE_BENEFICIAL_OWNERS_DELETE = (ultimate_beneficial_owner_id) => `/member/ultimate_beneficial_owners/${ultimate_beneficial_owner_id}`;

class ComplianceMember {
    constructor(authenticationService) {
        this.url = authenticationService.serviceConfig.noPrefix
            ? `${authenticationService.serviceConfig.url}`
            : `${authenticationService.serviceConfig.url}${COMPLIANCE_PREFIX}`;
        this.authenticationService = authenticationService;
    }
    async getExtraDocuments(params) {
        return await this.authenticationService.fetch({
            url: `${this.url}${COMPLIANCE_API_END_POINT_EXTRA_DOCUMENTS}`,
            method: "GET",
            params,
        });
    }
    async uploadExtraDocument(extraDocument) {
        const extraDocumentForm = constructFormDataFromObjectLegacy(extraDocument);
        return await this.authenticationService.fetch({
            data: extraDocumentForm,
            url: `${this.url}${COMPLIANCE_API_END_POINT_EXTRA_DOCUMENTS}`,
            method: "POST",
        });
    }
    async submitDocumentId(idDocumentData) {
        const documentForm = constructFormDataFromObjectLegacy(idDocumentData);
        return await this.authenticationService.fetch({
            data: documentForm,
            url: `${this.url}${COMPLIANCE_API_END_POINT_ID_DOCUMENTS}`,
            method: "POST",
            params: { verification_flow: idDocumentData.verification_flow },
        });
    }
    async getMemberStatus(params) {
        return await this.authenticationService.fetch({
            method: "GET",
            url: `${this.url}${COMPLIANCE_API_END_POINT_MEMBER_STATUS}`,
            params,
        });
    }
    async getJuridicalPerson() {
        return await this.authenticationService.fetch({
            method: "GET",
            url: `${this.url}${COMPLIANCE_API_END_POINT_JURIDICAL_PERSON_DATA}`,
        });
    }
    async submitJuridicalPerson(juridicalPersonData) {
        return await this.authenticationService.fetch({
            method: "POST",
            url: `${this.url}${COMPLIANCE_API_END_POINT_JURIDICAL_PERSON_DATA}`,
            data: juridicalPersonData,
        });
    }
    async getNaturalPerson() {
        return await this.authenticationService.fetch({
            method: "GET",
            url: `${this.url}${COMPLIANCE_API_END_POINT_NATURAL_PERSON_DATA}`,
        });
    }
    // TODO: Type respose
    async uploadVerificationVideo(fileVideo) {
        const videoForm = constructFormDataFromObjectLegacy({
            video: fileVideo,
        });
        return await this.authenticationService.fetch({
            data: videoForm,
            url: `${this.url}${COMPLIANCE_API_END_POINT_VIDEO}`,
            method: "POST",
        });
    }
    /** TODO TEST */
    async createCustomer(customerData) {
        return await this.authenticationService.fetch({
            data: customerData,
            url: `${this.url}${COMPLIANCE_API_END_POINT_CUSTOMER}`,
            method: "POST",
        });
    }
    /** TODO TEST */
    async getCustomer() {
        return await this.authenticationService.fetch({
            url: `${this.url}${COMPLIANCE_API_END_POINT_CUSTOMER}`,
            method: "GET",
        });
    }
    async submitLegalAddress(legalAddress) {
        return await this.authenticationService.fetch({
            method: "POST",
            url: `${this.url}${COMPLIANCE_API_END_POINT_NATURAL_PERSON_DATA}/legal_address`,
            data: legalAddress,
        });
    }
    async getInformationRequestTemplates(informationrequestTemplatesParams) {
        return await this.authenticationService.fetch({
            method: "GET",
            url: `${this.url}${COMPLIANCE_API_END_POINT_GET_MEMBER_INFORMATION_REQUEST_TEMPLATE}`,
            params: informationrequestTemplatesParams,
        });
    }
    async applyInformationRequestTemplate(applyRequestTemplatesParams) {
        return await this.authenticationService.fetch({
            method: "POST",
            url: `${this.url}${COMPLIANCE_API_END_POINT_MEMBER_APPLY_INFORMATION_REQUEST}`,
            data: applyRequestTemplatesParams,
        });
    }
    async getInformationRequests(informationrequestParams) {
        return await this.authenticationService.fetch({
            method: "GET",
            url: `${this.url}${COMPLIANCE_API_END_POINT_GET_MEMBER_INFORMATION_REQUEST}`,
            params: informationrequestParams,
        });
    }
    async submitInformationRequest(informationrequestBody) {
        const documentForm = constructFormDataFromObjectLegacy(informationrequestBody);
        return await this.authenticationService.fetch({
            method: "POST",
            url: `${this.url}${COMPLIANCE_API_END_POINT_POST_MEMBER_INFORMATION_REQUEST}`,
            data: documentForm,
        });
    }
    async notifyInformationRequest() {
        return await this.authenticationService.fetch({
            method: "POST",
            url: `${this.url}${COMPLIANCE_API_END_POINT_NOTIFY_MEMBER_INFORMATION_REQUEST}`,
        });
    }
    async newFlow(params, headers) {
        return await this.authenticationService.fetch({
            method: "POST",
            url: `${this.url}${COMPLIANCE_API_END_POINT_MEMBER_FLOW_NEW}`,
            data: params,
            headers,
        });
    }
    async getFlowNext(flow_category) {
        return await this.authenticationService.fetch({
            method: "GET",
            url: `${this.url}${COMPLIANCE_API_END_POINT_MEMBER_GET_FLOW_NEXT}`,
            params: { flow_category },
        });
    }
    async uploadFlow(params) {
        return await this.authenticationService.fetch({
            method: "POST",
            url: `${this.url}${COMPLIANCE_API_END_POINT_MEMBER_FLOW_UPLOAD}`,
            data: params,
        });
    }
    async getTermsAndConditions(params) {
        return await this.authenticationService.fetch({
            method: "GET",
            url: params
                ? `${this.url}${COMPLIANCE_API_END_POINT_MEMBER_GET_TERMS_AND_CONDITIONS}/${params.product_name}`
                : `${this.url}${COMPLIANCE_API_END_POINT_MEMBER_GET_TERMS_AND_CONDITIONS}`,
        });
    }
    async acceptTermsAndConditions(params) {
        return await this.authenticationService.fetch({
            method: "POST",
            url: `${this.url}${COMPLIANCE_API_END_POINT_MEMBER_ACCEPT_TERMS_AND_CONDITIONS}`,
            data: params,
        });
    }
    async getAcceptedTermsAndConditions() {
        return await this.authenticationService.fetch({
            method: "GET",
            url: `${this.url}${COMPLIANCE_API_END_POINT_MEMBER_GET_ACCEPTED_TERMS_AND_CONDITIONS}`,
        });
    }
    async getUpdatedTermsAndConditions() {
        return await this.authenticationService.fetch({
            method: "GET",
            url: `${this.url}${COMPLIANCE_API_END_POINT_MEMBER_GET_UPDATED_TERMS_AND_CONDITIONS}`,
        });
    }
    async getUltimateBeneficialOwner() {
        return await this.authenticationService.fetch({
            method: "GET",
            url: `${this.url}${COMPLIANCE_API_END_POINT_MEMBER_ULTIMATE_BENEFICIAL_OWNERS}`,
        });
    }
    async createUltimateBeneficialOwner(data) {
        const ultimateBeneficialOwner = constructFormDataFromObject(data);
        return await this.authenticationService.fetch({
            method: "POST",
            url: `${this.url}${COMPLIANCE_API_END_POINT_MEMBER_ULTIMATE_BENEFICIAL_OWNERS}`,
            data: ultimateBeneficialOwner,
        });
    }
    async updateUltimateBeneficialOwner(id, data) {
        const ultimateBeneficialOwner = constructFormDataFromObject(data);
        return await this.authenticationService.fetch({
            method: "PUT",
            url: `${this.url}${COMPLIANCE_API_END_POINT_MEMBER_ULTIMATE_BENEFICIAL_OWNERS}/${id}`,
            data: ultimateBeneficialOwner,
        });
    }
    async deleteUltimateBeneficialOwner(ultimateBeneficialOwnerId) {
        return await this.authenticationService.fetch({
            method: "DELETE",
            url: `${this.url}${COMPLIANCE_API_END_POINT_MEMBER_ULTIMATE_BENEFICIAL_OWNERS_DELETE(ultimateBeneficialOwnerId)}`,
        });
    }
    async uploadUltimateBeneficialOwnerDocument(ultimateBeneficialOwnerId, form) {
        const ultimateBeneficialOwnerDocumentForm = constructFormDataFromObject(form);
        return await this.authenticationService.fetch({
            method: "POST",
            url: `${this.url}${COMPLIANCE_API_END_POINT_MEMBER_ULTIMATE_BENEFICIAL_OWNERS_DOCUMENT(ultimateBeneficialOwnerId)}`,
            data: ultimateBeneficialOwnerDocumentForm,
        });
    }
    async editPhoneNumber(phone) {
        return await this.authenticationService.fetch({
            method: "PUT",
            url: `${this.url}${COMPLIANCE_API_END_POINT_EDIT_PHONE}`,
            data: { phone },
        });
    }
}

/******************************************************************************
Copyright (c) Microsoft Corporation.

Permission to use, copy, modify, and/or distribute this software for any
purpose with or without fee is hereby granted.

THE SOFTWARE IS PROVIDED "AS IS" AND THE AUTHOR DISCLAIMS ALL WARRANTIES WITH
REGARD TO THIS SOFTWARE INCLUDING ALL IMPLIED WARRANTIES OF MERCHANTABILITY
AND FITNESS. IN NO EVENT SHALL THE AUTHOR BE LIABLE FOR ANY SPECIAL, DIRECT,
INDIRECT, OR CONSEQUENTIAL DAMAGES OR ANY DAMAGES WHATSOEVER RESULTING FROM
LOSS OF USE, DATA OR PROFITS, WHETHER IN AN ACTION OF CONTRACT, NEGLIGENCE OR
OTHER TORTIOUS ACTION, ARISING OUT OF OR IN CONNECTION WITH THE USE OR
PERFORMANCE OF THIS SOFTWARE.
***************************************************************************** */

function __rest(s, e) {
  var t = {};
  for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p) && e.indexOf(p) < 0)
      t[p] = s[p];
  if (s != null && typeof Object.getOwnPropertySymbols === "function")
      for (var i = 0, p = Object.getOwnPropertySymbols(s); i < p.length; i++) {
          if (e.indexOf(p[i]) < 0 && Object.prototype.propertyIsEnumerable.call(s, p[i]))
              t[p[i]] = s[p[i]];
      }
  return t;
}

typeof SuppressedError === "function" ? SuppressedError : function (error, suppressed, message) {
  var e = new Error(message);
  return e.name = "SuppressedError", e.error = error, e.suppressed = suppressed, e;
};

var ServiceEnvEnum;
(function (ServiceEnvEnum) {
    ServiceEnvEnum["TEST"] = "test";
    ServiceEnvEnum["TEST2"] = "test2";
    ServiceEnvEnum["TEST3"] = "test3";
    ServiceEnvEnum["STAGE"] = "stage";
    ServiceEnvEnum["PROD"] = "prod";
    ServiceEnvEnum["LOCAL"] = "local";
})(ServiceEnvEnum || (ServiceEnvEnum = {}));

const API_URL_CONDITION = {
    [ServiceEnvEnum.LOCAL]: "http://local.letsbit.io:8080/api",
    [ServiceEnvEnum.PROD]: "https://api.letsbit.io/api",
    [ServiceEnvEnum.STAGE]: "https://api-stage.cubetadecapitales.com.ar/api",
    [ServiceEnvEnum.TEST]: "https://api-test.cubetadecapitales.com.ar/api",
    [ServiceEnvEnum.TEST2]: "https://api-test2.cubetadecapitales.com.ar/api",
    [ServiceEnvEnum.TEST3]: "https://api-test3.cubetadecapitales.com.ar/api",
};

// import "isomorphic-fetch";
class PublicMicroService {
    constructor(serviceConfig) {
        this.serviceConfig = serviceConfig;
        this.serviceConfig = serviceConfig;
    }
    async fetch(params) {
        const _a = this.serviceConfig, { prefix = "", env, version, url } = _a, rest = __rest(_a, ["prefix", "env", "version", "url"]);
        const headers = params.headers || {};
        if (params.data instanceof FormData) {
            headers["Content-Type"] = "multipart/form-data";
        }
        else {
            headers["Content-Type"] = "application/json";
        }
        headers["X-app-version"] = this.serviceConfig.appVersion || "noVersion";
        headers["X-app-live-version"] =
            this.serviceConfig.appLiveVersion || "noLiveVersion";
        const response = await axios.request(Object.assign(Object.assign(Object.assign({}, rest), params), { headers, data: params.data || {}, url: url
                ? `${url}${prefix}${params.url}`
                : `${API_URL_CONDITION[env]}/${version}${prefix}${params.url}` }));
        return response;
    }
}

class CompliancePublic extends PublicMicroService {
    constructor(serviceConfig) {
        super(Object.assign(Object.assign({}, serviceConfig), { prefix: serviceConfig.noPrefix ? "" : COMPLIANCE_PREFIX }));
    }
    async getDocumentType(params) {
        return await this.fetch({
            url: COMPLIANCE_API_END_POINT_DOCUMENT_TYPE,
            method: "GET",
            params: params,
        });
    }
    async getActiveTermsAndConditions() {
        return await this.fetch({
            url: COMPLIANCE_API_END_POINT_PUBLIC_GET_TERMS_AND_CONDITIONS,
            method: "GET",
        });
    }
    async getTermsAndConditionsLastVersion(tyc_name) {
        return await this.fetch({
            url: `${COMPLIANCE_API_END_POINT_PUBLIC_GET_TERMS_AND_CONDITIONS}/${tyc_name}`,
            method: "GET",
        });
    }
}

class CompliancePartner {
    constructor(authenticationService) {
        this.url = authenticationService.serviceConfig.noPrefix
            ? `${authenticationService.serviceConfig.url}`
            : `${authenticationService.serviceConfig.url}${COMPLIANCE_PREFIX}`;
        this.authenticationService = authenticationService;
    }
    async getMemberDescription(member_uid) {
        return await this.authenticationService.fetch({
            url: `${this.url}${COMPLIANCE_API_END_POINT_PARTNER_MEMBERS}/${member_uid}`,
            method: "GET",
        });
    }
    async getMembers(params) {
        return await this.authenticationService.fetch({
            url: `${this.url}${COMPLIANCE_API_END_POINT_PARTNER_MEMBERS}`,
            method: "GET",
            params,
        });
    }
    async getMemberCustomer(member_uid) {
        return await this.authenticationService.fetch({
            url: `${this.url}${COMPLIANCE_API_END_POINT_PARTNER_MEMBERS}/${member_uid}/customer`,
            method: "GET",
        });
    }
    async getMemberExtraDocs(member_uid) {
        return await this.authenticationService.fetch({
            url: `${this.url}${COMPLIANCE_API_END_POINT_PARTNER_MEMBERS}/${member_uid}/extra`,
            method: "GET",
        });
    }
    async getMemberNpd(member_uid) {
        return await this.authenticationService.fetch({
            url: `${this.url}${COMPLIANCE_API_END_POINT_PARTNER_MEMBERS}/${member_uid}/natural_person`,
            method: "GET",
        });
    }
}

const PAYMENTS_PREFIX = "/lb/payments";
const PAYMENT_API_END_POINT_BANKS = "/member/banks";
const PAYMENT_API_END_POINT_BENEFICIARIES = "/member/beneficiaries";
const PAYMENT_API_END_POINT_DEPOSITS = "/member/deposits";
const PAYMENT_API_END_POINT_EXTERNAL_PAYMENT_ADDRESSES = "/member/epas";
const PAYMENT_API_END_POINT_LIMITS = "/member/limits/remaining";
const PAYMENT_API_END_POINT_WITHDRAWS = "/member/withdraws";
const PAYMENT_API_END_POINT_DEPOSIT_ADDRESS = "/member/deposit_address";
const PAYMENT_API_END_POINT_DEPOSIT_DATA = "/member/deposit_data";
const PAYMENT_API_END_POINT_CURRENCIES = "/member/currencies";
const PAYMENT_API_END_POINT_PUBLIC_LIMITS = "/public/limits";
const PAYMENT_API_END_POINT_MEMBER_LIMITS = "/member/limits";
const PAYMENT_API_END_POINT_PUBLIC_CURRENCIES = "/public/currencies";
const PAYMENT_API_END_POINT_PUBLIC_WITHDRAW_CONCEPTS = "/public/concepts";
const PAYMENT_API_END_POINT_BATCH_WITHDRAW_DETAILS = "/member/withdraw_requests/details";
const PAYMENT_API_END_POINT_BATCH_WITHDRAW_PROCESS = "/member/withdraw_requests/process";
const PAYMENT_API_END_POINT_BATCH_WITHDRAW = "/member/withdraw_requests";
const PAYMENT_API_END_POINT_PARTNER_MEMBERS = "/partner/members";
const PAYMENT_API_END_POINT_COMMERCIAL_RULES = "/member/deposit_address/commercial_rule";
const PAYMENT_API_END_POINT_MEMBER_TRANSFER_REQUEST = "/member/transfer_requests";

class PaymentsMember {
    constructor(authenticationService) {
        this.url = authenticationService.serviceConfig.noPrefix
            ? `${authenticationService.serviceConfig.url}`
            : `${authenticationService.serviceConfig.url}${PAYMENTS_PREFIX}`;
        this.authenticationService = authenticationService;
    }
    async addBeneficiary(form) {
        return await this.authenticationService.fetch({
            method: "POST",
            url: `${this.url}${PAYMENT_API_END_POINT_BENEFICIARIES}`,
            data: form,
        });
    }
    async getBeneficiaries() {
        return await this.authenticationService.fetch({
            method: "GET",
            url: `${this.url}${PAYMENT_API_END_POINT_BENEFICIARIES}`,
        });
    }
    async getDepositHistory(params) {
        return await this.authenticationService.fetch({
            method: "GET",
            url: `${this.url}${PAYMENT_API_END_POINT_DEPOSITS}`,
            params,
        });
    }
    async getDeposit(tid) {
        return await this.authenticationService.fetch({
            method: "GET",
            url: `${this.url}${PAYMENT_API_END_POINT_DEPOSITS}/${tid}`,
        });
    }
    async getLimits(params) {
        return await this.authenticationService.fetch({
            method: "GET",
            url: `${this.url}${PAYMENT_API_END_POINT_LIMITS}`,
            params,
        });
    }
    async getGeneralLimits() {
        return await this.authenticationService.fetch({
            method: "GET",
            url: `${this.url}${PAYMENT_API_END_POINT_MEMBER_LIMITS}`,
        });
    }
    async withdraw(params, headers) {
        return await this.authenticationService.fetch({
            method: "POST",
            url: `${this.url}${PAYMENT_API_END_POINT_WITHDRAWS}`,
            data: params,
            headers,
        });
    }
    async deleteWithdrawRequest(id) {
        return await this.authenticationService.fetch({
            method: "DELETE",
            url: `${this.url}${PAYMENT_API_END_POINT_BATCH_WITHDRAW}`,
            params: {
                id,
            },
        });
    }
    async createWithdrawRequest(data) {
        const withdrawRequestForm = constructFormDataFromObjectLegacy(data);
        return await this.authenticationService.fetch({
            method: "POST",
            url: `${this.url}${PAYMENT_API_END_POINT_BATCH_WITHDRAW}`,
            data: withdrawRequestForm,
        });
    }
    async getWithdrawRequest(params) {
        return await this.authenticationService.fetch({
            method: "GET",
            url: `${this.url}${PAYMENT_API_END_POINT_BATCH_WITHDRAW}`,
            params,
        });
    }
    async deleteWithdrawRequestDetails(id) {
        return await this.authenticationService.fetch({
            method: "DELETE",
            url: `${this.url}${PAYMENT_API_END_POINT_BATCH_WITHDRAW_DETAILS}`,
            params: {
                id,
            },
        });
    }
    async getWithdrawRequestDetails(params) {
        return await this.authenticationService.fetch({
            method: "GET",
            url: `${this.url}${PAYMENT_API_END_POINT_BATCH_WITHDRAW_DETAILS}`,
            params,
        });
    }
    async processWithdrawRequest(data) {
        return await this.authenticationService.fetch({
            method: "POST",
            url: `${this.url}${PAYMENT_API_END_POINT_BATCH_WITHDRAW_PROCESS}`,
            data,
        });
    }
    async getWithdrawHistory(params) {
        return await this.authenticationService.fetch({
            method: "GET",
            url: `${this.url}${PAYMENT_API_END_POINT_WITHDRAWS}`,
            params,
        });
    }
    async getWithdraw(tid) {
        return await this.authenticationService.fetch({
            method: "GET",
            url: `${this.url}${PAYMENT_API_END_POINT_WITHDRAWS}/${tid}`,
        });
    }
    async getExternalPaymentAddresses() {
        return await this.authenticationService.fetch({
            method: "GET",
            url: `${this.url}${PAYMENT_API_END_POINT_EXTERNAL_PAYMENT_ADDRESSES}`,
        });
    }
    async addExternalPaymentAddress(form) {
        return await this.authenticationService.fetch({
            method: "POST",
            url: `${this.url}${PAYMENT_API_END_POINT_EXTERNAL_PAYMENT_ADDRESSES}`,
            data: form,
        });
    }
    async deleteExternalPaymentAddress(epaid) {
        return await this.authenticationService.fetch({
            method: "DELETE",
            url: `${this.url}${PAYMENT_API_END_POINT_EXTERNAL_PAYMENT_ADDRESSES}`,
            params: { epaid },
        });
    }
    async getBanks(payment_network) {
        return await this.authenticationService.fetch({
            method: "GET",
            url: `${this.url}${PAYMENT_API_END_POINT_BANKS}`,
            params: {
                payment_network,
            },
        });
    }
    async getWalletAddress(walletId, paymentNetwork) {
        return this.authenticationService.fetch({
            method: "POST",
            url: `${this.url}${PAYMENT_API_END_POINT_DEPOSIT_ADDRESS}`,
            data: { currency: walletId, payment_network: paymentNetwork },
        });
    }
    async editAlias(cvu_alias) {
        return await this.authenticationService.fetch({
            url: `${this.url}${PAYMENT_API_END_POINT_DEPOSIT_ADDRESS}/alias`,
            method: "PUT",
            data: { cvu_alias },
        });
    }
    async getMemberCurrencies() {
        return await this.authenticationService.fetch({
            method: "GET",
            url: `${this.url}${PAYMENT_API_END_POINT_CURRENCIES}`,
        });
    }
    async getMemberDepositAddress(params) {
        return await this.authenticationService.fetch({
            method: "GET",
            url: `${this.url}${PAYMENT_API_END_POINT_DEPOSIT_ADDRESS}/information`,
            params,
        });
    }
    async getMemberDepositAddressPaidStatus(params) {
        return await this.authenticationService.fetch({
            method: "GET",
            url: `${this.url}${PAYMENT_API_END_POINT_DEPOSIT_ADDRESS}/check`,
            params,
        });
    }
    async getMemberDepositData(params) {
        return await this.authenticationService.fetch({
            method: "GET",
            url: `${this.url}${PAYMENT_API_END_POINT_DEPOSIT_DATA}`,
            params,
        });
    }
    async getComercialRules(params) {
        return await this.authenticationService.fetch({
            method: "GET",
            url: `${this.url}${PAYMENT_API_END_POINT_COMMERCIAL_RULES}`,
            params,
        });
    }
    async createTransferRequest(data) {
        return await this.authenticationService.fetch({
            method: "POST",
            url: `${this.url}${PAYMENT_API_END_POINT_MEMBER_TRANSFER_REQUEST}`,
            data: constructFormDataFromObject(data),
        });
    }
    async getTransferRequest(params) {
        return await this.authenticationService.fetch({
            method: "GET",
            url: `${this.url}${PAYMENT_API_END_POINT_MEMBER_TRANSFER_REQUEST}`,
            params,
        });
    }
}

class PaymentsPartner {
    constructor(authenticationService) {
        this.url = authenticationService.serviceConfig.noPrefix
            ? `${authenticationService.serviceConfig.url}`
            : `${authenticationService.serviceConfig.url}${PAYMENTS_PREFIX}`;
        this.authenticationService = authenticationService;
    }
    async getMembersEpas(params) {
        return await this.authenticationService.fetch({
            url: `${this.url}${PAYMENT_API_END_POINT_PARTNER_MEMBERS}/epas`,
            method: "GET",
            params,
        });
    }
    async getMemberEpasList(member_uid) {
        return await this.authenticationService.fetch({
            url: `${this.url}${PAYMENT_API_END_POINT_PARTNER_MEMBERS}/${member_uid}/epas`,
            method: "GET",
        });
    }
    async getMemberLimit(params) {
        const { member_uid } = params, otherParams = __rest(params, ["member_uid"]);
        return await this.authenticationService.fetch({
            url: `${this.url}${PAYMENT_API_END_POINT_PARTNER_MEMBERS}/${member_uid}/limits/remaining`,
            method: "GET",
            params: otherParams,
        });
    }
    async getDeposit(tid) {
        return await this.authenticationService.fetch({
            url: `${this.url}${PAYMENT_API_END_POINT_PARTNER_MEMBERS}/deposits/${tid}`,
            method: "GET",
        });
    }
    async getDepositList(params) {
        return await this.authenticationService.fetch({
            url: `${this.url}${PAYMENT_API_END_POINT_PARTNER_MEMBERS}/deposits`,
            method: "GET",
            params,
        });
    }
    async getMemberDeposit(member_uid, tid) {
        return await this.authenticationService.fetch({
            url: `${this.url}${PAYMENT_API_END_POINT_PARTNER_MEMBERS}/${member_uid}/deposits/${tid}`,
            method: "GET",
        });
    }
    async getMemberDepositList(member_uid, params) {
        return await this.authenticationService.fetch({
            url: `${this.url}${PAYMENT_API_END_POINT_PARTNER_MEMBERS}/${member_uid}/deposits`,
            method: "GET",
            params,
        });
    }
    async getWithdraw(tid) {
        return await this.authenticationService.fetch({
            url: `${this.url}${PAYMENT_API_END_POINT_PARTNER_MEMBERS}/withdraws/${tid}`,
            method: "GET",
        });
    }
    async getWithdrawList(params) {
        return await this.authenticationService.fetch({
            url: `${this.url}${PAYMENT_API_END_POINT_PARTNER_MEMBERS}/withdraws`,
            method: "GET",
            params,
        });
    }
    async getMemberWithdraw(member_uid, tid) {
        return await this.authenticationService.fetch({
            url: `${this.url}${PAYMENT_API_END_POINT_PARTNER_MEMBERS}/${member_uid}/withdraws/${tid}`,
            method: "GET",
        });
    }
    async getMemberWithdrawList(member_uid, params, epas) {
        return await this.authenticationService.fetch({
            url: `${this.url}${PAYMENT_API_END_POINT_PARTNER_MEMBERS}/${member_uid}/withdraws`,
            method: "GET",
            params,
            data: epas,
        });
    }
}

const CONTENT_PREFIX = "/lb/content";
const CONTENT_API_END_POINT_CURRENCY_LEGAL = "/public/currencies";
const CONTENT_API_END_POINT_LIMITS = "/public/limits";
const CONTENT_API_END_POINT_NEWS = "/public/news";
const CONTENT_API_END_POINT_COUNTRIES = "/public/countries";
const CONTENT_API_END_POINT_PROMOTIONS = "/public/promotions";

class ContentPublic extends PublicMicroService {
    constructor(serviceConfig) {
        super(Object.assign(Object.assign({}, serviceConfig), { prefix: CONTENT_PREFIX }));
    }
    /** Get the term and conditions from an specifict asset */
    async getAssetsTnC(currency) {
        const url = currency
            ? `${CONTENT_API_END_POINT_CURRENCY_LEGAL}/${currency}`
            : CONTENT_API_END_POINT_CURRENCY_LEGAL;
        return await this.fetch({
            method: "GET",
            url,
        });
    }
    /** Get the public news */
    async getNews() {
        return await this.fetch({
            method: "GET",
            url: CONTENT_API_END_POINT_NEWS,
        });
    }
    /** Get the public countries */
    async getCountries() {
        return await this.fetch({
            method: "GET",
            url: CONTENT_API_END_POINT_COUNTRIES,
        });
    }
    /** Get the policy from an specifict country and legal type */
    async getPolicy(country, legal_type) {
        const params = {
            type: legal_type,
            html: "1",
        };
        const url = `${CONTENT_API_END_POINT_COUNTRIES}/${encodeURIComponent(country)}`;
        return await this.fetch({
            method: "GET",
            url,
            params,
        });
    }
    /** Get the public promotions */
    async getPromotions() {
        return await this.fetch({
            method: "GET",
            url: CONTENT_API_END_POINT_PROMOTIONS,
        });
    }
}

/* Delete this comment once you know that the current PREFIX is right*/
const BARONG_PREFIX = "/auth";
const BARONG_API_END_POINT_ME = "/resource/users/me";
const BARONG_API_END_POINT_PASSWORD = "/resource/users/password";
const BARONG_API_END_POINT_OTP = "/resource/otp";
const BARONG_API_END_POINT_GET_ACTIVITIES = "/resource/users/activity";
const BARONG_API_END_POINT_GET_API_KEY = "/resource/api_keys";
const BARONG_API_END_POINT_GET_USER = "/users/get";
const BARONG_API_END_POINT_SESSIONS = "/identity/sessions";
const BARONG_API_END_POINT_USERS = "/identity/users";
const BARONG_API_END_POINT_CONFIRM_USER = "/identity/users/email/confirm_code";
const BARONG_API_END_POINT_PASSWORD_GENERATE_CODE = "/identity/users/password/generate_code";
const BARONG_API_END_POINT_PASSWORD_CONFIRM_CODE = "/identity/users/password/confirm_code";
const BARONG_API_END_POINT_EMAIL_GENERATE_CODE = "/identity/users/email/generate_code";
const BARONG_API_END_POINT_PASSWORD_VALIDATE = "/identity/password/validate";
const BARONG_API_END_POINT_CONFIGS = "/public/configs";
const BARONG_API_END_POINT_ADMIN_IDENTITY_SESSIONS_AUTH0_LOG_IN = "/identity/sessions/auth0/login";
const BARONG_API_END_POINT_ADMIN_IDENTITY_SESSIONS_AUTH0_REGISTER = "/identity/sessions/auth0/register";
const BARONG_API_END_POINT_PUBLIC_AUTH0_CONFIG = "/public/configs/auth0";
const BARONG_API_END_POINT_RESOURCE_DATA_STORAGE = "/resource/data_storage";
const BARONG_API_END_POINT_ADMIN_ACTIVITIES = "/admin/activities";

class BarongMember {
    constructor(authenticationService) {
        this.url = authenticationService.serviceConfig.noPrefix
            ? `${authenticationService.serviceConfig.url}`
            : `${authenticationService.serviceConfig.url}${BARONG_PREFIX}`;
        this.authenticationService = authenticationService;
    }
    async updateApiKeys(form) {
        const { kid } = form, data = __rest(form, ["kid"]);
        return await this.authenticationService.fetch({
            method: "PATCH",
            url: `${this.url}${BARONG_API_END_POINT_GET_API_KEY}/${kid}`,
            data,
        });
    }
    async deleteApiKeys(kid, totp_code) {
        return await this.authenticationService.fetch({
            method: "DELETE",
            url: `${this.url}${BARONG_API_END_POINT_GET_API_KEY}/${kid}`,
            data: {
                totp_code,
            },
        });
    }
    async getApiKeys(ordering, order_by) {
        return await this.authenticationService.fetch({
            method: "GET",
            url: `${this.url}${BARONG_API_END_POINT_GET_API_KEY}`,
            params: {
                ordering,
                order_by,
            },
        });
    }
    async generateApiKeys(algorithm, totp_code) {
        return await this.authenticationService.fetch({
            method: "POST",
            url: `${this.url}${BARONG_API_END_POINT_GET_API_KEY}`,
            data: {
                algorithm,
                totp_code,
            },
        });
    }
    /** Changes the user password */
    async changePassword(form) {
        return await this.authenticationService.fetch({
            method: "PUT",
            url: `${this.url}${BARONG_API_END_POINT_PASSWORD}`,
            data: form,
        });
    }
    /** Get the user activities from a given topic
     * @param page defaults to 1
     * @param limit defaults to 100 (max)
     */
    async getActivities(topic, page = 1, limit = 100) {
        return this.authenticationService.fetch({
            method: "GET",
            url: `${this.url}${BARONG_API_END_POINT_GET_ACTIVITIES}/${encodeURIComponent(topic)}`,
            params: { page: page.toString(), limit: limit.toString() },
        });
    }
    /** Get the current user */
    async getUser() {
        return this.authenticationService.fetch({
            method: "GET",
            url: `${this.url}${BARONG_API_END_POINT_ME}`,
            withCredentials: true,
        });
    }
    async updateUserData(userData) {
        return this.authenticationService.fetch({
            method: "PUT",
            url: `${this.url}${BARONG_API_END_POINT_ME}`,
            data: { data: userData },
        });
    }
    /** Enable or disable the two factor status */
    async changeTwoFactorStatus(code, status) {
        return this.authenticationService.fetch({
            method: "POST",
            url: `${this.url}${BARONG_API_END_POINT_OTP}/${encodeURIComponent(status)}`,
            data: { code },
        });
    }
    /** Generates the two factor status */
    async generateTwoFactorCode() {
        return this.authenticationService.fetch({
            method: "POST",
            url: `${this.url}${BARONG_API_END_POINT_OTP}/generate_qrcode`,
        });
    }
    /** Destroys the current session
     *
     * WARNING: this will leave your member instance unnusable unless you login with barong's public method */
    async logout() {
        return await this.authenticationService.fetch({
            method: "DELETE",
            url: `${this.url}${BARONG_API_END_POINT_SESSIONS}`,
            responseType: "text",
        });
    }
    /** Post data storage of a member */
    async dataStorage(info) {
        return this.authenticationService.fetch({
            method: "POST",
            url: `${this.url}${BARONG_API_END_POINT_RESOURCE_DATA_STORAGE}`,
            data: { title: "last_device", data: JSON.stringify(info) },
        });
    }
}

class BarongPublic extends PublicMicroService {
    constructor(serviceConfig) {
        super(Object.assign(Object.assign({}, serviceConfig), { prefix: serviceConfig.noPrefix ? "" : BARONG_PREFIX }));
    }
    /** Confirms an account */
    async confirmUser(token) {
        return this.fetch({
            method: "POST",
            url: BARONG_API_END_POINT_CONFIRM_USER,
            data: { token },
            withCredentials: true,
        });
    }
    /** Send password reset instructions */
    async resetPassword(email, captcha_response) {
        return this.fetch({
            method: "POST",
            url: BARONG_API_END_POINT_PASSWORD_GENERATE_CODE,
            data: { email, captcha_response },
        });
    }
    /** Send confirmations instructions */
    async generateEmailConfirmation(email, captcha_response) {
        return this.fetch({
            method: "POST",
            url: BARONG_API_END_POINT_EMAIL_GENERATE_CODE,
            data: { email, captcha_response },
        });
    }
    /** Test the password strength */
    async validatePassword(password) {
        return this.fetch({
            method: "POST",
            url: BARONG_API_END_POINT_PASSWORD_VALIDATE,
            data: { password },
        });
    }
    /** Creates a new user */
    async register(form) {
        return this.fetch({
            method: "POST",
            url: BARONG_API_END_POINT_USERS,
            data: form,
        });
    }
    /** Changes the user password from the given email token */
    async changePassword(form) {
        return this.fetch({
            method: "POST",
            url: BARONG_API_END_POINT_PASSWORD_CONFIRM_CODE,
            data: form,
        });
    }
    async login(form) {
        return this.fetch({
            method: "POST",
            url: BARONG_API_END_POINT_SESSIONS,
            data: form,
            withCredentials: true,
        });
    }
    async getConfigs() {
        return this.fetch({
            method: "GET",
            url: BARONG_API_END_POINT_CONFIGS,
        });
    }
    async getAuth0Configs() {
        return this.fetch({
            method: "GET",
            url: BARONG_API_END_POINT_PUBLIC_AUTH0_CONFIG,
        });
    }
    /** Creates a new user with auth0 */
    async registerAuth0(form) {
        return this.fetch({
            method: "POST",
            url: BARONG_API_END_POINT_ADMIN_IDENTITY_SESSIONS_AUTH0_REGISTER,
            data: form,
        });
    }
    async loginAuth0(form) {
        return this.fetch({
            method: "POST",
            url: BARONG_API_END_POINT_ADMIN_IDENTITY_SESSIONS_AUTH0_LOG_IN,
            data: form,
            withCredentials: true,
        });
    }
}

class PaymentsPublic extends PublicMicroService {
    constructor(serviceConfig) {
        super(Object.assign(Object.assign({}, serviceConfig), { prefix: serviceConfig.noPrefix ? "" : PAYMENTS_PREFIX }));
    }
    async getPublicLimits() {
        return await this.fetch({
            method: "GET",
            url: PAYMENT_API_END_POINT_PUBLIC_LIMITS,
        });
    }
    async getPublicCurrencies() {
        return await this.fetch({
            method: "GET",
            url: PAYMENT_API_END_POINT_PUBLIC_CURRENCIES,
        });
    }
    async getWithdrawConcepts() {
        return await this.fetch({
            method: "GET",
            url: PAYMENT_API_END_POINT_PUBLIC_WITHDRAW_CONCEPTS,
        });
    }
}

/* Delete this comment once you know that the current PREFIX is right*/
const PEATIO_PREFIX = "/exchange";
const PEATIO_API_END_POINT_PUBLIC_CURRENCIES = "/public/currencies";
const PEATIO_API_END_POINT_PUBLIC_MARKETS = "/public/markets";
const PEATIO_API_END_POINT_PUBLIC_TICKERS = "/public/markets/tickers";
const PEATIO_API_END_POINT_PUBLIC_TRADING_FEES = "/public/trading_fees";
const PEATIO_API_END_POINT_MARKET_ORDERS = "/market/orders";
const PEATIO_API_END_POINT_MARKET_TRADES = "/market/trades";
const PEATIO_API_END_POINT_ACCOUNT_BALANCES = "/account/balances";
const PEATIO_API_END_POINT_ACCOUNT_DEPOSIT_ADDRESS = "/account/deposit_address";
const PEATIO_API_END_POINT_ADMIN_ORDERS = "/admin/orders";
const PEATIO_API_END_POINT_ADMIN_MEMBERS = "/admin/members";
const PEATIO_API_END_POINT_ACCOUNT_MEMBERS_ME = "/account/members/me";

class PeatioMember {
    constructor(authenticationService) {
        this.url = authenticationService.serviceConfig.noPrefix
            ? `${authenticationService.serviceConfig.url}`
            : `${authenticationService.serviceConfig.url}${PEATIO_PREFIX}`;
        this.authenticationService = authenticationService;
    }
    async executeOrder(orderForm) {
        return this.authenticationService.fetch({
            method: "POST",
            url: `${this.url}${PEATIO_API_END_POINT_MARKET_ORDERS}`,
            data: orderForm,
        });
    }
    async getOrdersHistory() {
        return this.authenticationService.fetch({
            method: "GET",
            url: `${this.url}${PEATIO_API_END_POINT_MARKET_ORDERS}`,
        });
    }
    async getBalances() {
        return this.authenticationService.fetch({
            method: "GET",
            url: `${this.url}${PEATIO_API_END_POINT_ACCOUNT_BALANCES}`,
        });
    }
    async getWalletAddress(walletId) {
        return this.authenticationService.fetch({
            method: "GET",
            url: `${this.url}${PEATIO_API_END_POINT_ACCOUNT_DEPOSIT_ADDRESS}/${walletId}`,
        });
    }
    async getMe() {
        return this.authenticationService.fetch({
            method: "GET",
            url: `${this.url}${PEATIO_API_END_POINT_ACCOUNT_MEMBERS_ME}`,
        });
    }
    async getTradeHistory(params) {
        return this.authenticationService.fetch({
            method: "GET",
            url: `${this.url}${PEATIO_API_END_POINT_MARKET_TRADES}`,
            params,
        });
    }
}

class PeatioPublic extends PublicMicroService {
    constructor(serviceConfig) {
        super(Object.assign(Object.assign({}, serviceConfig), { prefix: serviceConfig.noPrefix ? "" : PEATIO_PREFIX }));
    }
    async getCurrencies() {
        return this.fetch({
            method: "GET",
            url: PEATIO_API_END_POINT_PUBLIC_CURRENCIES,
        });
    }
    async getMarkets(params) {
        return this.fetch({
            method: "GET",
            url: PEATIO_API_END_POINT_PUBLIC_MARKETS,
            params,
        });
    }
    async getTickers() {
        return this.fetch({
            method: "GET",
            url: PEATIO_API_END_POINT_PUBLIC_TICKERS,
        });
    }
    async getTradingFees(params) {
        return this.fetch({
            method: "GET",
            url: PEATIO_API_END_POINT_PUBLIC_TRADING_FEES,
            params,
        });
    }
}

/* Delete this comment once you know that the current PREFIX is right*/
const REFERRER_PREFIX = "lb/referral-program";
const REFERRER_API_END_POINT_REFERRER_DATA = "/user/referrer/data";
const REFERRER_API_END_POINT_REFERRER_PAYOUTS = "/user/payouts";

class ReferrerMember {
    constructor(authenticationService) {
        this.url = authenticationService.serviceConfig.noPrefix
            ? `${authenticationService.serviceConfig.url}`
            : `${authenticationService.serviceConfig.url}${REFERRER_PREFIX}`;
        this.authenticationService = authenticationService;
    }
    async getReferrerData() {
        return this.authenticationService.fetch({
            method: "GET",
            url: `${this.url}${REFERRER_API_END_POINT_REFERRER_DATA}`,
        });
    }
    async getReferrerPayouts(limit, page) {
        return this.authenticationService.fetch({
            method: "GET",
            url: `${this.url}${REFERRER_API_END_POINT_REFERRER_PAYOUTS}`,
            params: { limit: limit.toString(), page: page.toString() },
        });
    }
    async getReferrerPayout(tid) {
        return this.authenticationService.fetch({
            method: "GET",
            url: `${this.url}${REFERRER_API_END_POINT_REFERRER_PAYOUTS}/${tid}`,
        });
    }
}

class AuthenticationService {
    constructor(sConfig, fetcher) {
        const { env, version, url: configUrl } = sConfig, rest = __rest(sConfig, ["env", "version", "url"]);
        const url = configUrl || `${API_URL_CONDITION[env]}/${version}`;
        this.serviceConfig = Object.assign(Object.assign({}, sConfig), { url });
        this.defaultHeaders = {};
        this.axiosConfig = rest;
        if (fetcher) {
            this.fetcher = fetcher;
        }
        else {
            this.fetcher = axios;
        }
    }
    async fetch(params) {
        const headers = params.headers || {};
        if (params.data instanceof FormData) {
            headers["Content-Type"] = "multipart/form-data";
        }
        else {
            headers["Content-Type"] = "application/json";
        }
        headers["X-app-version"] = this.serviceConfig.appVersion || "noVersion";
        headers["X-app-live-version"] =
            this.serviceConfig.appLiveVersion || "noLiveVersion";
        const response = await this.fetcher.request(Object.assign(Object.assign(Object.assign({}, this.axiosConfig), params), { data: params.data || {}, headers: Object.assign(Object.assign(Object.assign({}, this.defaultHeaders), headers), this.getAuthHeaders()) }));
        return response;
    }
}

class PeatioAdmin {
    constructor(authenticationService) {
        this.url = `${authenticationService.serviceConfig.url}${PEATIO_PREFIX}`;
        this.authenticationService = authenticationService;
    }
    async getOrders(filter) {
        return await this.authenticationService.fetch({
            method: "GET",
            url: `${this.url}${PEATIO_API_END_POINT_ADMIN_ORDERS}`,
            params: filter,
        });
    }
    async getMembers(filter) {
        return await this.authenticationService.fetch({
            method: "GET",
            url: `${this.url}${PEATIO_API_END_POINT_ADMIN_MEMBERS}`,
            params: filter,
        });
    }
}

const INTEREST_PREFIX = "/lb/interests";
const INTEREST_API_END_POINT_PUBLIC_CURRENCIES = "/public/currencies";
const INTEREST_API_END_POINT_INTEREST_PAYMENTS = "/member/interest_payments";
const INTEREST_API_END_POINT_INTEREST_PAYMENTS_SUM = "/member/interest_payments/sum";
const INTEREST_API_END_POINT_INVESTMENTS_ACTIVE = "/member/investments/active";
const INTEREST_API_END_POINT_INVESTMENTS_DEACTIVATE = "/member/investments/deactivate";
const INTEREST_API_END_POINT_INVESTMENTS_ACTIVATE = "/member/investments/activate";
const INTEREST_API_END_POINT_INVESTMENTS = "/member/investments";
const INTEREST_API_END_POINT_ALL_STRATEGIES = "/member/investments/all_strategies";
const INTEREST_API_END_POINT_ADMIN_CURRENCIES = "/admin/currencies";
const INTEREST_API_END_POINT_ADMIN_STRATEGIES = "/admin/strategies";
const INTEREST_API_END_POINT_ADMIN_RATE_OF_DATE = "/admin/rate_of_date";
const INTEREST_API_END_POINT_ADMIN_FEE_OF_DATE = "/admin/fee_of_date";
const INTEREST_API_END_POINT_ADMIN_TOTALS = "/admin/totals";

class InterestAdmin {
    constructor(authenticationService) {
        this.url = `${authenticationService.serviceConfig.url}${INTEREST_PREFIX}`;
        this.authenticationService = authenticationService;
    }
    /** Update a currency */
    async updateCurrency(params) {
        return await this.authenticationService.fetch({
            method: "PUT",
            url: `${this.url}${INTEREST_API_END_POINT_ADMIN_CURRENCIES}`,
            data: params,
        });
    }
    /** Create a new currency */
    async createCurrency(params) {
        return await this.authenticationService.fetch({
            method: "POST",
            url: `${this.url}${INTEREST_API_END_POINT_ADMIN_CURRENCIES}`,
            data: params,
        });
    }
    /** Update a strategy */
    async updateStrategy(params) {
        return await this.authenticationService.fetch({
            method: "PUT",
            url: `${this.url}${INTEREST_API_END_POINT_ADMIN_STRATEGIES}`,
            data: params,
        });
    }
    /** Create a strategy */
    async createStrategy(params) {
        return await this.authenticationService.fetch({
            method: "POST",
            url: `${this.url}${INTEREST_API_END_POINT_ADMIN_STRATEGIES}`,
            data: params,
        });
    }
    /** Delete a rate */
    async deleteRate(params) {
        return await this.authenticationService.fetch({
            method: "DELETE",
            url: `${this.url}${INTEREST_API_END_POINT_ADMIN_RATE_OF_DATE}`,
            params,
        });
    }
    /** Create a rate */
    async createRate(params) {
        return await this.authenticationService.fetch({
            method: "POST",
            url: `${this.url}${INTEREST_API_END_POINT_ADMIN_RATE_OF_DATE}`,
            data: params,
        });
    }
    /** Delete a fee */
    async deleteFee(params) {
        return await this.authenticationService.fetch({
            method: "DELETE",
            url: `${this.url}${INTEREST_API_END_POINT_ADMIN_FEE_OF_DATE}`,
            data: params,
        });
    }
    /** Create a fee */
    async createFee(params) {
        return await this.authenticationService.fetch({
            method: "POST",
            url: `${this.url}${INTEREST_API_END_POINT_ADMIN_FEE_OF_DATE}`,
            data: params,
        });
    }
    /** Get sum of all investments */
    async getTotals(params) {
        return await this.authenticationService.fetch({
            method: "GET",
            url: `${this.url}${INTEREST_API_END_POINT_ADMIN_TOTALS}`,
            params,
        });
    }
}

class InterestMember {
    constructor(authenticationService) {
        this.url = authenticationService.serviceConfig.noPrefix
            ? `${authenticationService.serviceConfig.url}`
            : `${authenticationService.serviceConfig.url}${INTEREST_PREFIX}`;
        this.authenticationService = authenticationService;
    }
    /** Get members sum of interest payments by strategy */
    async getInterestPaymentsSum(params) {
        return await this.authenticationService.fetch({
            method: "GET",
            url: `${this.url}${INTEREST_API_END_POINT_INTEREST_PAYMENTS_SUM}`,
            params,
        });
    }
    /** Get members interest payments */
    async getInterestPayments(params) {
        return await this.authenticationService.fetch({
            method: "GET",
            url: `${this.url}${INTEREST_API_END_POINT_INTEREST_PAYMENTS}`,
            params,
        });
    }
    /** Get member single interest payment */
    async getInterestPayment(tid) {
        return await this.authenticationService.fetch({
            method: "GET",
            url: `${this.url}${INTEREST_API_END_POINT_INTEREST_PAYMENTS}/${tid}`,
        });
    }
    /** Get member active investments list */
    async getActiveInvestments(params) {
        return await this.authenticationService.fetch({
            method: "GET",
            url: `${this.url}${INTEREST_API_END_POINT_INVESTMENTS_ACTIVE}`,
            params,
        });
    }
    /** Get investments list including members */
    async getInvestments(params) {
        return await this.authenticationService.fetch({
            method: "GET",
            url: `${this.url}${INTEREST_API_END_POINT_INVESTMENTS}`,
            params,
        });
    }
    /** Get all strategies list including members data from investments */
    async getInvestmentsStrategies(params) {
        return await this.authenticationService.fetch({
            method: "GET",
            url: `${this.url}${INTEREST_API_END_POINT_ALL_STRATEGIES}`,
            params,
        });
    }
    /** Deactivate an investment */
    async deactivateInvestment(strategy) {
        return await this.authenticationService.fetch({
            method: "POST",
            url: `${this.url}${INTEREST_API_END_POINT_INVESTMENTS_DEACTIVATE}`,
            data: { strategy },
        });
    }
    /** Activate an investment */
    async activateInvestment(params) {
        return await this.authenticationService.fetch({
            method: "POST",
            url: `${this.url}${INTEREST_API_END_POINT_INVESTMENTS_ACTIVATE}`,
            data: params,
        });
    }
}

class InterestPublic extends PublicMicroService {
    constructor(serviceConfig) {
        super(Object.assign(Object.assign({}, serviceConfig), { prefix: serviceConfig.noPrefix ? "" : INTEREST_PREFIX }));
    }
    async getCurrenciesInterests(params) {
        return await this.fetch({
            method: "GET",
            url: INTEREST_API_END_POINT_PUBLIC_CURRENCIES,
            params,
        });
    }
}

/* Delete this comment once you know that the current PREFIX is right*/
const HUB_PREFIX = "/hub";
const HUB_API_END_POINT_CURRENCIES = "/public/currencies";
const HUB_API_END_POINT_CURRENCY_MARKETS = "/public/currency_markets";
const HUB_API_END_POINT_PAIRS = "/public/pairs";
const HUB_API_END_POINT_CONVERT_CURRENCIES = "/public/convert-currencies";
const HUB_API_END_POINT_PUBLIC_APP_MIN_VERSION_ALLOWED = "/public/app/min_version_allowed";
const HUB_API_END_POINT_PUBLIC_COUNTRIES = "/public/countries/";
const HUB_API_END_POINT_PUBLIC_REGIONS = "/public/countries/regions";
const HUB_API_END_POINT_PUBLIC_CITIES = "/public/countries/cities";
const HUB_API_END_POINT_HISTORY = "/member/history";
const HUB_API_END_POINT_BALANCES = "/member/balances";
const HUB_API_END_POINT_BALANCES_TOTAL = "/member/balances/total";
const HUB_API_END_POINT_REFERRER_DATA = "/member/referrer_data";
const HUB_API_END_POINT_CONTACTS = "/member/contacts";
const HUB_API_END_POINT_ADDRESSES = "/member/addresses";
const HUB_API_END_POINT_REWARDS_DATA = "/member/rewards_data";
const HUB_API_END_POINT_REWARDS_CASHBACK_LEVELS = "/member/rewards_data/cashback-levels";
const HUB_API_END_POINT_MEMBER_GET_MONTHLY_REWARDS = "/member/rewards_data/monthly-rewards";
const HUB_API_END_POINT_INVESTMENTS = "/member/investments";
const HUB_API_END_POINT_FEES = "/public/fees";
const HUB_API_END_POINT_MEMBER_CURRENCIES = "/member/currencies";
const HUB_API_END_POINT_MEMBER_CURRENCIES_PREFERRED_DEPOSIT = "/member/currencies/deposit-preferred";
const HUB_API_END_POINT_MEMBER_CURRENCIES_PREFERRED_WITHDRAW = "/member/currencies/withdraw-preferred";
const HUB_API_END_POINT_MEMBER_PAIRS = "/member/pairs";
const HUB_API_END_POINT_MEMBER_VERIFICATION_ENTERPRISE = "/member/enterprise/verification-steps";
const HUB_API_END_POINT_MEMBER_SEND_ESIGNATURE_CONTRACT = "/member/enterprise/sworn_declaration";
const HUB_API_END_POINT_MEMBER_CARD_EXPIRATION_DATE = "/member/cards/expiration";
const HUB_API_END_POINT_MEMBER_DOCKET_UPDATE_EXPIRATION_DATE = "/member/compliance/docket-update/expiration";
const HUB_API_END_POINT_MEMBER_GET_TERMS_AND_CONDITIONS = "/member/compliance/terms_and_conditions";
const HUB_API_END_POINT_MEMBER_GET_ACCEPTED_TERMS_AND_CONDITIONS = "/member/compliance/terms_and_conditions/accepted/all";
const HUB_API_END_POINT_MEMBER_GET_UPDATED_TERMS_AND_CONDITIONS = "/member/compliance/terms_and_conditions/accepted/updates";
const HUB_API_END_POINT_MEMBER_GET_NEXT_ROUTE = "/member/app/next";
const HUB_API_END_POINT_MEMBER_GET_ACCOUNT_ADVISOR = "/member/account_advisor";
const HUB_API_END_POINT_MEMBER_GET_HOME_BANNER = "/member/home_banner";
const HUB_API_END_POINT_MEMBER_GET_BANNER = "/member/banner";
const HUB_API_END_POINT_MEMBER_GET_STOCK_PORTFOLIOS = "/member/stock_portfolios";
const HUB_API_END_POINT_MEMBER_GET_MARKET_ASSETS = "/member/market-assets";
const HUB_API_END_POINT_MEMBER_PRICES = "/member/prices/get_prices";
const HUB_API_END_POINT_MEMBER_CLOSURE_PRICES = "/member/prices/get_closure_prices";
const HUB_API_END_POINT_MEMBER_OPEN_POSITIONS = "/member/merk/positions";
const HUB_API_END_POINT_MEMBER_MERK_ACCOUNT_INFO = "/member/merk/accounts";
const HUB_API_END_POINT_MEMBER_INVESTMENTS_GET_INTEREST_PAYMENTS = "/member/investments/interest_payments";

class HubMember {
    constructor(authenticationService) {
        this.url = `${authenticationService.serviceConfig.url}${HUB_PREFIX}`;
        this.authenticationService = authenticationService;
    }
    async getUserHistory(params) {
        return await this.authenticationService.fetch({
            method: "GET",
            url: `${this.url}${HUB_API_END_POINT_HISTORY}`,
            params: params,
        });
    }
    async getReferrerData() {
        return await this.authenticationService.fetch({
            method: "GET",
            url: `${this.url}${HUB_API_END_POINT_REFERRER_DATA}`,
        });
    }
    async getHomeBanner() {
        return await this.authenticationService.fetch({
            method: "GET",
            url: `${this.url}${HUB_API_END_POINT_MEMBER_GET_HOME_BANNER}`,
        });
    }
    async getBanner(params) {
        return await this.authenticationService.fetch({
            method: "GET",
            url: `${this.url}${HUB_API_END_POINT_MEMBER_GET_BANNER}`,
            params: params,
        });
    }
    async getStockPortfolios() {
        return await this.authenticationService.fetch({
            method: "GET",
            url: `${this.url}${HUB_API_END_POINT_MEMBER_GET_STOCK_PORTFOLIOS}`,
        });
    }
    async getInterestPayments(params) {
        return await this.authenticationService.fetch({
            method: "GET",
            url: `${this.url}${HUB_API_END_POINT_MEMBER_INVESTMENTS_GET_INTEREST_PAYMENTS}`,
            params: params,
        });
    }
    async getBalances(params) {
        return await this.authenticationService.fetch({
            method: "GET",
            url: `${this.url}${HUB_API_END_POINT_BALANCES}`,
            params: params,
        });
    }
    async getTotalBalance(params) {
        return await this.authenticationService.fetch({
            method: "GET",
            url: `${this.url}${HUB_API_END_POINT_BALANCES_TOTAL}`,
            params: params,
        });
    }
    async getContacts(params) {
        return await this.authenticationService.fetch({
            method: "GET",
            url: `${this.url}${HUB_API_END_POINT_CONTACTS}`,
            params: params,
        });
    }
    async getAddresses(params) {
        return await this.authenticationService.fetch({
            method: "GET",
            url: `${this.url}${HUB_API_END_POINT_ADDRESSES}`,
            params: params,
        });
    }
    async getRewardTotals(params) {
        return await this.authenticationService.fetch({
            method: "GET",
            url: `${this.url}${HUB_API_END_POINT_REWARDS_DATA}`,
            params: params,
        });
    }
    async getMonthlyRewards(params) {
        return await this.authenticationService.fetch({
            method: "GET",
            url: `${this.url}${HUB_API_END_POINT_MEMBER_GET_MONTHLY_REWARDS}`,
            params: params,
        });
    }
    async getInvestments(params) {
        return await this.authenticationService.fetch({
            method: "GET",
            url: `${this.url}${HUB_API_END_POINT_INVESTMENTS}`,
            params: params,
        });
    }
    async getCashbackLevels() {
        return await this.authenticationService.fetch({
            method: "GET",
            url: `${this.url}${HUB_API_END_POINT_REWARDS_CASHBACK_LEVELS}`,
        });
    }
    async getVerificationEnterpriseStep() {
        return await this.authenticationService.fetch({
            method: "GET",
            url: `${this.url}${HUB_API_END_POINT_MEMBER_VERIFICATION_ENTERPRISE}`,
        });
    }
    async sendEsignatureContract(params) {
        return await this.authenticationService.fetch({
            method: "POST",
            url: `${this.url}${HUB_API_END_POINT_MEMBER_SEND_ESIGNATURE_CONTRACT}`,
            data: params,
        });
    }
    async getCurrencies(params) {
        return await this.authenticationService.fetch({
            method: "GET",
            url: `${this.url}${HUB_API_END_POINT_MEMBER_CURRENCIES}`,
            params: params,
        });
    }
    async getDepositPreferredCurrency(params) {
        return await this.authenticationService.fetch({
            method: "GET",
            url: `${this.url}${HUB_API_END_POINT_MEMBER_CURRENCIES_PREFERRED_DEPOSIT}`,
            params: params,
        });
    }
    async getWithdrawPreferredCurrency(params) {
        return await this.authenticationService.fetch({
            method: "GET",
            url: `${this.url}${HUB_API_END_POINT_MEMBER_CURRENCIES_PREFERRED_WITHDRAW}`,
            params: params,
        });
    }
    async getCardsExpirationDate(card_type_id) {
        return await this.authenticationService.fetch({
            method: "GET",
            url: `${this.url}${HUB_API_END_POINT_MEMBER_CARD_EXPIRATION_DATE}`,
            params: card_type_id ? { card_type_id: card_type_id } : undefined,
        });
    }
    async getPairs(params) {
        return await this.authenticationService.fetch({
            method: "GET",
            url: `${this.url}${HUB_API_END_POINT_MEMBER_PAIRS}`,
            params,
        });
    }
    async getDocketUpdateExpiration() {
        return await this.authenticationService.fetch({
            method: "GET",
            url: `${this.url}${HUB_API_END_POINT_MEMBER_DOCKET_UPDATE_EXPIRATION_DATE}`,
        });
    }
    async getTermsAndConditions(params) {
        return await this.authenticationService.fetch({
            method: "GET",
            url: `${this.url}${HUB_API_END_POINT_MEMBER_GET_TERMS_AND_CONDITIONS}`,
            params,
        });
    }
    async getAcceptedTermsAndConditions() {
        return await this.authenticationService.fetch({
            method: "GET",
            url: `${this.url}${HUB_API_END_POINT_MEMBER_GET_ACCEPTED_TERMS_AND_CONDITIONS}`,
        });
    }
    async getUpdatedTermsAndConditions() {
        return await this.authenticationService.fetch({
            method: "GET",
            url: `${this.url}${HUB_API_END_POINT_MEMBER_GET_UPDATED_TERMS_AND_CONDITIONS}`,
        });
    }
    async getNextRoute(params) {
        return await this.authenticationService.fetch({
            method: "GET",
            url: `${this.url}${HUB_API_END_POINT_MEMBER_GET_NEXT_ROUTE}`,
            params: params,
        });
    }
    async getAdvisor() {
        return await this.authenticationService.fetch({
            method: "GET",
            url: `${this.url}${HUB_API_END_POINT_MEMBER_GET_ACCOUNT_ADVISOR}`,
        });
    }
    async getMarketAssetsCategories(params) {
        return await this.authenticationService.fetch({
            method: "GET",
            url: `${this.url}${HUB_API_END_POINT_MEMBER_GET_MARKET_ASSETS}/categories`,
            params,
        });
    }
    async getMarketAssets(preferred_currency, currency_type) {
        return await this.authenticationService.fetch({
            method: "GET",
            url: `${this.url}${HUB_API_END_POINT_MEMBER_GET_MARKET_ASSETS}`,
            params: { preferred_currency, currency_type },
        });
    }
    async getPrices(params) {
        return await this.authenticationService.fetch({
            method: "GET",
            url: `${this.url}${HUB_API_END_POINT_MEMBER_PRICES}`,
            params,
        });
    }
    async getPricesClosure(params) {
        return await this.authenticationService.fetch({
            method: "GET",
            url: `${this.url}${HUB_API_END_POINT_MEMBER_CLOSURE_PRICES}`,
            params,
        });
    }
    async getMerkOpenPositions(currencyIdToPreferred) {
        return await this.authenticationService.fetch({
            method: "GET",
            url: `${this.url}${HUB_API_END_POINT_MEMBER_OPEN_POSITIONS}`,
            params: { currencyIdToPreferred },
        });
    }
    async getMerkAccountInfo(provider_id, currencyIdToPreferred) {
        return await this.authenticationService.fetch({
            method: "GET",
            url: `${this.url}${HUB_API_END_POINT_MEMBER_MERK_ACCOUNT_INFO}`,
            params: { provider_id, currencyIdToPreferred },
        });
    }
}

class HubPublic extends PublicMicroService {
    constructor(serviceConfig) {
        super(Object.assign(Object.assign({}, serviceConfig), { prefix: HUB_PREFIX }));
    }
    async getCurrencies(params) {
        return await this.fetch({
            url: HUB_API_END_POINT_CURRENCIES,
            method: "GET",
            params,
        });
    }
    async getCurrencyMarket(params) {
        return await this.fetch({
            url: HUB_API_END_POINT_CURRENCY_MARKETS,
            method: "GET",
            params: params,
        });
    }
    async getPairs(params) {
        return await this.fetch({
            method: "GET",
            url: HUB_API_END_POINT_PAIRS,
            params,
        });
    }
    async getFees() {
        return await this.fetch({
            method: "GET",
            url: HUB_API_END_POINT_FEES,
        });
    }
    async convertCurrencies(params) {
        return await this.fetch({
            method: "POST",
            url: HUB_API_END_POINT_CONVERT_CURRENCIES,
            data: params,
        });
    }
    async getAppMinVersionAllowed() {
        return await this.fetch({
            method: "GET",
            url: HUB_API_END_POINT_PUBLIC_APP_MIN_VERSION_ALLOWED,
        });
    }
    async getCountries(params) {
        return await this.fetch({
            method: "GET",
            url: HUB_API_END_POINT_PUBLIC_COUNTRIES,
            params,
        });
    }
    async getRegions(params) {
        return await this.fetch({
            method: "GET",
            url: HUB_API_END_POINT_PUBLIC_REGIONS,
            params,
        });
    }
    async getCities(params) {
        return await this.fetch({
            method: "GET",
            url: HUB_API_END_POINT_PUBLIC_CITIES,
            params,
        });
    }
}

/* Delete this comment once you know that the current PREFIX is right*/
const KARASU_PREFIX = "/lb/karasu";
const KARASU_API_END_POINT_MEMBER_DEVICE_SUBSCRIBE = "/member/devices/subscribe";
const KARASU_API_END_POINT_MEMBER_DEVICE_UNSUBSCRIBE = "/member/devices/unsubscribe";
const KARASU_API_END_POINT_MEMBER_DEVICE_STATUS = "/member/devices/status";
const KARASU_API_END_POINT_MEMBER_GET_PREFERENCES = "/member/preferences";
const KARASU_API_END_POINT_MEMBER_UPDATE_PREFERENCES = "/member/preferences";

class KarasuMember {
    constructor(authenticationService) {
        this.url = `${authenticationService.serviceConfig.url}${KARASU_PREFIX}`;
        this.authenticationService = authenticationService;
    }
    async subscribeDevice(tokens) {
        return this.authenticationService.fetch({
            method: "POST",
            url: `${this.url}${KARASU_API_END_POINT_MEMBER_DEVICE_SUBSCRIBE}`,
            data: tokens,
        });
    }
    async unsubscribeDevice(identifier) {
        return this.authenticationService.fetch({
            method: "POST",
            url: `${this.url}${KARASU_API_END_POINT_MEMBER_DEVICE_UNSUBSCRIBE}`,
            data: { identifier },
        });
    }
    async getNotificationsPreferences() {
        return await this.authenticationService.fetch({
            url: `${this.url}${KARASU_API_END_POINT_MEMBER_GET_PREFERENCES}`,
            method: "GET",
        });
    }
    async updateNotificationsPreferences(preference) {
        return await this.authenticationService.fetch({
            url: `${this.url}${KARASU_API_END_POINT_MEMBER_UPDATE_PREFERENCES}`,
            method: "PUT",
            data: preference,
        });
    }
    async getStatus(identifier) {
        return await this.authenticationService.fetch({
            url: `${this.url}${KARASU_API_END_POINT_MEMBER_DEVICE_STATUS}`,
            method: "GET",
            params: { identifier },
        });
    }
}

class KarasuPublic extends PublicMicroService {
    constructor(serviceConfig) {
        super(Object.assign(Object.assign({}, serviceConfig), { prefix: KARASU_PREFIX }));
    }
}

const KAIBACORP_PREFIX = "/lb/card_processor";
const KAIBACORP_API_END_POINT_MEMBER_CARDS = "/member/cards";
const KAIBACORP_API_END_POINT_MEMBER_CARDS_ACTIVATE = "/member/cards/activate";
const KAIBACORP_API_END_POINT_MEMBER_TRANSACTIONS = "/member/transactions";
const KAIBACORP_API_END_POINT_PUBLIC_CARDS_TYPES = "/public/cards/types";
const KAIBACORP_API_END_POINT_PUBLIC_FEES = "/public/fees";
const KAIBACORP_API_END_POINT_SHIPPING_DETAILS = "/member/shipping/details";
const KAIBACORP_API_END_POINT_REPORT_CARD = "/public/report";
const KAIBACORP_API_END_POINT_CONFIRM_REPORT_CARD = "/public/report/confirm";
const KAIBACORP_API_END_POINT_RESTRICTION_RULES_STATUS = "/member/restriction_rules/status";
const KAIBACORP_API_END_POINT_COMERCIAL_RULES_STATUS = "/member/comercial_rules";

class KaibacorpMember {
    constructor(authenticationService) {
        this.url = authenticationService.serviceConfig.noPrefix
            ? `${authenticationService.serviceConfig.url}`
            : `${authenticationService.serviceConfig.url}${KAIBACORP_PREFIX}`;
        this.authenticationService = authenticationService;
    }
    async getCards(params) {
        return this.authenticationService.fetch({
            method: "GET",
            url: `${this.url}${KAIBACORP_API_END_POINT_MEMBER_CARDS}`,
            params: params,
        });
    }
    async createCard(body, headers) {
        return this.authenticationService.fetch({
            method: "POST",
            url: `${this.url}${KAIBACORP_API_END_POINT_MEMBER_CARDS}`,
            data: body,
            headers,
        });
    }
    async patchCard(body) {
        return this.authenticationService.fetch({
            method: "PATCH",
            url: `${this.url}${KAIBACORP_API_END_POINT_MEMBER_CARDS}`,
            data: body,
        });
    }
    async getTransactions(params) {
        return this.authenticationService.fetch({
            method: "GET",
            url: `${this.url}${KAIBACORP_API_END_POINT_MEMBER_TRANSACTIONS}`,
            params: params,
        });
    }
    async activateCard(body) {
        return this.authenticationService.fetch({
            method: "POST",
            url: `${this.url}${KAIBACORP_API_END_POINT_MEMBER_CARDS_ACTIVATE}`,
            data: body,
        });
    }
    async getCardDetails(card_id) {
        return this.authenticationService.fetch({
            method: "GET",
            url: `${this.url}${KAIBACORP_API_END_POINT_MEMBER_CARDS}/${card_id}/details`,
            params: { card_id: card_id },
        });
    }
    async getShippingDetails(card_id) {
        return this.authenticationService.fetch({
            method: "GET",
            url: `${this.url}${KAIBACORP_API_END_POINT_SHIPPING_DETAILS}`,
            params: { card_id: card_id },
        });
    }
    async getRestrictionRules(type) {
        return this.authenticationService.fetch({
            method: "GET",
            url: `${this.url}${KAIBACORP_API_END_POINT_RESTRICTION_RULES_STATUS}`,
            params: { type },
        });
    }
    async getComercialRules(type) {
        return this.authenticationService.fetch({
            method: "GET",
            url: `${this.url}${KAIBACORP_API_END_POINT_COMERCIAL_RULES_STATUS}`,
            params: { type },
        });
    }
}

class KaibacorpPublic extends PublicMicroService {
    constructor(serviceConfig) {
        super(Object.assign(Object.assign({}, serviceConfig), { prefix: serviceConfig.noPrefix ? "" : KAIBACORP_PREFIX }));
    }
    async getTransactionFees() {
        return this.fetch({
            method: "GET",
            url: KAIBACORP_API_END_POINT_PUBLIC_FEES,
        });
    }
    async getCardsTypes(params) {
        return this.fetch({
            method: "GET",
            url: KAIBACORP_API_END_POINT_PUBLIC_CARDS_TYPES,
            params: params,
        });
    }
    async reportCard(body) {
        return this.fetch({
            method: "POST",
            url: KAIBACORP_API_END_POINT_REPORT_CARD,
            data: body,
        });
    }
    async confirmReportCard(body) {
        return this.fetch({
            method: "POST",
            url: KAIBACORP_API_END_POINT_CONFIRM_REPORT_CARD,
            data: body,
        });
    }
}

/* Delete this comment once you know that the current PREFIX is right*/
const COINAGPROXY_PREFIX = "/lb/coinagproxy";
const COINAGPROXY_API_END_POINT_ADMIN_BALANCES = "/admin/balances";
const COINAGPROXY_API_END_POINT_ALIAS = "/alias";
const COINAGPROXY_API_END_POINT_CVU = "/cvu";
const COINAGPROXY_API_END_POINT_CBU = "/cbu";

class CoinagproxyMember {
    constructor(authenticationService) {
        this.url = `${authenticationService.serviceConfig.url}${COINAGPROXY_PREFIX}`;
        this.authenticationService = authenticationService;
    }
    async getAliasData(alias) {
        return await this.authenticationService.fetch({
            url: `${this.url}${COINAGPROXY_API_END_POINT_ALIAS}/${encodeURIComponent(alias)}`,
            method: "GET",
        });
    }
    async getCvuData(cvu) {
        return await this.authenticationService.fetch({
            url: `${this.url}${COINAGPROXY_API_END_POINT_CVU}/${encodeURIComponent(cvu)}`,
            method: "GET",
        });
    }
    async getCbuData(cbu) {
        return await this.authenticationService.fetch({
            url: `${this.url}${COINAGPROXY_API_END_POINT_CBU}/${encodeURIComponent(cbu)}`,
            method: "GET",
        });
    }
}

class CoinagproxyPublic extends PublicMicroService {
    constructor(serviceConfig) {
        super(Object.assign(Object.assign({}, serviceConfig), { prefix: COINAGPROXY_PREFIX }));
    }
}

class CoinagproxyAdmin {
    constructor(authenticationService) {
        this.url = `${authenticationService.serviceConfig.url}${COINAGPROXY_PREFIX}`;
        this.authenticationService = authenticationService;
    }
    async getBalance(cbu, from, to) {
        return await this.authenticationService.fetch({
            url: `${this.url}${COINAGPROXY_API_END_POINT_ADMIN_BALANCES}`,
            method: "GET",
            params: {
                cbu,
                from: from.toISOString(),
                to: to.toISOString(),
            },
        });
    }
}

class BarongManagement {
    constructor(authenticationService) {
        this.url = `${authenticationService.serviceConfig.url}`;
        this.authenticationService = authenticationService;
    }
    async getUsers(filter = {}) {
        return await this.authenticationService.fetch({
            method: "POST",
            url: `${this.url}${BARONG_API_END_POINT_GET_USER}`,
            data: filter,
            service: "barong",
            action: "read_users",
        });
    }
}

/* Delete this comment once you know that the current PREFIX is right*/
const PRECOGS_PREFIX = "/lb/precogs";
const PRECOGS_API_END_POINT_USER = "/member/user";
const PRECOGS_API_END_POINT_TAGS = "/member/tags";
const PRECOGS_API_END_POINT_FLAGS_OMIT = "/member/flags/omit";
const PRECOGS_API_END_POINT_FLAGS_ANSWER = "/member/flags/answer";
const PRECOGS_API_END_POINT_FLAGS_UNANSWER = "/member/flags/unanswer";
const PRECOGS_API_END_POINT_FLAGS_POSTPONE = "/member/flags/pospone";
const PRECOGS_PUBLIC_API_END_POINT_REFERRER_CODE = "/public/referrer-code/validate";
const PRECOGS_API_END_POINT_GET_CONFIGS = "/member/configs";

class PrecogsMember {
    constructor(authenticationService) {
        this.url = authenticationService.serviceConfig.noPrefix
            ? `${authenticationService.serviceConfig.url}`
            : `${authenticationService.serviceConfig.url}${PRECOGS_PREFIX}`;
        this.authenticationService = authenticationService;
    }
    async getUser() {
        return await this.authenticationService.fetch({
            url: `${this.url}${PRECOGS_API_END_POINT_USER}`,
            method: "GET",
        });
    }
    async postTag(tag) {
        return await this.authenticationService.fetch({
            url: `${this.url}${PRECOGS_API_END_POINT_TAGS}`,
            method: "POST",
            params: { tag },
        });
    }
    async omitAnswer(flagId) {
        return await this.authenticationService.fetch({
            url: `${this.url}${PRECOGS_API_END_POINT_FLAGS_OMIT}/${flagId}`,
            method: "PUT",
        });
    }
    async postponeAnswer(flagId) {
        return await this.authenticationService.fetch({
            url: `${this.url}${PRECOGS_API_END_POINT_FLAGS_POSTPONE}/${flagId}`,
            method: "PUT",
        });
    }
    async answer(answer) {
        return await this.authenticationService.fetch({
            url: `${this.url}${PRECOGS_API_END_POINT_FLAGS_ANSWER}/${answer.flagId}`,
            method: "PUT",
            data: { value: answer.data },
        });
    }
    async getUnansweredFlags() {
        return await this.authenticationService.fetch({
            url: `${this.url}${PRECOGS_API_END_POINT_FLAGS_UNANSWER}`,
            method: "GET",
        });
    }
    async getConfig(configName) {
        return await this.authenticationService.fetch({
            url: `${this.url}${PRECOGS_API_END_POINT_GET_CONFIGS}/${configName}`,
            method: "GET",
        });
    }
    async getServicesConfigs() {
        return this.getConfig("services");
    }
    async getConfigCurrenciesNetworksWithTransfers() {
        return this.getConfig("currencies_networks_with_transfers");
    }
    async getConfigCurrenciesNetworks() {
        return this.getConfig("currencies_networks");
    }
    async getConfigPairs() {
        return this.getConfig("pairs");
    }
    async getConfigCurrenciesStategies() {
        return this.getConfig("investment-strategies");
    }
    async getConfigCurrencyDeposit() {
        return this.getConfig("deposit_currency");
    }
    async getConfigCurrencyWithdraw() {
        return this.getConfig("withdraw_currency");
    }
    async getConfigAssetsCategories() {
        return this.getConfig("assets-categories");
    }
    async putLBtag(username) {
        return await this.authenticationService.fetch({
            url: `${this.url}${PRECOGS_API_END_POINT_USER}/username`,
            method: "PUT",
            data: { username },
        });
    }
}

class PrecogsPublic extends PublicMicroService {
    constructor(serviceConfig) {
        super(Object.assign(Object.assign({}, serviceConfig), { prefix: serviceConfig.noPrefix ? "" : PRECOGS_PREFIX }));
    }
    async validateReferrerCode(code) {
        return this.fetch({
            url: PRECOGS_PUBLIC_API_END_POINT_REFERRER_CODE,
            method: "GET",
            params: { code },
        });
    }
}

/* Delete this comment once you know that the current PREFIX is right*/
const SNAKE_PREFIX = "/lb/snake";
const SNAKE_API_END_POINT_VALIDATE = "/addresses/validate";

class SnakeMember {
    constructor(authenticationService) {
        this.url = `${authenticationService.serviceConfig.url}${SNAKE_PREFIX}`;
        this.authenticationService = authenticationService;
    }
    async validate(params) {
        return await this.authenticationService.fetch({
            url: `${this.url}${SNAKE_API_END_POINT_VALIDATE}`,
            method: "POST",
            data: params,
        });
    }
}

const CARONTE_PREFIX = "/lb/adjustments";
const CARONTE_API_END_POINT_MEMBER_ADJUSTMENTS = "/member/adjustments";
const CARONTE_API_END_POINT_ADMIN_ADJUSTMENTS = "/admin/adjustments";

class CaronteMember {
    constructor(authenticationService) {
        this.url = authenticationService.serviceConfig.noPrefix
            ? `${authenticationService.serviceConfig.url}`
            : `${authenticationService.serviceConfig.url}${CARONTE_PREFIX}`;
        this.authenticationService = authenticationService;
    }
    async getAdjustments(page = 1, limit = 100) {
        return this.authenticationService.fetch({
            method: "GET",
            url: `${this.url}${CARONTE_API_END_POINT_MEMBER_ADJUSTMENTS}`,
            params: { page: page.toString(), limit: limit.toString() },
        });
    }
}

class CaronteAdmin {
    constructor(authenticationService) {
        this.url = `${authenticationService.serviceConfig.url}${CARONTE_PREFIX}`;
        this.authenticationService = authenticationService;
    }
    async createAdjustment(data) {
        return await this.authenticationService.fetch({
            method: "POST",
            url: `${this.url}${CARONTE_API_END_POINT_ADMIN_ADJUSTMENTS}`,
            data,
        });
    }
    async takeActionOnAdjustment(params, data) {
        return await this.authenticationService.fetch({
            method: "POST",
            url: `${this.url}${CARONTE_API_END_POINT_ADMIN_ADJUSTMENTS}/${params.action}`,
            data,
        });
    }
}

/* Delete this comment once you know that the current PREFIX is right*/
const BROKER_PREFIX = "/lb/broker";
const BROKER_API_END_POINT_PAIRS = "/swaps/public/pairs";
const BROKER_API_END_POINT_SWAPS = "/swaps/member/swaps";
const BROKER_API_END_POINT_WS_PAIR = "/prices/public/ws";
const BROKER_API_END_POINT_LIST = "/prices/public/prices/list";

class BrokerMember {
    constructor(authenticationService, replace) {
        this.replace = replace;
        this.url = authenticationService.serviceConfig.noPrefix
            ? `${authenticationService.serviceConfig.url}`
            : `${authenticationService.serviceConfig.url}${BROKER_PREFIX}`;
        this.authenticationService = authenticationService;
    }
    async getSwaps(params) {
        const url = `${this.url}${BROKER_API_END_POINT_SWAPS}`;
        return await this.authenticationService.fetch({
            url: this.replace ? url.replace("/swaps", "") : url,
            method: "GET",
            params,
        });
    }
    async getSwap(uid) {
        const url = `${this.url}${BROKER_API_END_POINT_SWAPS}/${uid}`;
        return await this.authenticationService.fetch({
            url: this.replace ? url.replace("/swaps", "") : url,
            method: "GET",
        });
    }
    async createSwap(body) {
        const url = `${this.url}${BROKER_API_END_POINT_SWAPS}`;
        return await this.authenticationService.fetch({
            url: this.replace ? url.replace("/swaps", "") : url,
            method: "POST",
            data: body,
        });
    }
    async getWsPair(pairUid) {
        const url = `${this.url}${BROKER_API_END_POINT_WS_PAIR}/${pairUid}`;
        return await this.authenticationService.fetch({
            url: this.replace ? url.replace("/prices", "") : url,
            method: "GET",
        });
    }
}

class BrokerPublic extends PublicMicroService {
    constructor(serviceConfig, replace) {
        super(Object.assign(Object.assign({}, serviceConfig), { prefix: serviceConfig.noPrefix ? "" : BROKER_PREFIX }));
        this.replace = replace;
    }
    async getPairs(params) {
        return await this.fetch({
            method: "GET",
            url: this.replace
                ? BROKER_API_END_POINT_PAIRS.replace("/swaps", "")
                : BROKER_API_END_POINT_PAIRS,
            params,
        });
    }
    async getList(body) {
        return await this.fetch({
            method: "POST",
            url: this.replace
                ? BROKER_API_END_POINT_LIST.replace("/prices", "")
                : BROKER_API_END_POINT_LIST,
            data: body,
        });
    }
}

/* Delete this comment once you know that the current PREFIX is right*/
const REWARDS_PREFIX = "/lb/rewards";
const REWARDS_API_ENDPOINT_PAYMENTS = "/member/payments";
const REWARDS_API_ENDPOINT_PAYMENTS_PAID = "/member/payments/paid";
const REWARDS_API_ENDPOINT_LIMITS = "/member/limits";
const REWARDS_API_ENDPOINT_CAMPAIGNS = "/member/campaigns";
const REWARDS_API_ENDPOINT_REFEREES = "/member/referees";
const REWARDS_API_ENDPOINT_PROMOTION_CODES = "/member/promotion_codes";
const REWARDS_API_ENDPOINT_REFERRAL = "/member/referral";

class RewardsMember {
    constructor(authenticationService) {
        this.url = authenticationService.serviceConfig.noPrefix
            ? `${authenticationService.serviceConfig.url}`
            : `${authenticationService.serviceConfig.url}${REWARDS_PREFIX}`;
        this.authenticationService = authenticationService;
    }
    async getReferralStatus() {
        return await this.authenticationService.fetch({
            url: `${this.url}${REWARDS_API_ENDPOINT_REFERRAL}`,
            method: "GET",
        });
    }
    async setReferral(data) {
        return await this.authenticationService.fetch({
            url: `${this.url}${REWARDS_API_ENDPOINT_REFERRAL}`,
            method: "POST",
            data,
        });
    }
    async getPayments(params) {
        return await this.authenticationService.fetch({
            url: `${this.url}${REWARDS_API_ENDPOINT_PAYMENTS}`,
            method: "GET",
            params,
        });
    }
    async getPaymentsPaid(params) {
        return await this.authenticationService.fetch({
            url: `${this.url}${REWARDS_API_ENDPOINT_PAYMENTS_PAID}`,
            method: "GET",
            params,
        });
    }
    async getLimits(params) {
        return await this.authenticationService.fetch({
            url: `${this.url}${REWARDS_API_ENDPOINT_LIMITS}`,
            method: "GET",
            params,
        });
    }
    async getCampaigns(params) {
        return await this.authenticationService.fetch({
            url: `${this.url}${REWARDS_API_ENDPOINT_CAMPAIGNS}`,
            method: "GET",
            params,
        });
    }
    async getReferees(params) {
        return await this.authenticationService.fetch({
            url: `${this.url}${REWARDS_API_ENDPOINT_REFEREES}`,
            method: "GET",
            params,
        });
    }
    async asignPromotionCode(params) {
        return await this.authenticationService.fetch({
            url: `${this.url}${REWARDS_API_ENDPOINT_PROMOTION_CODES}`,
            method: "PUT",
            params,
        });
    }
}

/* Delete this comment once you know that the current PREFIX is right*/
const DEATHNOTE_PREFIX = "/lb/contacts";
const DEATHNOTE_API_END_POINT_CONTACTS = "/member/contacts";
const DEATHNOTE_API_END_POINT_ADDRESSES = "/member/addresses";
const DEATHNOTE_API_END_POINT_ENTITIES = "/member/entities";
const DEATHNOTE_API_END_POINT_ENTITIES_FROM = "/member/entities/from_address";
const DEATHNOTE_API_END_POINT_VALIDATE_ADDRES = "/member/validate_address";
const DEATHNOTE_API_END_POINT_BOOK_ADDRESS = "/member/addresses/book";

class DeathnoteMember {
    constructor(authenticationService) {
        this.url = authenticationService.serviceConfig.noPrefix
            ? `${authenticationService.serviceConfig.url}`
            : `${authenticationService.serviceConfig.url}${DEATHNOTE_PREFIX}`;
        this.authenticationService = authenticationService;
    }
    async createAddressBook(body) {
        return await this.authenticationService.fetch({
            url: `${this.url}${DEATHNOTE_API_END_POINT_ADDRESSES}/book`,
            method: "POST",
            data: body,
        });
    }
    async getAddresses(params) {
        return await this.authenticationService.fetch({
            url: `${this.url}${DEATHNOTE_API_END_POINT_ADDRESSES}`,
            method: "GET",
            params,
        });
    }
    async putAddress(form) {
        return await this.authenticationService.fetch({
            url: `${this.url}${DEATHNOTE_API_END_POINT_ADDRESSES}`,
            method: "PUT",
            data: form,
        });
    }
    async deleteAddress(rid) {
        return await this.authenticationService.fetch({
            url: `${this.url}${DEATHNOTE_API_END_POINT_ADDRESSES}`,
            method: "DELETE",
            params: { rid },
        });
    }
    async getEntities(params) {
        return await this.authenticationService.fetch({
            url: `${this.url}${DEATHNOTE_API_END_POINT_ENTITIES}`,
            method: "GET",
            params,
        });
    }
    async getContacts(params) {
        return await this.authenticationService.fetch({
            url: `${this.url}${DEATHNOTE_API_END_POINT_CONTACTS}`,
            method: "GET",
            params,
        });
    }
    async putContact(params) {
        return await this.authenticationService.fetch({
            url: `${this.url}${DEATHNOTE_API_END_POINT_CONTACTS}`,
            method: "PUT",
            params,
        });
    }
    async getEntityFromAddress(address) {
        return await this.authenticationService.fetch({
            url: `${this.url}${DEATHNOTE_API_END_POINT_ENTITIES_FROM}`,
            method: "GET",
            params: { address },
        });
    }
    async validateAddress(body) {
        return await this.authenticationService.fetch({
            url: `${this.url}${DEATHNOTE_API_END_POINT_VALIDATE_ADDRES}`,
            method: "POST",
            data: body,
        });
    }
    async bookCOAddress(body) {
        return await this.authenticationService.fetch({
            url: `${this.url}${DEATHNOTE_API_END_POINT_BOOK_ADDRESS}`,
            method: "POST",
            data: body,
        });
    }
    async bookFiatAddress(body) {
        return await this.authenticationService.fetch({
            url: `${this.url}${DEATHNOTE_API_END_POINT_BOOK_ADDRESS}`,
            method: "POST",
            data: body,
        });
    }
    async bookPEAddress(body) {
        return await this.authenticationService.fetch({
            url: `${this.url}${DEATHNOTE_API_END_POINT_BOOK_ADDRESS}`,
            method: "POST",
            data: body,
        });
    }
    async bookCoinAddress(body) {
        return await this.authenticationService.fetch({
            url: `${this.url}${DEATHNOTE_API_END_POINT_BOOK_ADDRESS}`,
            method: "POST",
            data: body,
        });
    }
    async bookUsdeeuuAddress(body) {
        return await this.authenticationService.fetch({
            url: `${this.url}${DEATHNOTE_API_END_POINT_BOOK_ADDRESS}`,
            method: "POST",
            data: body,
        });
    }
    async bookAddress(body) {
        return await this.authenticationService.fetch({
            url: `${this.url}${DEATHNOTE_API_END_POINT_BOOK_ADDRESS}`,
            method: "POST",
            data: body,
        });
    }
}

class DeathnotePublic extends PublicMicroService {
    constructor(serviceConfig) {
        super(Object.assign(Object.assign({}, serviceConfig), { prefix: serviceConfig.noPrefix ? "" : DEATHNOTE_PREFIX }));
    }
}

/* Delete this comment once you know that the current PREFIX is right*/
const HODOR_PREFIX = "/lb/auth";
const HODOR_API_END_POINT_REGISTRATION_BEGIN = "/member/webauthn/registration/begin";
const HODOR_API_END_POINT_REGISTRATION_FINISH = "/member/webauthn/registration/finish";
const HODOR_API_END_POINT_AUTH_BEGIN = "/member/webauthn/login/begin";
const HODOR_API_END_POINT_AUTH_FINISH = "/member/webauthn/login/finish";
const HODOR_API_END_POINT_DEVICE_ENROLLMENT_BEGIN = "/member/device/enrollment/begin";
const HODOR_API_END_POINT_DEVICE_ENROLLMENT_FINISH = "/member/device/enrollment/finish";
const HODOR_API_END_POINT_SECURE_DEVICE = "/member/device/secure";
const HODOR_API_END_POINT_LOGIN_API_BEGIN = "/identity/login/api/begin";
const HODOR_API_END_POINT_LOGIN_BROWSER_BEGIN = "/identity/login/browser/begin";
const HODOR_API_END_POINT_LOGIN_FINISH = "/identity/login/finish";
const HODOR_API_END_POINT_PASSWORD_GENERATE_CODE = "/identity/password/generate_code";
const HODOR_API_END_POINT_PASSWORD_CONFIRM_CODE = "/identity/password/confirm_code";
const HODOR_API_END_POINT_ME = "/member/me";
const HODOR_API_END_POINT_AUTHORIZATIONS = "/member/authorizations";
const HODOR_API_END_POINT_AUTHORIZATION_UPDATE = "/member/authorizations/update";
const HODOR_API_END_POINT_SESSION_UPGRADE = "/member/session/upgrade";
const HODOR_API_END_POINT_LOGOUT = "/member/logout";
const HODOR_API_END_POINT_ACCESS = "/member/access";
const HODOR_API_END_POINT_ROTATE = "/member/identity/rotate";
const HODOR_API_END_POINT_REGISTER_ORGANIZATION = "/organization/register";
const HODOR_API_END_POINT_MEMBER_ORGANIZATION_MEMBERS = "/member/organization/members";
const HODOR_API_END_POINT_MEMBER_INVITES = "/member/invites";
const HODOR_API_END_POINT_MEMBER_ORGANIZATION_INVITE = "/member/organization/invite";
const HODOR_API_END_POINT_MEMBER_CHALLENGES_BEGIN = "/member/challenges/begin";
const HODOR_API_END_POINT_MEMBER_CHALLENGES_FINISH = "/member/challenges/finish";

class HodorMember {
    constructor(authenticationService) {
        this.url = authenticationService.serviceConfig.noPrefix
            ? `${authenticationService.serviceConfig.url}`
            : `${authenticationService.serviceConfig.url}${HODOR_PREFIX}`;
        this.authenticationService = authenticationService;
    }
    async registrationBegin() {
        return this.authenticationService.fetch({
            method: "POST",
            url: `${this.url}${HODOR_API_END_POINT_REGISTRATION_BEGIN}`,
        });
    }
    async registrationFinish(data) {
        return this.authenticationService.fetch({
            method: "POST",
            url: `${this.url}${HODOR_API_END_POINT_REGISTRATION_FINISH}`,
            data: data,
        });
    }
    async authBegin() {
        return this.authenticationService.fetch({
            method: "POST",
            url: `${this.url}${HODOR_API_END_POINT_AUTH_BEGIN}`,
        });
    }
    async authFinish(data) {
        return this.authenticationService.fetch({
            method: "POST",
            url: `${this.url}${HODOR_API_END_POINT_AUTH_FINISH}`,
            data: data,
        });
    }
    async deviceEnrollmentBegin(verification_id) {
        return this.authenticationService.fetch({
            method: "POST",
            url: `${this.url}${HODOR_API_END_POINT_DEVICE_ENROLLMENT_BEGIN}`,
            data: { verification_id },
        });
    }
    async deviceEnrollmentFinish(data) {
        return this.authenticationService.fetch({
            method: "POST",
            url: `${this.url}${HODOR_API_END_POINT_DEVICE_ENROLLMENT_FINISH}`,
            data: data,
        });
    }
    async getAuthorizations(status) {
        return this.authenticationService.fetch({
            method: "GET",
            url: `${this.url}${HODOR_API_END_POINT_AUTHORIZATIONS}`,
            params: { status: status },
        });
    }
    async getSecureDevice() {
        return this.authenticationService.fetch({
            method: "GET",
            url: `${this.url}${HODOR_API_END_POINT_SECURE_DEVICE}`,
        });
    }
    async getMe() {
        return this.authenticationService.fetch({
            method: "GET",
            url: `${this.url}${HODOR_API_END_POINT_ME}`,
        });
    }
    async updateAuthorization(data) {
        return this.authenticationService.fetch({
            method: "POST",
            url: `${this.url}${HODOR_API_END_POINT_AUTHORIZATION_UPDATE}`,
            data: data,
        });
    }
    async upgradeSession(otp_code) {
        return this.authenticationService.fetch({
            method: "POST",
            url: `${this.url}${HODOR_API_END_POINT_SESSION_UPGRADE}`,
            data: { otp_code },
        });
    }
    async logout(sessionOnly) {
        return this.authenticationService.fetch({
            method: "DELETE",
            url: `${this.url}${HODOR_API_END_POINT_LOGOUT}`,
            params: { session_only: sessionOnly },
        });
    }
    async getAccess() {
        return this.authenticationService.fetch({
            method: "GET",
            url: `${this.url}${HODOR_API_END_POINT_ACCESS}`,
        });
    }
    async rotateSession(uid, type) {
        return this.authenticationService.fetch({
            method: "POST",
            url: `${this.url}${HODOR_API_END_POINT_ROTATE}`,
            data: { uid, type },
        });
    }
    async getOrganizationMembers() {
        return this.authenticationService.fetch({
            method: "GET",
            url: `${this.url}${HODOR_API_END_POINT_MEMBER_ORGANIZATION_MEMBERS}`,
        });
    }
    async updateOrganizationMembers(params) {
        return this.authenticationService.fetch({
            method: "PUT",
            url: `${this.url}${HODOR_API_END_POINT_MEMBER_ORGANIZATION_MEMBERS}`,
            data: params,
        });
    }
    async getInvites(token) {
        return this.authenticationService.fetch({
            method: "GET",
            url: `${this.url}${HODOR_API_END_POINT_MEMBER_INVITES}`,
            params: { token },
        });
    }
    async updateInvite(params) {
        return this.authenticationService.fetch({
            method: "PUT",
            url: `${this.url}${HODOR_API_END_POINT_MEMBER_INVITES}`,
            data: params,
        });
    }
    async updateOrganizationInvite(params) {
        return this.authenticationService.fetch({
            method: "PUT",
            url: `${this.url}${HODOR_API_END_POINT_MEMBER_ORGANIZATION_INVITE}`,
            data: params,
        });
    }
    async createOrganizationInvite(params) {
        return this.authenticationService.fetch({
            method: "POST",
            url: `${this.url}${HODOR_API_END_POINT_MEMBER_ORGANIZATION_INVITE}`,
            data: params,
        });
    }
    async beginChallenge(params) {
        return this.authenticationService.fetch({
            method: "POST",
            url: `${this.url}${HODOR_API_END_POINT_MEMBER_CHALLENGES_BEGIN}`,
            data: params,
        });
    }
    async finishChallenge(params) {
        return this.authenticationService.fetch({
            method: "POST",
            url: `${this.url}${HODOR_API_END_POINT_MEMBER_CHALLENGES_FINISH}`,
            data: params,
        });
    }
}

/* Delete this comment once you know that the current PREFIX is right*/
const ALCHEMIST_PREFIX = "/lb/swap_automations";
const ALCHEMIST_MEMBER_RULES = "/member/rules";
const ALCHEMIST_MEMBER_RULES_DISABLE = "/member/rules/disable";
const ALCHEMIST_MEMBER_RULES_TARJET_DISABLE = "/member/rules/tarjet/disable";
const ALCHEMIST_MEMBER_RULES_EXECUTIONS = "/member/rule_executions";
const ALCHEMIST_MEMBER_EVENTS = "/member/events";

class AlchemistMember {
    constructor(authenticationService) {
        this.url = authenticationService.serviceConfig.noPrefix
            ? `${authenticationService.serviceConfig.url}`
            : `${authenticationService.serviceConfig.url}${ALCHEMIST_PREFIX}`;
        this.authenticationService = authenticationService;
    }
    async getRules(params) {
        return await this.authenticationService.fetch({
            url: `${this.url}${ALCHEMIST_MEMBER_RULES}`,
            method: "GET",
            params,
        });
    }
    async createRules(body) {
        return await this.authenticationService.fetch({
            url: `${this.url}${ALCHEMIST_MEMBER_RULES}`,
            method: "POST",
            data: body,
        });
    }
    async disableRule(id) {
        return await this.authenticationService.fetch({
            url: `${this.url}${ALCHEMIST_MEMBER_RULES_DISABLE}`,
            method: "PUT",
            data: { id },
        });
    }
    async disableTarjetRule(rule_target_id) {
        return await this.authenticationService.fetch({
            url: `${this.url}${ALCHEMIST_MEMBER_RULES_TARJET_DISABLE}`,
            method: "PUT",
            data: { rule_target_id },
        });
    }
    async getRuleExecutions(params) {
        return await this.authenticationService.fetch({
            url: `${this.url}${ALCHEMIST_MEMBER_RULES_EXECUTIONS}`,
            method: "GET",
            params,
        });
    }
    async getEvents(friendly_name, limit, page) {
        return await this.authenticationService.fetch({
            url: `${this.url}${ALCHEMIST_MEMBER_EVENTS}`,
            method: "GET",
            params: { friendly_name, limit, page },
        });
    }
}

/* Delete this comment once you know that the current PREFIX is right*/
const SUBSCRIPTION_PAYMENTS_PREFIX = "/lb/subscription_payments";
const SUBSCRIPTION_PAYMENTS_API_ENDPOINT_CATEGORIES = "/member/categories";
const SUBSCRIPTION_PAYMENTS_API_ENDPOINT_COMPANIES = "/member/companies";
const SUBSCRIPTION_PAYMENTS_API_ENDPOINT_COMPANIES_MAIN = "/member/companies/main";
const SUBSCRIPTION_PAYMENTS_API_ENDPOINT_PAYMENTS = "/member/payments";
const SUBSCRIPTION_PAYMENTS_API_ENDPOINT_SUBSCRIPTIONS = "/member/subscriptions";
const SUBSCRIPTION_PAYMENTS_API_ENDPOINT_DEBTS_DETAILS_BARCODE = "/member/debts/details_barcode";
const SUBSCRIPTION_PAYMENTS_API_ENDPOINT_DEBTS_DETAILS = "/member/debts/details";
const SUBSCRIPTION_PAYMENTS_API_ENDPOINT_DEBTS = "/member/debts";
const SUBSCRIPTION_PAYMENTS_API_ENDPOINT_MEMBERS = "/member/members";
const SUBSCRIPTION_PAYMENTS_API_ENDPOINT_INVOICES = "/member/invoices";

class SubscriptionPaymentsMember {
    constructor(authenticationService) {
        this.url = `${authenticationService.serviceConfig.url}${SUBSCRIPTION_PAYMENTS_PREFIX}`;
        this.authenticationService = authenticationService;
    }
    async getCategories(params) {
        return await this.authenticationService.fetch({
            url: `${this.url}${SUBSCRIPTION_PAYMENTS_API_ENDPOINT_CATEGORIES}`,
            method: "GET",
            params,
        });
    }
    async getCompanies(params) {
        return await this.authenticationService.fetch({
            url: `${this.url}${SUBSCRIPTION_PAYMENTS_API_ENDPOINT_COMPANIES}`,
            method: "GET",
            params,
        });
    }
    async getCompaniesMain(params) {
        return await this.authenticationService.fetch({
            url: `${this.url}${SUBSCRIPTION_PAYMENTS_API_ENDPOINT_COMPANIES_MAIN}`,
            method: "GET",
            params,
        });
    }
    async postPayment(body) {
        return await this.authenticationService.fetch({
            url: `${this.url}${SUBSCRIPTION_PAYMENTS_API_ENDPOINT_PAYMENTS}`,
            method: "POST",
            data: body,
        });
    }
    async getPayments(params) {
        return await this.authenticationService.fetch({
            url: `${this.url}${SUBSCRIPTION_PAYMENTS_API_ENDPOINT_PAYMENTS}/get`,
            method: "POST",
            data: params,
        });
    }
    async deleteSubscription(id) {
        return await this.authenticationService.fetch({
            url: `${this.url}${SUBSCRIPTION_PAYMENTS_API_ENDPOINT_SUBSCRIPTIONS}`,
            method: "DELETE",
            params: { id },
        });
    }
    async putSubscription(body) {
        return await this.authenticationService.fetch({
            url: `${this.url}${SUBSCRIPTION_PAYMENTS_API_ENDPOINT_SUBSCRIPTIONS}`,
            method: "PUT",
            data: body,
        });
    }
    async postSubscription(body) {
        return await this.authenticationService.fetch({
            url: `${this.url}${SUBSCRIPTION_PAYMENTS_API_ENDPOINT_SUBSCRIPTIONS}`,
            method: "POST",
            data: body,
        });
    }
    async getSubscription(params) {
        return await this.authenticationService.fetch({
            url: `${this.url}${SUBSCRIPTION_PAYMENTS_API_ENDPOINT_SUBSCRIPTIONS}/get`,
            method: "POST",
            data: params,
        });
    }
    async getDebtsDetailsBarcode(params) {
        return await this.authenticationService.fetch({
            url: `${this.url}${SUBSCRIPTION_PAYMENTS_API_ENDPOINT_DEBTS_DETAILS_BARCODE}`,
            method: "GET",
            params,
        });
    }
    async getDebtsDetails(params) {
        return await this.authenticationService.fetch({
            url: `${this.url}${SUBSCRIPTION_PAYMENTS_API_ENDPOINT_DEBTS_DETAILS}`,
            method: "POST",
            data: params,
        });
    }
    async getDebts(params) {
        return await this.authenticationService.fetch({
            url: `${this.url}${SUBSCRIPTION_PAYMENTS_API_ENDPOINT_DEBTS}`,
            method: "GET",
            params,
        });
    }
    async putMember(currency_id) {
        return await this.authenticationService.fetch({
            url: `${this.url}${SUBSCRIPTION_PAYMENTS_API_ENDPOINT_MEMBERS}`,
            method: "PUT",
            data: { currency_id },
        });
    }
    async getMember() {
        return await this.authenticationService.fetch({
            url: `${this.url}${SUBSCRIPTION_PAYMENTS_API_ENDPOINT_MEMBERS}`,
            method: "GET",
        });
    }
    async getInvoice(id) {
        return await this.authenticationService.fetch({
            url: `${this.url}${SUBSCRIPTION_PAYMENTS_API_ENDPOINT_INVOICES}/${id}`,
            method: "GET",
        });
    }
    async getInvoicesMonthlyResume() {
        return await this.authenticationService.fetch({
            url: `${this.url}${SUBSCRIPTION_PAYMENTS_API_ENDPOINT_INVOICES}/monthly_resume`,
            method: "GET",
        });
    }
}

/* Delete this comment once you know that the current PREFIX is right*/
const MEMENTO_PREFIX = "/lb/history";
const MEMENTO_API_END_POINT_HISTORY = "/management/history";
const MEMENTO_API_END_CSV_RESUME = "/member/exporter";
const MEMENTO_API_END_PDF_RESUME = "/member/exporter/pdf";

class MementoManagement {
    constructor(authenticationService) {
        this.url = `${authenticationService.serviceConfig.url}`;
        this.authenticationService = authenticationService;
    }
    async getHistory(filter) {
        return await this.authenticationService.fetch({
            method: "POST",
            url: `${this.url}${MEMENTO_API_END_POINT_HISTORY}`,
            data: filter,
            service: "memento",
            action: "read_history",
        });
    }
}

class MementoMember {
    constructor(authenticationService) {
        this.url = `${authenticationService.serviceConfig.url}${MEMENTO_PREFIX}`;
        this.authenticationService = authenticationService;
    }
    getDownloadCsvResumeUrl() {
        return `${this.url}${MEMENTO_API_END_CSV_RESUME}`;
    }
    async downloadCsvResume(body) {
        return this.authenticationService.fetch({
            method: "POST",
            responseType: "blob",
            url: `${this.url}${MEMENTO_API_END_CSV_RESUME}`,
            data: body,
        });
    }
    async getResumeActivities() {
        return this.authenticationService.fetch({
            method: "GET",
            url: `${this.url}${MEMENTO_API_END_CSV_RESUME}`,
        });
    }
    async triggerPdfExport(body) {
        return this.authenticationService.fetch({
            method: "POST",
            url: `${this.url}${MEMENTO_API_END_PDF_RESUME}`,
            data: body,
        });
    }
}

/* Delete this comment once you know that the current PREFIX is right*/
const WITHHOLDINGS_PREFIX = "/lb/withholdings";
const WITHHOLDINGS_MEMBER_TAXES = "/member/taxes";
const WITHHOLDINGS_PUBLIC_TAXES = "/public/taxes";
const WITHHOLDINGS_MEMBER_INVOICE = "/member/invoices";

class WithholdingsMember {
    constructor(authenticationService) {
        this.url = `${authenticationService.serviceConfig.url}${WITHHOLDINGS_PREFIX}`;
        this.authenticationService = authenticationService;
    }
    async getTaxes() {
        return await this.authenticationService.fetch({
            url: `${this.url}${WITHHOLDINGS_MEMBER_TAXES}`,
            method: "GET",
        });
    }
    async getInvoice(params) {
        return await this.authenticationService.fetch({
            url: `${this.url}${WITHHOLDINGS_MEMBER_INVOICE}`,
            method: "GET",
            params,
        });
    }
}

class WithholdingsPublic extends PublicMicroService {
    constructor(serviceConfig) {
        super(Object.assign(Object.assign({}, serviceConfig), { prefix: serviceConfig.noPrefix ? "" : WITHHOLDINGS_PREFIX }));
    }
    async getTaxes() {
        return await this.fetch({
            url: WITHHOLDINGS_PUBLIC_TAXES,
            method: "GET",
        });
    }
}

class ComplianceAdmin {
    constructor(authenticationService) {
        this.url = `${authenticationService.serviceConfig.url}${COMPLIANCE_PREFIX}`;
        this.authenticationService = authenticationService;
    }
    async deleteMembersVerificationData(member_uid) {
        return await this.authenticationService.fetch({
            method: "DELETE",
            url: `${this.url}${COMPLIANCE_API_END_POINT_ADMIN_VERIFICATION_DATA}`,
            params: { uid: member_uid },
        });
    }
    async getMembersVerificationData(member_uid) {
        return await this.authenticationService.fetch({
            method: "GET",
            url: `${this.url}${COMPLIANCE_API_END_POINT_ADMIN_VERIFICATION}/${member_uid}`,
        });
    }
    async postTermsAndConditions(body) {
        const data = constructFormDataFromObjectLegacy(body);
        return await this.authenticationService.fetch({
            method: "POST",
            url: `${this.url}${COMPLIANCE_API_END_POINT_ADMIN_POST_TERMS_AND_CONDITIONS}`,
            data: data,
        });
    }
    async postTermsAndConditionsConstrains(body) {
        return await this.authenticationService.fetch({
            method: "POST",
            url: `${this.url}${COMPLIANCE_API_END_POINT_ADMIN_POST_TYCS_CONSTRAINS}`,
            data: body,
        });
    }
}

class ComplianceManagement {
    constructor(authenticationService) {
        this.url = `${authenticationService.serviceConfig.url}`;
        this.authenticationService = authenticationService;
    }
    async getUser(filter) {
        return await this.authenticationService.fetch({
            method: "POST",
            url: `${this.url}${COMPLIANCE_API_END_POINT_GET_MEMBER}`,
            data: filter,
            service: "compliance",
            action: "read_members",
        });
    }
}

class BarongAdmin {
    constructor(authenticationService) {
        this.url = `${authenticationService.serviceConfig.url}${BARONG_PREFIX}`;
        this.authenticationService = authenticationService;
    }
    async getMemberActivities(filters) {
        return await this.authenticationService.fetch({
            method: "GET",
            url: `${this.url}${BARONG_API_END_POINT_ADMIN_ACTIVITIES}`,
            params: filters && Object.assign(Object.assign({}, filters), { from: filters.from && filters.from.getTime() / 1000, to: filters.to && filters.to.getTime() / 1000 }),
        });
    }
}

class HodorPublic extends PublicMicroService {
    constructor(serviceConfig) {
        super(Object.assign(Object.assign({}, serviceConfig), { prefix: HODOR_PREFIX }));
    }
    async loginBrowserBegin(data) {
        return this.fetch({
            method: "POST",
            url: HODOR_API_END_POINT_LOGIN_BROWSER_BEGIN,
            data: data,
        });
    }
    async loginAPIBegin(data, captcha_response) {
        return this.fetch({
            method: "POST",
            url: HODOR_API_END_POINT_LOGIN_API_BEGIN,
            data: data,
            headers: {
                "x-appcheck-token": captcha_response || "",
            },
        });
    }
    async loginFinish(flow_id, data) {
        return this.fetch({
            method: "POST",
            url: HODOR_API_END_POINT_LOGIN_FINISH,
            params: { flow_id },
            data: data,
            withCredentials: true,
        });
    }
    async registerOrganization(data, captcha_response) {
        return this.fetch({
            method: "POST",
            url: HODOR_API_END_POINT_REGISTER_ORGANIZATION,
            data: data,
            headers: {
                "x-appcheck-token": captcha_response || "",
            },
        });
    }
    async resetPassword(email, captcha_response) {
        return this.fetch({
            method: "POST",
            url: HODOR_API_END_POINT_PASSWORD_GENERATE_CODE,
            data: { email, captcha_response },
            headers: {
                "x-appcheck-token": captcha_response || "",
            },
        });
    }
    async changePassword(form) {
        return this.fetch({
            method: "POST",
            url: HODOR_API_END_POINT_PASSWORD_CONFIRM_CODE,
            data: form,
        });
    }
}

/* Delete this comment once you know that the current PREFIX is right*/
const MERK_PREFIX = "/lb/merk/main";
const MERK_API_END_POINT_MEMBER_PROFILE_SCORING = "/member/profile/scoring";
const MERK_API_END_POINT_MEMBER_PROFILE_FORMS = "/member/profile/form";
const MERK_API_END_POINT_MEMBER_ASSETS = "/member/assets";
const MERK_API_END_POINT_PUBLIC_ASSETS = "/public/assets";
const MERK_API_END_POINT_MEMBER_OPEN_POSITIONS = "/member/positions";
const MERK_API_END_POINT_MEMBER_ACTIVITIES = "/member/activities";
const MERK_API_END_POINT_MEMBER_ORDERS = "/member/orders";
const MERK_API_END_POINT_MEMBER_DOCUMENTS = "/member/documents";
const MERK_API_END_POINT_MEMBER_DOCUMENTS_DOWNLOAD = "/member/documents/download";
const MERK_API_END_POINT_MEMBER_ACCOUNT = "/member/account";
const MERK_API_END_POINT_WITHDRAW_FROM_PROVIDER = "/member/account/withdraw";
const MERK_API_END_POINT_INVEST_FROM_PROVIDER = "/member/orders/cash";
const MERK_API_END_POINT_GET_MARKET_BARS = "/member/marketdata/bars";
const MERK_API_END_POINT_GET_WIZARD = "/member/wizard";
const MERK_API_END_POINT_POST_WIZARD_ANSWER = "/member/wizard/answer";
const MERK_API_END_POINT_CREATE_ACCOUNT = "/member/profile/data";
const MERK_API_END_POINT_GET_BRIDGE_CURRENCY = "/member/account/bridge-currency";
const MERK_API_END_POINT_GET_ACCOUNT_CHARGING_AMOUNT = "/member/account/charging-amount";
const MERK_API_END_POINT_GET_ACCOUNT_CHARGED = "/member/account/charged";
const MERK_API_END_POINT_GET_OPERATOR_CONTACT = "/member/operator/contact";
const MERK_API_END_POINT_GET_MARKET_HOURS = "/public/market-hours";

class MerkMember {
    constructor(authenticationService) {
        this.url = this.url = authenticationService.serviceConfig.noPrefix
            ? `${authenticationService.serviceConfig.url}`
            : `${authenticationService.serviceConfig.url}${MERK_PREFIX}`;
        this.authenticationService = authenticationService;
    }
    getInvestorProfile() {
        return this.authenticationService.fetch({
            method: "GET",
            url: `${this.url}${MERK_API_END_POINT_MEMBER_PROFILE_SCORING}`,
        });
    }
    getInvestorProfileForms() {
        return this.authenticationService.fetch({
            method: "GET",
            url: `${this.url}${MERK_API_END_POINT_MEMBER_PROFILE_FORMS}`,
        });
    }
    submitInvestorProfileForms(body) {
        return this.authenticationService.fetch({
            method: "POST",
            url: `${this.url}${MERK_API_END_POINT_MEMBER_PROFILE_SCORING}`,
            data: body,
        });
    }
    getOpenPositions(params) {
        return this.authenticationService.fetch({
            method: "GET",
            url: `${this.url}${MERK_API_END_POINT_MEMBER_OPEN_POSITIONS}`,
            params,
        });
    }
    getAssets() {
        return this.authenticationService.fetch({
            method: "GET",
            url: `${this.url}${MERK_API_END_POINT_MEMBER_ASSETS}`,
        });
    }
    getAssetsByProvider(params) {
        return this.authenticationService.fetch({
            method: "GET",
            url: `${this.url}${MERK_API_END_POINT_MEMBER_ASSETS}/provider`,
            params,
        });
    }
    getMerkActivities(params) {
        return this.authenticationService.fetch({
            method: "GET",
            url: `${this.url}${MERK_API_END_POINT_MEMBER_ACTIVITIES}`,
            params,
        });
    }
    createOrder(order) {
        return this.authenticationService.fetch({
            method: "POST",
            url: `${this.url}${MERK_API_END_POINT_MEMBER_ORDERS}`,
            data: order,
        });
    }
    createAccount(account) {
        return this.authenticationService.fetch({
            method: "POST",
            url: `${this.url}${MERK_API_END_POINT_CREATE_ACCOUNT}`,
            data: account,
        });
    }
    closePosition(asset_id) {
        return this.authenticationService.fetch({
            method: "DELETE",
            url: `${this.url}${MERK_API_END_POINT_MEMBER_OPEN_POSITIONS}`,
            params: {
                asset_id,
            },
        });
    }
    getDocuments({ start, end, type, provider_id, }) {
        return this.authenticationService.fetch({
            method: "GET",
            url: `${this.url}${MERK_API_END_POINT_MEMBER_DOCUMENTS}`,
            params: {
                start,
                end,
                type,
                provider_id,
            },
        });
    }
    downloadDocument({ document_id, provider_id, }, headers) {
        return this.authenticationService.fetch({
            method: "GET",
            url: `${this.url}${MERK_API_END_POINT_MEMBER_DOCUMENTS_DOWNLOAD}`,
            responseType: "blob",
            params: {
                document_id,
                provider_id,
            },
            headers,
        });
    }
    getAccountInfo(provider_id) {
        return this.authenticationService.fetch({
            method: "GET",
            url: `${this.url}${MERK_API_END_POINT_MEMBER_ACCOUNT}`,
            params: {
                provider_id,
            },
        });
    }
    withdrawFromProvider(params) {
        return this.authenticationService.fetch({
            method: "POST",
            url: `${this.url}${MERK_API_END_POINT_WITHDRAW_FROM_PROVIDER}`,
            data: params,
        });
    }
    investFromProvider(params) {
        return this.authenticationService.fetch({
            method: "POST",
            url: `${this.url}${MERK_API_END_POINT_INVEST_FROM_PROVIDER}`,
            data: params,
        });
    }
    getMarketBars(params) {
        return this.authenticationService.fetch({
            method: "GET",
            url: `${this.url}${MERK_API_END_POINT_GET_MARKET_BARS}`,
            params,
        });
    }
    getWizard() {
        return this.authenticationService.fetch({
            method: "GET",
            url: `${this.url}${MERK_API_END_POINT_GET_WIZARD}`,
        });
    }
    getBridgeCurrency() {
        return this.authenticationService.fetch({
            method: "GET",
            url: `${this.url}${MERK_API_END_POINT_GET_BRIDGE_CURRENCY}`,
        });
    }
    getAccountChargingAmount() {
        return this.authenticationService.fetch({
            method: "GET",
            url: `${this.url}${MERK_API_END_POINT_GET_ACCOUNT_CHARGING_AMOUNT}`,
        });
    }
    checkIfAccountIsCharged() {
        return this.authenticationService.fetch({
            method: "GET",
            url: `${this.url}${MERK_API_END_POINT_GET_ACCOUNT_CHARGED}`,
        });
    }
    postWizardResponse(params) {
        return this.authenticationService.fetch({
            method: "POST",
            url: `${this.url}${MERK_API_END_POINT_POST_WIZARD_ANSWER}`,
            data: params,
        });
    }
    getOperatorContact() {
        return this.authenticationService.fetch({
            method: "GET",
            url: `${this.url}${MERK_API_END_POINT_GET_OPERATOR_CONTACT}`,
        });
    }
}

/* Delete this comment once you know that the current PREFIX is right*/
const BABYLON_PREFIX = "/lb/babylon";
const BABYLON_API_END_POINT_PRICES = "/public/price_provider/get_prices";
const BABYLON_API_END_POINT_CLOSURE_PRICES = "/public/price_provider/get_closure_prices";
const BABYLON_API_END_POINT_VARIATION_PERCENTAGE = "/public/price_provider/get_variations";

class BabylonPublic extends PublicMicroService {
    constructor(serviceConfig) {
        super(Object.assign(Object.assign({}, serviceConfig), { prefix: serviceConfig.noPrefix ? "" : BABYLON_PREFIX }));
    }
    async getPrices(params) {
        return await this.fetch({
            url: BABYLON_API_END_POINT_PRICES,
            method: "GET",
            params: params,
        });
    }
    async getClosurePrices(params) {
        return await this.fetch({
            url: BABYLON_API_END_POINT_CLOSURE_PRICES,
            method: "GET",
            params: params,
        });
    }
    async getVariationPercentage(params) {
        return await this.fetch({
            url: BABYLON_API_END_POINT_VARIATION_PERCENTAGE,
            method: "GET",
            params: params,
        });
    }
}

const JARVIS_PREFIX = "/lb/reporter";
const JARVIS_API_END_POINT_MEMBER_REPORTS = "/member/reports";

class JarvisMember {
    constructor(authenticationService) {
        this.url = authenticationService.serviceConfig.noPrefix
            ? `${authenticationService.serviceConfig.url}`
            : `${authenticationService.serviceConfig.url}${JARVIS_PREFIX}`;
        this.authenticationService = authenticationService;
    }
    postMemberReports(body) {
        return this.authenticationService.fetch({
            method: "POST",
            url: `${this.url}${JARVIS_API_END_POINT_MEMBER_REPORTS}`,
            data: body,
        });
    }
}

class MerkPublic extends PublicMicroService {
    constructor(serviceConfig) {
        super(Object.assign(Object.assign({}, serviceConfig), { prefix: serviceConfig.noPrefix ? "" : MERK_PREFIX }));
    }
    getMarketHours() {
        return this.fetch({
            method: "GET",
            url: `${MERK_API_END_POINT_GET_MARKET_HOURS}`,
        });
    }
    getAssets() {
        return this.fetch({
            method: "GET",
            url: `${MERK_API_END_POINT_PUBLIC_ASSETS}`,
        });
    }
}

/* Delete this comment once you know that the current PREFIX is right*/
const PARTNER_PREFIX = "/lb/partner-api";
const PARTNER_API_END_POINT_MEMBERS = "/member/introducing_broker/members";
const PARTNER_API_END_PIONT_DEPOSIT_ADDRESS = "/member/introducing_broker/deposit_address";
const PARTNER_API_END_PIONT_HISTORY_PLATFORM = "/member/introducing_broker/history/platform";
const PARTNER_API_END_PIONT_HISTORY_MARKET = "/member/introducing_broker/history/market";
const PARTNER_API_END_POINT_ASSOCIATE_TO_BROKER = "/member/introducing_broker/associate";
const PARTNER_API_END_POINT_ASSOCIATED_BROKERS = "/member/introducing_broker";

class PartnerMember {
    constructor(authenticationService) {
        this.url = `${authenticationService.serviceConfig.url}${PARTNER_PREFIX}`;
        this.authenticationService = authenticationService;
    }
    async associateToBroker(params) {
        return await this.authenticationService.fetch({
            method: "POST",
            url: `${this.url}${PARTNER_API_END_POINT_ASSOCIATE_TO_BROKER}`,
            data: params,
        });
    }
    async deleteAssociatedBroker(params) {
        return await this.authenticationService.fetch({
            method: "DELETE",
            url: `${this.url}${PARTNER_API_END_POINT_ASSOCIATE_TO_BROKER}`,
            data: params,
        });
    }
    async getAssociatedBroker() {
        return await this.authenticationService.fetch({
            method: "GET",
            url: `${this.url}${PARTNER_API_END_POINT_ASSOCIATED_BROKERS}`,
        });
    }
    async updateAssociatedBroker(params) {
        return await this.authenticationService.fetch({
            method: "PATCH",
            url: `${this.url}${PARTNER_API_END_POINT_ASSOCIATED_BROKERS}`,
            params,
        });
    }
    async getMembers(params) {
        return await this.authenticationService.fetch({
            method: "GET",
            url: `${this.url}${PARTNER_API_END_POINT_MEMBERS}`,
            params,
        });
    }
    async getMemberDepositAddress(params) {
        return await this.authenticationService.fetch({
            method: "GET",
            url: `${this.url}${PARTNER_API_END_PIONT_DEPOSIT_ADDRESS}`,
            params,
        });
    }
    async getMemberHistoryPlatform(params) {
        return await this.authenticationService.fetch({
            method: "GET",
            url: `${this.url}${PARTNER_API_END_PIONT_HISTORY_PLATFORM}`,
            params,
        });
    }
    async getMemberHistoryMarket(params) {
        return await this.authenticationService.fetch({
            method: "GET",
            url: `${this.url}${PARTNER_API_END_PIONT_HISTORY_MARKET}`,
            params,
        });
    }
}

var USER_STATE;
(function (USER_STATE) {
    USER_STATE["ACTIVE"] = "active";
    USER_STATE["BANNED"] = "banned";
    USER_STATE["PENDING"] = "pending";
    USER_STATE["LOCKED"] = "locked";
    USER_STATE["NONE"] = "none";
    USER_STATE["RESTRICTED"] = "restricted";
    USER_STATE["FROZEN"] = "frozen";
    USER_STATE["RETRIEVED"] = "retrieved";
})(USER_STATE || (USER_STATE = {}));
var USER_LEVEL;
(function (USER_LEVEL) {
    USER_LEVEL[USER_LEVEL["LEVEL_1"] = 1] = "LEVEL_1";
    USER_LEVEL[USER_LEVEL["LEVEL_2"] = 2] = "LEVEL_2";
    USER_LEVEL[USER_LEVEL["LEVEL_3"] = 3] = "LEVEL_3";
})(USER_LEVEL || (USER_LEVEL = {}));
var LABELS_SCOPE;
(function (LABELS_SCOPE) {
    LABELS_SCOPE["PUBLIC"] = "public";
    LABELS_SCOPE["PRIVATE"] = "private";
})(LABELS_SCOPE || (LABELS_SCOPE = {}));

var SWAP_STATUS;
(function (SWAP_STATUS) {
    SWAP_STATUS["PENDING"] = "pending";
    SWAP_STATUS["PROCESSING"] = "processing";
    SWAP_STATUS["ACCEPTED"] = "accepted";
    SWAP_STATUS["RELEASE_FOUNDS"] = "release-founds";
    SWAP_STATUS["REJECTED"] = "rejected";
    SWAP_STATUS["SUCCEED"] = "succeed";
})(SWAP_STATUS || (SWAP_STATUS = {}));

var GENDERS;
(function (GENDERS) {
    GENDERS["MALE"] = "male";
    GENDERS["FEMALE"] = "female";
    GENDERS["OTHER"] = "other";
})(GENDERS || (GENDERS = {}));
var MARITAL_STATUSES;
(function (MARITAL_STATUSES) {
    MARITAL_STATUSES["SINGLE"] = "single";
    MARITAL_STATUSES["MARRIED"] = "married";
    MARITAL_STATUSES["DIVORCED"] = "divorced";
    MARITAL_STATUSES["WIDOW"] = "widowed";
})(MARITAL_STATUSES || (MARITAL_STATUSES = {}));
var OCCUPATIONS;
(function (OCCUPATIONS) {
    OCCUPATIONS["UNEMPLOYED"] = "unemployed";
    OCCUPATIONS["STUDENT"] = "student";
    OCCUPATIONS["EMPLOYED"] = "employed";
    OCCUPATIONS["RETIRED"] = "retired";
    OCCUPATIONS["BUSINESSMAN"] = "self_employed";
})(OCCUPATIONS || (OCCUPATIONS = {}));
var MEMBER_VIDEO_STATUS;
(function (MEMBER_VIDEO_STATUS) {
    MEMBER_VIDEO_STATUS["GENERATED"] = "generated";
    MEMBER_VIDEO_STATUS["SUBMITTED"] = "submitted";
    MEMBER_VIDEO_STATUS["ACCEPTED"] = "accepted";
    MEMBER_VIDEO_STATUS["REJECTED"] = "rejected";
    MEMBER_VIDEO_STATUS["PENDING_REVIEW"] = "pending_review";
})(MEMBER_VIDEO_STATUS || (MEMBER_VIDEO_STATUS = {}));
var MEMBER_PHOTO_STATUS;
(function (MEMBER_PHOTO_STATUS) {
    MEMBER_PHOTO_STATUS["SUBMITTED"] = "submitted";
    MEMBER_PHOTO_STATUS["ACCEPTED"] = "accepted";
    MEMBER_PHOTO_STATUS["REJECTED"] = "rejected";
    MEMBER_PHOTO_STATUS["SYNC_FAILED"] = "sync_failed";
})(MEMBER_PHOTO_STATUS || (MEMBER_PHOTO_STATUS = {}));
var MEMBER_PERSON_STATUS;
(function (MEMBER_PERSON_STATUS) {
    MEMBER_PERSON_STATUS["SUBMITTED"] = "submitted";
    MEMBER_PERSON_STATUS["ACCEPTED"] = "accepted";
    MEMBER_PERSON_STATUS["REJECTED"] = "rejected";
})(MEMBER_PERSON_STATUS || (MEMBER_PERSON_STATUS = {}));
var MEMBER_CUSTOMER_STATUS;
(function (MEMBER_CUSTOMER_STATUS) {
    MEMBER_CUSTOMER_STATUS["SUBMITTED"] = "submitted";
    MEMBER_CUSTOMER_STATUS["ACCEPTED"] = "accepted";
    MEMBER_CUSTOMER_STATUS["REJECTED"] = "rejected";
    MEMBER_CUSTOMER_STATUS["SYNC_FAILED"] = "sync_failed";
})(MEMBER_CUSTOMER_STATUS || (MEMBER_CUSTOMER_STATUS = {}));
var VERIFICATION_STEPS;
(function (VERIFICATION_STEPS) {
    VERIFICATION_STEPS["CUSTOMER"] = "request_customer_data";
    VERIFICATION_STEPS["NPD"] = "request_natural_person_data";
    VERIFICATION_STEPS["ID"] = "request_document_upload";
    VERIFICATION_STEPS["VIDEO"] = "request_video_upload";
    VERIFICATION_STEPS["DONE"] = "done";
    VERIFICATION_STEPS["LOADING"] = "loading";
    VERIFICATION_STEPS["PHOTO_ID"] = "request_photo_id";
    VERIFICATION_STEPS["PHOTO_INFO"] = "show_photo_info";
    VERIFICATION_STEPS["MANUAL_INPUT"] = "request_manual_input";
    VERIFICATION_STEPS["POSSIBLE_CUIT"] = "show_possible_CUIT";
    VERIFICATION_STEPS["EDIT_CUIT"] = "edit_CUIT";
    VERIFICATION_STEPS["LEGAL_ADDRESS"] = "request_legal_address";
    VERIFICATION_STEPS["VIDEO_GESTURES"] = "request_video_gestures";
    VERIFICATION_STEPS["VIDEO_SELFIE"] = "request_video_selfie";
    VERIFICATION_STEPS["VERIFIED"] = "verified";
    VERIFICATION_STEPS["DONE_JURIDICAL"] = "done_juridical";
})(VERIFICATION_STEPS || (VERIFICATION_STEPS = {}));
var PERSON_TYPES;
(function (PERSON_TYPES) {
    PERSON_TYPES["NATURAL"] = "natural";
    PERSON_TYPES["JURIDICAL"] = "juridical";
})(PERSON_TYPES || (PERSON_TYPES = {}));
var AIV_GENDERS;
(function (AIV_GENDERS) {
    AIV_GENDERS["FEMALE"] = "F";
    AIV_GENDERS["MALE"] = "M";
    AIV_GENDERS["OTHER"] = "X";
})(AIV_GENDERS || (AIV_GENDERS = {}));
var VERIFICATION_FLOW_VERSION;
(function (VERIFICATION_FLOW_VERSION) {
    VERIFICATION_FLOW_VERSION[VERIFICATION_FLOW_VERSION["ONE"] = 1] = "ONE";
    VERIFICATION_FLOW_VERSION[VERIFICATION_FLOW_VERSION["TWO"] = 2] = "TWO";
})(VERIFICATION_FLOW_VERSION || (VERIFICATION_FLOW_VERSION = {}));
var USER_DOCUMENTATION_TYPE;
(function (USER_DOCUMENTATION_TYPE) {
    USER_DOCUMENTATION_TYPE["PROFIT"] = "profit";
    USER_DOCUMENTATION_TYPE["PAYCHECK"] = "paycheck";
    USER_DOCUMENTATION_TYPE["PERSONAL_PROPERTY_TAX_STATEMENT"] = "personal_property_tax_statement";
    USER_DOCUMENTATION_TYPE["INCOME_TAX_STATEMENT"] = "income_tax_statement";
    USER_DOCUMENTATION_TYPE["CAR_SALE_STATEMENT"] = "car_sale_statement";
    USER_DOCUMENTATION_TYPE["PROPERTY_SALE_STATEMENT"] = "property_sale_statement";
    USER_DOCUMENTATION_TYPE["RETIREMENT_CHECK"] = "retirement_check";
    USER_DOCUMENTATION_TYPE["COMPENSATION_STATEMENT"] = "compensation_statement";
    USER_DOCUMENTATION_TYPE["ACCOUNTING_CERTIFICATION"] = "accounting_certification";
    USER_DOCUMENTATION_TYPE["OTHER"] = "other";
    USER_DOCUMENTATION_TYPE["COMPANY_EXTRACT"] = "company_extract";
    USER_DOCUMENTATION_TYPE["MINUTES_OF_MEETING_OF_PARTNERS"] = "minutes_of_meeting_of_partners";
    USER_DOCUMENTATION_TYPE["SOURCE_OF_FUNDS"] = "source_of_funds";
    USER_DOCUMENTATION_TYPE["SWORN_DECLARATION"] = "sworn_declaration";
    USER_DOCUMENTATION_TYPE["DIRECTOR_PASSPORT"] = "director_passport";
    USER_DOCUMENTATION_TYPE["VERIFICATION_SUMARY"] = "verification_summary";
    USER_DOCUMENTATION_TYPE["ULTIMATE_BENEFICIAL_OWNERS"] = "ultimate_beneficial_owners";
})(USER_DOCUMENTATION_TYPE || (USER_DOCUMENTATION_TYPE = {}));
var EXTRA_DOCUMENT_STATUS;
(function (EXTRA_DOCUMENT_STATUS) {
    EXTRA_DOCUMENT_STATUS["SUBMITTED"] = "submitted";
    EXTRA_DOCUMENT_STATUS["ACCEPTED"] = "accepted";
    EXTRA_DOCUMENT_STATUS["REJECTED"] = "rejected";
})(EXTRA_DOCUMENT_STATUS || (EXTRA_DOCUMENT_STATUS = {}));
var INFORMATION_REQUEST_TYPE;
(function (INFORMATION_REQUEST_TYPE) {
    INFORMATION_REQUEST_TYPE["TEXT"] = "text";
    INFORMATION_REQUEST_TYPE["DOCUMENT"] = "document";
})(INFORMATION_REQUEST_TYPE || (INFORMATION_REQUEST_TYPE = {}));
var INFORMATION_REQUEST_STATUS;
(function (INFORMATION_REQUEST_STATUS) {
    INFORMATION_REQUEST_STATUS["NOTIFY_MEMBER"] = "notify_member";
    INFORMATION_REQUEST_STATUS["AWAITING_MEMBER"] = "awaiting_member";
    INFORMATION_REQUEST_STATUS["NOTIFY_ADMIN"] = "notify_admin";
    INFORMATION_REQUEST_STATUS["AWAITING_ADMIN"] = "awaiting_admin";
    INFORMATION_REQUEST_STATUS["ACCEPTED"] = "accepted";
    INFORMATION_REQUEST_STATUS["REJECTED"] = "rejected";
    INFORMATION_REQUEST_STATUS["ARCHIVED"] = "archived";
})(INFORMATION_REQUEST_STATUS || (INFORMATION_REQUEST_STATUS = {}));
var VERIFICATION_REJECTED_REASONS;
(function (VERIFICATION_REJECTED_REASONS) {
    VERIFICATION_REJECTED_REASONS["ALREADY_TAKEN"] = "national_id_already_taken";
})(VERIFICATION_REJECTED_REASONS || (VERIFICATION_REJECTED_REASONS = {}));
var FLOW_NEXT_STEP;
(function (FLOW_NEXT_STEP) {
    FLOW_NEXT_STEP["CREATE_NEW_FLOW"] = "create_new_flow";
    FLOW_NEXT_STEP["NATIONAL_ID"] = "national_id";
    FLOW_NEXT_STEP["NATIONAL_ID_RESUBMIT"] = "national_id_resubmit";
    FLOW_NEXT_STEP["NATIONAL_ID_QUEUED"] = "national_id_queued";
    FLOW_NEXT_STEP["VERIFICATION_REJECTED"] = "verification_rejected";
    FLOW_NEXT_STEP["AWAITING_NATIONAL_ID_VERIFICATION"] = "awating_national_id_verification";
    FLOW_NEXT_STEP["NATIONAL_ID_CONFIRM"] = "national_id_data_confirm";
    FLOW_NEXT_STEP["DATA_SCORE"] = "data_score";
    FLOW_NEXT_STEP["NATURAL_PERSON_DATA"] = "natural_person_data";
    FLOW_NEXT_STEP["AWAITING_SCORE_CALCULATION"] = "awaiting_score_calculation";
    FLOW_NEXT_STEP["AWAITING_ADMIN_REVIEW"] = "awaiting_admin_review";
    FLOW_NEXT_STEP["AWAITING_SCORE_EVALUATION"] = "awaiting_score_evaluation";
    FLOW_NEXT_STEP["LIVENESS"] = "liveness";
    FLOW_NEXT_STEP["AWAITING_LIVENESS_VERIFICATION"] = "awaiting_liveness_verification";
    FLOW_NEXT_STEP["AWAITING_LIVENESS_COMPARISON"] = "awaiting_liveness_comparasion";
    FLOW_NEXT_STEP["LIVENESS_REJECTED"] = "liveness_rejected";
    FLOW_NEXT_STEP["COMPLETED"] = "completed";
    FLOW_NEXT_STEP["SKIPPED"] = "skipped";
    FLOW_NEXT_STEP["LEGAL_ADDRESS"] = "legal_address";
    FLOW_NEXT_STEP["CONTINUE_NEW_FLOW"] = "continue_new_flow";
    FLOW_NEXT_STEP["SKIP"] = "skip";
    FLOW_NEXT_STEP["CONTINUE"] = "continue";
    FLOW_NEXT_STEP["LIVENESS_STARTED"] = "liveness_started";
    FLOW_NEXT_STEP["LIVENESS_ACCEPTED"] = "liveness_accepted";
    FLOW_NEXT_STEP["LIVENESS_ENDED"] = "liveness_ended";
    FLOW_NEXT_STEP["AWAITING_LIVENESS_COMPLETED"] = "awaiting_liveness_completed";
    FLOW_NEXT_STEP["ONBOARDING_FLOW_NOT_COMPLETED"] = "onboarding_flow_not_completed";
    FLOW_NEXT_STEP["REVERIFICATION_MAX_ATTEMPTS_REACHED"] = "reverification_max_attempts_reached";
    FLOW_NEXT_STEP["LIVENESS_DOCKET_REJECTED"] = "liveness_docket_rejected";
    FLOW_NEXT_STEP["LIVENESS_DOCKET_REVIEW"] = "liveness_docket_review";
    FLOW_NEXT_STEP["INVALID_COUNTRY"] = "invalid_country";
})(FLOW_NEXT_STEP || (FLOW_NEXT_STEP = {}));
var LIVENESS_MESSAGES;
(function (LIVENESS_MESSAGES) {
    LIVENESS_MESSAGES["NUMBER_DOESNT_MATCH"] = "procedure_number_does_not_match";
    LIVENESS_MESSAGES["DOCUMENT_EXTRACTION_FAILED"] = "document_field_extraction_failed";
    LIVENESS_MESSAGES["NOT_FOLLOW_INSTRUCTIONS"] = "liveness_user_did_not_follow_instrucctions";
})(LIVENESS_MESSAGES || (LIVENESS_MESSAGES = {}));
var FLOW_COUNTRY;
(function (FLOW_COUNTRY) {
    FLOW_COUNTRY["PE"] = "PE";
    FLOW_COUNTRY["CO"] = "CO";
    FLOW_COUNTRY["AR"] = "AR";
})(FLOW_COUNTRY || (FLOW_COUNTRY = {}));
var LIMITS_APPLIES_TO;
(function (LIMITS_APPLIES_TO) {
    LIMITS_APPLIES_TO["LIMIT_INCREMENTATION"] = "limit_incrementation";
    LIMITS_APPLIES_TO["LIMIT_INCREMENTATION_ARB"] = "limit_incrementation_arb";
    LIMITS_APPLIES_TO["ONBOARDING"] = "onboarding";
    LIMITS_APPLIES_TO["UPDATE"] = "update";
    LIMITS_APPLIES_TO["ERROR_ONBOARDING"] = "error - onboarding";
    LIMITS_APPLIES_TO["ONBOARDING_CONTROL_CHAIN"] = "onboarding_control-chain";
})(LIMITS_APPLIES_TO || (LIMITS_APPLIES_TO = {}));
var FLOW_CATEGORIES;
(function (FLOW_CATEGORIES) {
    FLOW_CATEGORIES["ONBOARDING"] = "onboarding";
    FLOW_CATEGORIES["DOCKET_UPDATE"] = "docket_update";
    FLOW_CATEGORIES["REVERIFICATION"] = "reverification";
})(FLOW_CATEGORIES || (FLOW_CATEGORIES = {}));
var PRODUCT_NAME_TERMS_AND_CONDITION;
(function (PRODUCT_NAME_TERMS_AND_CONDITION) {
    PRODUCT_NAME_TERMS_AND_CONDITION["PLATFORM"] = "platform";
    PRODUCT_NAME_TERMS_AND_CONDITION["ARS"] = "ars";
    PRODUCT_NAME_TERMS_AND_CONDITION["CARDS"] = "cards";
    PRODUCT_NAME_TERMS_AND_CONDITION["PAYMENT_SERVICES"] = "pds";
    PRODUCT_NAME_TERMS_AND_CONDITION["CVU_PROVIDED"] = "cvu_provided";
    PRODUCT_NAME_TERMS_AND_CONDITION["USDEEUU"] = "usdeeuu";
    PRODUCT_NAME_TERMS_AND_CONDITION["REFERRALS"] = "referrals";
    PRODUCT_NAME_TERMS_AND_CONDITION["REFERRALS_CAMPAING"] = "rewards_campaigns";
    PRODUCT_NAME_TERMS_AND_CONDITION["ARBITRATORS"] = "arbitrators";
    PRODUCT_NAME_TERMS_AND_CONDITION["USDL"] = "usdl";
    PRODUCT_NAME_TERMS_AND_CONDITION["FCI"] = "fci";
    PRODUCT_NAME_TERMS_AND_CONDITION["CRYPTO"] = "crypto";
    PRODUCT_NAME_TERMS_AND_CONDITION["ALPACA"] = "alpaca";
    PRODUCT_NAME_TERMS_AND_CONDITION["ALYC_ARG"] = "alyc-arg";
})(PRODUCT_NAME_TERMS_AND_CONDITION || (PRODUCT_NAME_TERMS_AND_CONDITION = {}));

var LEGAL_NOTICES_CURRENCY_TYPES;
(function (LEGAL_NOTICES_CURRENCY_TYPES) {
    LEGAL_NOTICES_CURRENCY_TYPES["BUYTERMS"] = "buy-terms";
    LEGAL_NOTICES_CURRENCY_TYPES["TERMS"] = "terms";
})(LEGAL_NOTICES_CURRENCY_TYPES || (LEGAL_NOTICES_CURRENCY_TYPES = {}));
var LEGAL_NOTICES_COUNTRY_TYPES;
(function (LEGAL_NOTICES_COUNTRY_TYPES) {
    LEGAL_NOTICES_COUNTRY_TYPES["PRIVACY_POLICY"] = "privacy_policy";
    LEGAL_NOTICES_COUNTRY_TYPES["TERMS"] = "terms";
    LEGAL_NOTICES_COUNTRY_TYPES["PEP"] = "pep";
})(LEGAL_NOTICES_COUNTRY_TYPES || (LEGAL_NOTICES_COUNTRY_TYPES = {}));

var ENTITY_TYPE;
(function (ENTITY_TYPE) {
    ENTITY_TYPE["FIAT"] = "fiat";
    ENTITY_TYPE["CRYPTO"] = "crypto";
    ENTITY_TYPE["EXCHANGE"] = "exchange";
    ENTITY_TYPE["WALLET"] = "wallet";
})(ENTITY_TYPE || (ENTITY_TYPE = {}));
var CURRENCY_TYPE;
(function (CURRENCY_TYPE) {
    CURRENCY_TYPE["FIAT"] = "fiat";
    CURRENCY_TYPE["COIN"] = "coin";
    CURRENCY_TYPE["ALL"] = "all";
})(CURRENCY_TYPE || (CURRENCY_TYPE = {}));
var ENTITY_STATE;
(function (ENTITY_STATE) {
    ENTITY_STATE["ACTIVE"] = "active";
    ENTITY_STATE["ARCHIVED"] = "archived";
})(ENTITY_STATE || (ENTITY_STATE = {}));
var CONTACT_STATE;
(function (CONTACT_STATE) {
    CONTACT_STATE["ACTIVE"] = "active";
    CONTACT_STATE["ARCHIVED"] = "archived";
})(CONTACT_STATE || (CONTACT_STATE = {}));
var CONTACT_TYPE;
(function (CONTACT_TYPE) {
    CONTACT_TYPE["ENTITY"] = "entity";
    CONTACT_TYPE["PERSON"] = "person";
})(CONTACT_TYPE || (CONTACT_TYPE = {}));
var CONTACT_ADDRESS_STATE;
(function (CONTACT_ADDRESS_STATE) {
    CONTACT_ADDRESS_STATE["PENDING"] = "pending";
    CONTACT_ADDRESS_STATE["ACTIVE"] = "active";
    CONTACT_ADDRESS_STATE["PROCESSING"] = "processing";
    CONTACT_ADDRESS_STATE["UNDER_REVIEW"] = "under_review";
    CONTACT_ADDRESS_STATE["ARCHIVED"] = "archived";
    CONTACT_ADDRESS_STATE["AML_BLOCKED"] = "aml_blocked";
    CONTACT_ADDRESS_STATE["REJECTED"] = "rejected";
})(CONTACT_ADDRESS_STATE || (CONTACT_ADDRESS_STATE = {}));
var ACCOUNT_TYPE;
(function (ACCOUNT_TYPE) {
    ACCOUNT_TYPE["SAVING"] = "ahorro";
    ACCOUNT_TYPE["BOOKING"] = "corriente";
    ACCOUNT_TYPE["USD_SAVING"] = "savings";
    ACCOUNT_TYPE["USD_BOOKING"] = "checking";
})(ACCOUNT_TYPE || (ACCOUNT_TYPE = {}));
var PERSON_TYPE;
(function (PERSON_TYPE) {
    PERSON_TYPE["INDIVIDUAL"] = "F";
    PERSON_TYPE["BUSINESS"] = "J";
})(PERSON_TYPE || (PERSON_TYPE = {}));

var FEE_TYPES;
(function (FEE_TYPES) {
    FEE_TYPES["VALUE"] = "value";
    FEE_TYPES["PERCENTAGE"] = "percentage";
    FEE_TYPES["BOTH"] = "both";
})(FEE_TYPES || (FEE_TYPES = {}));
var HISTORY_OPERATION_TYPE;
(function (HISTORY_OPERATION_TYPE) {
    HISTORY_OPERATION_TYPE["TRADE"] = "trade";
    HISTORY_OPERATION_TYPE["DEPOSIT"] = "deposit";
    HISTORY_OPERATION_TYPE["WITHDRAW"] = "withdraw";
    HISTORY_OPERATION_TYPE["MERK_ACCOUNT_MOVEMENT"] = "merk_account_movement";
    HISTORY_OPERATION_TYPE["REFERRER_PAYOUT"] = "referrer_payout";
    HISTORY_OPERATION_TYPE["INTEREST_PAYMENT"] = "interest_payment";
    HISTORY_OPERATION_TYPE["CARD_TRANSACTIONS"] = "card_transactions";
    HISTORY_OPERATION_TYPE["ADJUSTMENT"] = "adjustment";
    HISTORY_OPERATION_TYPE["REWARD"] = "reward";
    HISTORY_OPERATION_TYPE["AUTOSWAP"] = "autoswap";
    HISTORY_OPERATION_TYPE["PAYMENT"] = "payment";
    HISTORY_OPERATION_TYPE["WITHHOLDING"] = "withholding";
    HISTORY_OPERATION_TYPE["WITHHOLDINGS_WITHHOLDING"] = "withholdings_withholding";
    HISTORY_OPERATION_TYPE["DOLLARIZE"] = "dollarize";
    HISTORY_OPERATION_TYPE["MERK_ORDER"] = "merk_order";
})(HISTORY_OPERATION_TYPE || (HISTORY_OPERATION_TYPE = {}));
var BANNER_TYPE;
(function (BANNER_TYPE) {
    BANNER_TYPE["HOME"] = "home";
    BANNER_TYPE["USDEEUU"] = "usdeeuu";
})(BANNER_TYPE || (BANNER_TYPE = {}));
var CURRENCY_FILTER_PARAMS;
(function (CURRENCY_FILTER_PARAMS) {
    CURRENCY_FILTER_PARAMS["PREFERRED_CURRENCY"] = "preferred_currency";
    CURRENCY_FILTER_PARAMS["PAYMENT_CARD_CURRENCY"] = "payment_card_currency";
})(CURRENCY_FILTER_PARAMS || (CURRENCY_FILTER_PARAMS = {}));
var VERIFICATION_ENTERPRISE_STEPS;
(function (VERIFICATION_ENTERPRISE_STEPS) {
    VERIFICATION_ENTERPRISE_STEPS["CUSTOMER_DATA"] = "customer-data";
    VERIFICATION_ENTERPRISE_STEPS["JURIDICAL_DATA"] = "juridical-data";
    VERIFICATION_ENTERPRISE_STEPS["EXTRA_DOCS"] = "extra-docs";
    VERIFICATION_ENTERPRISE_STEPS["ENDED"] = "ended";
})(VERIFICATION_ENTERPRISE_STEPS || (VERIFICATION_ENTERPRISE_STEPS = {}));
var ONBOARDING_FLOW;
(function (ONBOARDING_FLOW) {
    ONBOARDING_FLOW["LOCAL"] = "local";
    ONBOARDING_FLOW["FOREIGN"] = "FOREIGN";
})(ONBOARDING_FLOW || (ONBOARDING_FLOW = {}));
var ROUTE_TYPE;
(function (ROUTE_TYPE) {
    ROUTE_TYPE["PROMOTION_CODES_ASIGN"] = "promotion_code_asign";
    ROUTE_TYPE["DOCKET_UPDATE_VERIFY_IDENTITY"] = "docket_update_verify_identity";
    ROUTE_TYPE["DOCKET_UPDATE_LEGAL_ADDRESS"] = "docket_update_legal_address";
    ROUTE_TYPE["DOCKET_UPDATE_START_METAMAP"] = "docket_update_start_metamap";
    ROUTE_TYPE["DOCKET_UPDATE_ON_VERIFICATION"] = "docket_update_on_verification";
    ROUTE_TYPE["DOCKET_UPDATE_REJECTED"] = "docket_update_rejected";
    ROUTE_TYPE["DOCKET_UPDATE_ADMIN_REVIEW"] = "docket_update_admin_review";
    ROUTE_TYPE["DOCKET_UPDATE_LIVENESS_REDO_ERRORS"] = "docket_update_liveness_redo_errors";
    ROUTE_TYPE["USER_VERIFICATION_CHOOSE_COUNTRY"] = "user_verification_choose_country";
    ROUTE_TYPE["USER_VERIFICATION_INVALID_COUNTRY"] = "user_verification_invalid_country";
    ROUTE_TYPE["USER_VERIFICATION_COUNTRY_TYPE_ID"] = "user_verification_country_type_id";
    ROUTE_TYPE["USER_VERIFICATION_NATIONAL_ID_INVALID"] = "user_verification_national_id_invalid";
    ROUTE_TYPE["USER_VERIFICATION_NATIONAL_ID_CONFIRM"] = "user_verification_national_id_confirm";
    ROUTE_TYPE["USER_VERIFICATION_NATIONAL_ID_RESUBMIT"] = "user_verification_national_id_resubmit";
    ROUTE_TYPE["USER_VERIFICATION_NATIONAL_ID_QUEUED"] = "user_verification_national_id_queued";
    ROUTE_TYPE["USER_VERIFICATION_ERROR_REJECTED"] = "user_verification_error_rejected";
    ROUTE_TYPE["USER_VERIFICATION_MY_CUIT"] = "user_verification_my_cuit";
    ROUTE_TYPE["USER_VERIFICATION_LEGAL_ADDRESS"] = "user_verification_legal_address";
    ROUTE_TYPE["USER_VERIFICATION_NAME_INPUT"] = "user_verification_name_input";
    ROUTE_TYPE["USER_VERIFICATION_PE_COL_GENDER"] = "user_verification_pe_col_gender";
    ROUTE_TYPE["USER_VERIFICATION_START_METAMAP"] = "user_verification_start_metamap";
    ROUTE_TYPE["USER_VERIFICATION_AWAITING_NATIONAL_ID"] = "user_verification_awaiting_national_id";
    ROUTE_TYPE["USER_VERIFICATION_AWAITING_ADMIN_REVIEW"] = "user_verification_awaiting_admin_review";
    ROUTE_TYPE["USER_VERIFICATION_AWAITING_SCORE"] = "user_verification_awaiting_score";
    ROUTE_TYPE["USER_VERIFICATION_AWAITING_LIVENESS"] = "user_verification_awaiting_liveness";
    ROUTE_TYPE["USER_VERIFICATION_AWAITING_LIVENESS_COMPLETED"] = "user_verification_awaiting_liveness_completed";
    ROUTE_TYPE["USER_VERIFICATION_AWAITING"] = "user_verification_awaiting";
    ROUTE_TYPE["USER_VERIFICATION_ERROR"] = "user_verification_error";
    ROUTE_TYPE["USER_VERIFICATION_LIVENESS_REDO_ERRORS"] = "user_verification_liveness_redo_errors";
    ROUTE_TYPE["USER_VERIFICATION_JURIDICAL_PERSONS_WEB"] = "user_verification_juridical_persons_web";
    ROUTE_TYPE["TERMS_AND_CONDITIONS"] = "terms_and_conditions";
    ROUTE_TYPE["ONBOARDING_WELCOME"] = "onboarding_welcome";
    ROUTE_TYPE["CONVERSION_HOME"] = "conversion_home";
    ROUTE_TYPE["RETRIEVE_INFO"] = "retrieve_info";
    ROUTE_TYPE["FREEZE_INFO"] = "freeze_info";
    ROUTE_TYPE["HOME"] = "home";
    ROUTE_TYPE["ERROR_PAGE"] = "error";
    ROUTE_TYPE["NOT_ACTIVE_INFO"] = "not_active_info";
    ROUTE_TYPE["REVERIFICATION_ENROLL"] = "reverification_enroll";
    ROUTE_TYPE["REVERIFICATION_VERIFY_IDENTITY"] = "reverification_verify_identity";
    ROUTE_TYPE["REVERIFICATION_ON_ERROR"] = "reverification_on_error";
    ROUTE_TYPE["REVERIFICATION_ON_VALIDATION"] = "reverification_on_validation";
    ROUTE_TYPE["REVERIFICATION_MAX_ATTEMPTS_REACHED"] = "reverification_max_attempts_reached";
    ROUTE_TYPE["REVERIFICATION_ON_START_ERROR"] = "reverification_on_start_error";
    ROUTE_TYPE["REVERIFICATION_START_METAMAP"] = "reverification_start_metamap";
})(ROUTE_TYPE || (ROUTE_TYPE = {}));
var ACCOUNT_ADVISOR_TYPE;
(function (ACCOUNT_ADVISOR_TYPE) {
    ACCOUNT_ADVISOR_TYPE["PERSONAL_ADVISOR"] = "personal_advisor";
    ACCOUNT_ADVISOR_TYPE["INTERCOM"] = "intercom";
})(ACCOUNT_ADVISOR_TYPE || (ACCOUNT_ADVISOR_TYPE = {}));
var CATEGORIES_AUTOMATED_ITEM;
(function (CATEGORIES_AUTOMATED_ITEM) {
    CATEGORIES_AUTOMATED_ITEM["LAST_ADDED_SECURITIES"] = "last_added_securities";
    CATEGORIES_AUTOMATED_ITEM["TOP_SECURITIES"] = "top_securities";
    CATEGORIES_AUTOMATED_ITEM["BOTTOM_SECURITIES"] = "bottom_securities";
    CATEGORIES_AUTOMATED_ITEM["TOP_CRIPTO"] = "top_crypto";
})(CATEGORIES_AUTOMATED_ITEM || (CATEGORIES_AUTOMATED_ITEM = {}));

var TRADE_SIDES;
(function (TRADE_SIDES) {
    TRADE_SIDES["BUY"] = "buy";
    TRADE_SIDES["SELL"] = "sell";
})(TRADE_SIDES || (TRADE_SIDES = {}));

var DEPOSIT_STATE_OPTIONS;
(function (DEPOSIT_STATE_OPTIONS) {
    DEPOSIT_STATE_OPTIONS["SUBMITTED"] = "submitted";
    DEPOSIT_STATE_OPTIONS["CANCELED"] = "canceled";
    DEPOSIT_STATE_OPTIONS["BLOCKED"] = "blocked";
    DEPOSIT_STATE_OPTIONS["REJECTED"] = "rejected";
    DEPOSIT_STATE_OPTIONS["ACCEPTED"] = "accepted";
    DEPOSIT_STATE_OPTIONS["SKIPPED"] = "skipped";
    DEPOSIT_STATE_OPTIONS["COLLECTED"] = "collected";
})(DEPOSIT_STATE_OPTIONS || (DEPOSIT_STATE_OPTIONS = {}));
var TIME_PERIOD_VALUES;
(function (TIME_PERIOD_VALUES) {
    TIME_PERIOD_VALUES["DAILY"] = "daily";
    TIME_PERIOD_VALUES["MONTHLY"] = "monthly";
    TIME_PERIOD_VALUES["YEARLY"] = "yearly";
})(TIME_PERIOD_VALUES || (TIME_PERIOD_VALUES = {}));
var KIND_VALUES;
(function (KIND_VALUES) {
    KIND_VALUES["VOLUME"] = "volume";
    KIND_VALUES["QUANTITY"] = "quantity";
})(KIND_VALUES || (KIND_VALUES = {}));
var OPERATIONS_VALUES;
(function (OPERATIONS_VALUES) {
    OPERATIONS_VALUES["WITHDRAW"] = "withdraw";
    OPERATIONS_VALUES["DEPOSIT"] = "deposit";
})(OPERATIONS_VALUES || (OPERATIONS_VALUES = {}));
var WithdrawOperationState;
(function (WithdrawOperationState) {
    WithdrawOperationState["PREPARED"] = "prepared";
    WithdrawOperationState["SUBMITTED"] = "submitted";
    WithdrawOperationState["CANCELED"] = "canceled";
    WithdrawOperationState["ACCEPTED"] = "accepted";
    WithdrawOperationState["REJECTED"] = "rejected";
    WithdrawOperationState["PROCESSING"] = "processing";
    WithdrawOperationState["SKIPPED"] = "skipped";
    WithdrawOperationState["SUCCEED"] = "succeed";
    WithdrawOperationState["FAILED"] = "failed";
    WithdrawOperationState["WAITING_AUTHORIZATION"] = "waiting_authorization";
    WithdrawOperationState["EXPIRED"] = "expired";
    WithdrawOperationState["UNAUTHORIZED"] = "unauthorized";
})(WithdrawOperationState || (WithdrawOperationState = {}));
var FEE_TYPE;
(function (FEE_TYPE) {
    FEE_TYPE["VALUE"] = "value";
    FEE_TYPE["PERCENTAGE"] = "percentage";
    FEE_TYPE["BOTH"] = "both";
})(FEE_TYPE || (FEE_TYPE = {}));
var WithdrawRequestDetailsState;
(function (WithdrawRequestDetailsState) {
    WithdrawRequestDetailsState["SUBMITTED"] = "submitted";
    WithdrawRequestDetailsState["VERIFYING"] = "verifying";
    WithdrawRequestDetailsState["PENDING"] = "pending";
    WithdrawRequestDetailsState["ERROR"] = "error";
    WithdrawRequestDetailsState["FAILED"] = "failed";
    WithdrawRequestDetailsState["ARCHIVED"] = "archived";
    WithdrawRequestDetailsState["PROCESSING"] = "processing";
    WithdrawRequestDetailsState["SUCCEED"] = "succeed";
})(WithdrawRequestDetailsState || (WithdrawRequestDetailsState = {}));
var WithdrawRequestState;
(function (WithdrawRequestState) {
    WithdrawRequestState["SUBMITTED"] = "submitted";
    WithdrawRequestState["CREATING"] = "creating";
    WithdrawRequestState["VERIFYING"] = "verifying";
    WithdrawRequestState["PENDING"] = "pending";
    WithdrawRequestState["PROCESSING"] = "processing";
    WithdrawRequestState["SUCCEED"] = "succeed";
    WithdrawRequestState["FAILED"] = "failed";
    WithdrawRequestState["REVIEW"] = "review";
})(WithdrawRequestState || (WithdrawRequestState = {}));
var EXTERNAL_PAYMENT_ADDRESS_STATES;
(function (EXTERNAL_PAYMENT_ADDRESS_STATES) {
    EXTERNAL_PAYMENT_ADDRESS_STATES["PENDING"] = "pending";
    EXTERNAL_PAYMENT_ADDRESS_STATES["REJECTED"] = "rejected";
    EXTERNAL_PAYMENT_ADDRESS_STATES["ACTIVE"] = "active";
    EXTERNAL_PAYMENT_ADDRESS_STATES["PROCESSING"] = "processing";
    EXTERNAL_PAYMENT_ADDRESS_STATES["UNDER_REVIEW"] = "under_review";
    EXTERNAL_PAYMENT_ADDRESS_STATES["ARCHIVED"] = "archived";
    EXTERNAL_PAYMENT_ADDRESS_STATES["AML_BLOCKED"] = "aml_blocked";
})(EXTERNAL_PAYMENT_ADDRESS_STATES || (EXTERNAL_PAYMENT_ADDRESS_STATES = {}));
var EXTERNAL_PAYMENT_ADDRESS_ACCOUNT_TYPES;
(function (EXTERNAL_PAYMENT_ADDRESS_ACCOUNT_TYPES) {
    EXTERNAL_PAYMENT_ADDRESS_ACCOUNT_TYPES["AHORRO"] = "ahorro";
    EXTERNAL_PAYMENT_ADDRESS_ACCOUNT_TYPES["CORRIENTE"] = "corriente";
})(EXTERNAL_PAYMENT_ADDRESS_ACCOUNT_TYPES || (EXTERNAL_PAYMENT_ADDRESS_ACCOUNT_TYPES = {}));
var DEPOSIT_ADDRESS_STATE;
(function (DEPOSIT_ADDRESS_STATE) {
    DEPOSIT_ADDRESS_STATE["PENDING"] = "pending";
    DEPOSIT_ADDRESS_STATE["CREATED"] = "created";
    DEPOSIT_ADDRESS_STATE["AWAITING_TOS"] = "awaiting_tos";
})(DEPOSIT_ADDRESS_STATE || (DEPOSIT_ADDRESS_STATE = {}));
var REMAINING_LIMITS_CATEGORY;
(function (REMAINING_LIMITS_CATEGORY) {
    REMAINING_LIMITS_CATEGORY["OPERATIONAL"] = "operational";
    REMAINING_LIMITS_CATEGORY["OWN_ACCOUNT"] = "own_account";
    REMAINING_LIMITS_CATEGORY["THIRD_PARTY_ACCOUNT"] = "third_party_account";
    REMAINING_LIMITS_CATEGORY["COIN_CONVERTED"] = "coin_converted";
})(REMAINING_LIMITS_CATEGORY || (REMAINING_LIMITS_CATEGORY = {}));
var DEPOSIT_ADDRESS_TYPE;
(function (DEPOSIT_ADDRESS_TYPE) {
    DEPOSIT_ADDRESS_TYPE["PERSONAL_ACCOUNT_ARS"] = "PERSONAL_ACCOUNT_ARS";
    DEPOSIT_ADDRESS_TYPE["PERSONAL_ACCOUNT_CRYPTO"] = "PERSONAL_ACCOUNT_CRYPTO";
    DEPOSIT_ADDRESS_TYPE["PERSONAL_ACCOUNT_BRIDGE"] = "PERSONAL_ACCOUNT_BRIDGE";
    DEPOSIT_ADDRESS_TYPE["OMNIBUS_ACCOUNT"] = "OMNIBUS_ACCOUNT";
    DEPOSIT_ADDRESS_TYPE["PERSONAL_ACCOUNT"] = "PERSONAL_ACCOUNT";
})(DEPOSIT_ADDRESS_TYPE || (DEPOSIT_ADDRESS_TYPE = {}));
var DEPOSIT_ADDRESS_BRIDGE_ACCOUNT_TYPE;
(function (DEPOSIT_ADDRESS_BRIDGE_ACCOUNT_TYPE) {
    DEPOSIT_ADDRESS_BRIDGE_ACCOUNT_TYPE["CHECKING"] = "checking";
    DEPOSIT_ADDRESS_BRIDGE_ACCOUNT_TYPE["SAVINGS"] = "savings";
})(DEPOSIT_ADDRESS_BRIDGE_ACCOUNT_TYPE || (DEPOSIT_ADDRESS_BRIDGE_ACCOUNT_TYPE = {}));
var TransferRequestType;
(function (TransferRequestType) {
    TransferRequestType["DEPOSIT"] = "deposit";
    TransferRequestType["WITHDRAW"] = "withdraw";
})(TransferRequestType || (TransferRequestType = {}));
var TransferRequestStatus;
(function (TransferRequestStatus) {
    TransferRequestStatus["PENDING"] = "pending";
    TransferRequestStatus["PENDING_DOCUMENTATION"] = "pending_documentation";
    TransferRequestStatus["ACCEPTED"] = "accepted";
    TransferRequestStatus["REJECTED"] = "rejected";
})(TransferRequestStatus || (TransferRequestStatus = {}));
var TransferDocumentStatus;
(function (TransferDocumentStatus) {
    TransferDocumentStatus["ACCEPTED"] = "accepted";
    TransferDocumentStatus["REJECTED"] = "rejected";
})(TransferDocumentStatus || (TransferDocumentStatus = {}));
var TransferRequestBankMovementStatus;
(function (TransferRequestBankMovementStatus) {
    TransferRequestBankMovementStatus["MATCHED"] = "matched";
    TransferRequestBankMovementStatus["UNMATCHED"] = "unmatched";
})(TransferRequestBankMovementStatus || (TransferRequestBankMovementStatus = {}));

var COMERCIAL_RULE_TYPE;
(function (COMERCIAL_RULE_TYPE) {
    COMERCIAL_RULE_TYPE["DEPOSIT"] = "DEPOSIT";
    COMERCIAL_RULE_TYPE["QUANTITY"] = "QUANTITY";
})(COMERCIAL_RULE_TYPE || (COMERCIAL_RULE_TYPE = {}));
var CARD_STATUS;
(function (CARD_STATUS) {
    CARD_STATUS["PENDING"] = "pending";
    CARD_STATUS["CREATED"] = "created";
    CARD_STATUS["ACTIVE"] = "active";
    CARD_STATUS["EMBOSSED"] = "embossed";
    CARD_STATUS["BLOCKED"] = "blocked";
    CARD_STATUS["DISABLED"] = "disabled";
    CARD_STATUS["TRACKED"] = "tracked";
    CARD_STATUS["DELIVERY"] = "delivery";
    CARD_STATUS["DELIVERY_FAILED"] = "delivery_failed";
    CARD_STATUS["EXPIRED"] = "expired";
})(CARD_STATUS || (CARD_STATUS = {}));
var CARD_TYPE;
(function (CARD_TYPE) {
    CARD_TYPE["PHYSICAL"] = "physical";
    CARD_TYPE["VIRTUAL"] = "virtual";
    CARD_TYPE["EPHEMERAL"] = "ephemeral";
})(CARD_TYPE || (CARD_TYPE = {}));
var CARD_TYPE_ID;
(function (CARD_TYPE_ID) {
    CARD_TYPE_ID["PHYSICAL"] = "physical_cards";
    CARD_TYPE_ID["VIRTUAL"] = "virtual_cards";
    CARD_TYPE_ID["EPHEMERAL"] = "ephemeral_cards";
    CARD_TYPE_ID["PHYSICAL_PRISMA"] = "prisma_physical_cards";
    CARD_TYPE_ID["VIRTUAL_PRISMA"] = "prisma_virtual_cards";
    CARD_TYPE_ID["EPHEMERAL_PRISMA"] = "prisma_ephemeral_cards";
})(CARD_TYPE_ID || (CARD_TYPE_ID = {}));
var CARD_TYPE_STATUS;
(function (CARD_TYPE_STATUS) {
    CARD_TYPE_STATUS["ACTIVE"] = "active";
    CARD_TYPE_STATUS["ARCHIVED"] = "archived";
})(CARD_TYPE_STATUS || (CARD_TYPE_STATUS = {}));
var CARD_REASON;
(function (CARD_REASON) {
    CARD_REASON["CLIENT_INTERNAL_REASON"] = "CLIENT_INTERNAL_REASON";
    CARD_REASON["USER_INTERNAL_REASON"] = "USER_INTERNAL_REASON";
    CARD_REASON["FRAUDULENT"] = "FRAUDULENT";
    CARD_REASON["LOST"] = "LOST";
    CARD_REASON["STOLEN"] = "STOLEN";
    CARD_REASON["BROKEN"] = "BROKEN";
    CARD_REASON["UPGRADE"] = "UPGRADE";
})(CARD_REASON || (CARD_REASON = {}));
var CARD_TRANSACTION_TYPE;
(function (CARD_TRANSACTION_TYPE) {
    CARD_TRANSACTION_TYPE["PURCHASE"] = "PURCHASE";
    CARD_TRANSACTION_TYPE["WITHDRAWAL"] = "WITHDRAWAL";
    CARD_TRANSACTION_TYPE["EXTRACASH"] = "EXTRACASH";
    CARD_TRANSACTION_TYPE["REFUND"] = "REFUND";
    CARD_TRANSACTION_TYPE["PAYMENT"] = "PAYMENT";
    CARD_TRANSACTION_TYPE["REVERSAL_PURCHASE"] = "REVERSAL_PURCHASE";
    CARD_TRANSACTION_TYPE["REVERSAL_WITHDRAWAL"] = "REVERSAL_WITHDRAWAL";
    CARD_TRANSACTION_TYPE["REVERSAL_EXTRACASH"] = "REVERSAL_EXTRACASH";
    CARD_TRANSACTION_TYPE["REVERSAL_REFUND"] = "REVERSAL_REFUND";
    CARD_TRANSACTION_TYPE["REVERSAL_PAYMENT"] = "REVERSAL_PAYMENT";
    CARD_TRANSACTION_TYPE["REVERSAL"] = "REVERSAL";
    CARD_TRANSACTION_TYPE["VOID"] = "VOID";
})(CARD_TRANSACTION_TYPE || (CARD_TRANSACTION_TYPE = {}));
var CARD_TRANSACTION_STATUS;
(function (CARD_TRANSACTION_STATUS) {
    CARD_TRANSACTION_STATUS["APPROVED"] = "approved";
    CARD_TRANSACTION_STATUS["REJECTED"] = "rejected";
    CARD_TRANSACTION_STATUS["HELD"] = "held";
})(CARD_TRANSACTION_STATUS || (CARD_TRANSACTION_STATUS = {}));
var CARD_TRANSACTION_DETAIL_TYPE;
(function (CARD_TRANSACTION_DETAIL_TYPE) {
    CARD_TRANSACTION_DETAIL_TYPE["BASE"] = "BASE";
    CARD_TRANSACTION_DETAIL_TYPE["FEE"] = "FEE";
    CARD_TRANSACTION_DETAIL_TYPE["TAX"] = "TAX";
    CARD_TRANSACTION_DETAIL_TYPE["EXTRACASH"] = "EXTRACASH";
    CARD_TRANSACTION_DETAIL_TYPE["DISCOUNT"] = "DISCOUNT";
})(CARD_TRANSACTION_DETAIL_TYPE || (CARD_TRANSACTION_DETAIL_TYPE = {}));
var CARD_LIMIT_STATUS;
(function (CARD_LIMIT_STATUS) {
    CARD_LIMIT_STATUS["ACTIVE"] = "active";
    CARD_LIMIT_STATUS["ARCHIVED"] = "archived";
})(CARD_LIMIT_STATUS || (CARD_LIMIT_STATUS = {}));
var CARD_LIMIT_TYPE;
(function (CARD_LIMIT_TYPE) {
    CARD_LIMIT_TYPE["TOTAL"] = "total";
    CARD_LIMIT_TYPE["MONTHLY"] = "monthly";
})(CARD_LIMIT_TYPE || (CARD_LIMIT_TYPE = {}));
var CARD_SHIPMENT_STATUS;
(function (CARD_SHIPMENT_STATUS) {
    CARD_SHIPMENT_STATUS["CREATED"] = "CREATED";
    CARD_SHIPMENT_STATUS["REJECTED"] = "REJECTED";
    CARD_SHIPMENT_STATUS["PENDING"] = "PENDING";
    CARD_SHIPMENT_STATUS["TRACKED"] = "TRACKED";
    CARD_SHIPMENT_STATUS["IN_TRANSIT"] = "IN_TRANSIT";
    CARD_SHIPMENT_STATUS["START_OF_CUSTODY"] = "START_OF_CUSTODY";
    CARD_SHIPMENT_STATUS["END_OF_CUSTODY"] = "END_OF_CUSTODY";
    CARD_SHIPMENT_STATUS["DISTRIBUTION"] = "DISTRIBUTION";
    CARD_SHIPMENT_STATUS["NOT_DELIVERED"] = "NOT_DELIVERED";
    CARD_SHIPMENT_STATUS["DELIVERED"] = "DELIVERED";
    CARD_SHIPMENT_STATUS["DESTRUCTION"] = "DESTRUCTION";
    CARD_SHIPMENT_STATUS["ACCIDENT"] = "ACCIDENT";
})(CARD_SHIPMENT_STATUS || (CARD_SHIPMENT_STATUS = {}));
var CARD_SHIPMENT_STATUS_DETAIL;
(function (CARD_SHIPMENT_STATUS_DETAIL) {
    CARD_SHIPMENT_STATUS_DETAIL["CREATED"] = "CREATED";
    CARD_SHIPMENT_STATUS_DETAIL["REJECTED"] = "REJECTED";
    CARD_SHIPMENT_STATUS_DETAIL["PENDING"] = "PENDING";
    CARD_SHIPMENT_STATUS_DETAIL["TRACKED"] = "TRACKED";
    CARD_SHIPMENT_STATUS_DETAIL["SHIPMENT_CONSOLIDATED"] = "SHIPMENT_CONSOLIDATED";
    CARD_SHIPMENT_STATUS_DETAIL["RECEIVED_BY_WAREHOUSE"] = "RECEIVED_BY_WAREHOUSE";
    CARD_SHIPMENT_STATUS_DETAIL["RECEIVED_BY_CARRIER"] = "RECEIVED_BY_CARRIER";
    CARD_SHIPMENT_STATUS_DETAIL["LEFT_WAREHOUSE"] = "LEFT_WAREHOUSE";
    CARD_SHIPMENT_STATUS_DETAIL["IN_TRANSIT"] = "IN_TRANSIT";
    CARD_SHIPMENT_STATUS_DETAIL["FAILED_DELIVERY_ATTEMPT"] = "FAILED_DELIVERY_ATTEMPT";
    CARD_SHIPMENT_STATUS_DETAIL["INCOMPLETE_ADDRESS"] = "INCOMPLETE_ADDRESS";
    CARD_SHIPMENT_STATUS_DETAIL["WRONG_ADDRESS"] = "WRONG_ADDRESS";
    CARD_SHIPMENT_STATUS_DETAIL["MISSING_SIGNATURE"] = "MISSING_SIGNATURE";
    CARD_SHIPMENT_STATUS_DETAIL["MISSING_DOCUMENTS"] = "MISSING_DOCUMENTS";
    CARD_SHIPMENT_STATUS_DETAIL["CARRIER_CANCELLED_ACCIDENT"] = "CARRIER_CANCELLED_ACCIDENT";
    CARD_SHIPMENT_STATUS_DETAIL["CARRIER_CANCELLED_HOLIDAY"] = "CARRIER_CANCELLED_HOLIDAY";
    CARD_SHIPMENT_STATUS_DETAIL["CARRIER_CANCELLED"] = "CARRIER_CANCELLED";
    CARD_SHIPMENT_STATUS_DETAIL["CARRIER_CANCELLED_VOLUME"] = "CARRIER_CANCELLED_VOLUME";
    CARD_SHIPMENT_STATUS_DETAIL["CARRIER_CANCELLED_VEHICLE"] = "CARRIER_CANCELLED_VEHICLE";
    CARD_SHIPMENT_STATUS_DETAIL["CARRIER_CANCELLED_ZONE"] = "CARRIER_CANCELLED_ZONE";
    CARD_SHIPMENT_STATUS_DETAIL["ACCIDENT_RECOVERY"] = "ACCIDENT_RECOVERY";
    CARD_SHIPMENT_STATUS_DETAIL["FAILED_DELIVERY_ATTEMPT_LOST"] = "FAILED_DELIVERY_ATTEMPT_LOST";
    CARD_SHIPMENT_STATUS_DETAIL["FAILED_DELIVERY_ATTEMPT_DAMAGED_GOODS"] = "FAILED_DELIVERY_ATTEMPT_DAMAGED_GOODS";
    CARD_SHIPMENT_STATUS_DETAIL["FAILED_DELIVERY_ATTEMPT_STOLEN"] = "FAILED_DELIVERY_ATTEMPT_STOLEN";
    CARD_SHIPMENT_STATUS_DETAIL["RESEND_SHIPMENT"] = "RESEND_SHIPMENT";
    CARD_SHIPMENT_STATUS_DETAIL["START_OF_CUSTODY"] = "START_OF_CUSTODY";
    CARD_SHIPMENT_STATUS_DETAIL["END_OF_CUSTODY"] = "END_OF_CUSTODY";
    CARD_SHIPMENT_STATUS_DETAIL["DISTRIBUTION"] = "DISTRIBUTION";
    CARD_SHIPMENT_STATUS_DETAIL["NOT_DELIVERED"] = "NOT_DELIVERED";
    CARD_SHIPMENT_STATUS_DETAIL["NOT_DELIVERED_INCOMPLETE_ADDRESS"] = "NOT_DELIVERED_INCOMPLETE_ADDRESS";
    CARD_SHIPMENT_STATUS_DETAIL["NOT_DELIVERED_WRONG_ADDRESS"] = "NOT_DELIVERED_WRONG_ADDRESS";
    CARD_SHIPMENT_STATUS_DETAIL["NOT_DELIVERED_ACCIDENT_CLOSED"] = "NOT_DELIVERED_ACCIDENT_CLOSED";
    CARD_SHIPMENT_STATUS_DETAIL["DELIVERED"] = "DELIVERED";
    CARD_SHIPMENT_STATUS_DETAIL["DESTRUCTION"] = "DESTRUCTION";
    CARD_SHIPMENT_STATUS_DETAIL["ACCIDENT"] = "ACCIDENT";
    CARD_SHIPMENT_STATUS_DETAIL["LOST"] = "LOST";
    CARD_SHIPMENT_STATUS_DETAIL["DAMAGED_GOODS"] = "DAMAGED_GOODS";
    CARD_SHIPMENT_STATUS_DETAIL["STOLEN"] = "STOLEN";
})(CARD_SHIPMENT_STATUS_DETAIL || (CARD_SHIPMENT_STATUS_DETAIL = {}));
var CARD_AGENT;
(function (CARD_AGENT) {
    CARD_AGENT["POMELO"] = "Pomelo";
    CARD_AGENT["PRISMA"] = "Prisma";
})(CARD_AGENT || (CARD_AGENT = {}));

var PRECOGS_USER_TAGS;
(function (PRECOGS_USER_TAGS) {
    PRECOGS_USER_TAGS["TNC"] = "tnc";
    PRECOGS_USER_TAGS["HDYHAU"] = "hdyhau";
    PRECOGS_USER_TAGS["PAYMENT_CARD"] = "payment_card";
    PRECOGS_USER_TAGS["PAYMENT_CARD_NO_VALIDATION"] = "payment_card_no_validation";
    PRECOGS_USER_TAGS["ONBOARDING_WELCOME_SEPT_22"] = "onboarding_welcome_sept_22";
    PRECOGS_USER_TAGS["BROKER"] = "broker";
    PRECOGS_USER_TAGS["DOLLARIZE"] = "dollarize";
    PRECOGS_USER_TAGS["ONBOARDING_DOLLARIZE"] = "onboarding_dollarize";
    PRECOGS_USER_TAGS["PJ_CVU"] = "pj-cvu";
    PRECOGS_USER_TAGS["CASHBACK_MENU"] = "cashback_menu";
    PRECOGS_USER_TAGS["CONVERSION"] = "conversion";
    PRECOGS_USER_TAGS["REVIEW_APP"] = "review_app";
    PRECOGS_USER_TAGS["NEW_HOME"] = "new_home";
    PRECOGS_USER_TAGS["PAYMENT_SERVICE"] = "show_payment_service";
    PRECOGS_USER_TAGS["DEACTIVATED_BIOMETRIC"] = "deactivated_biometric";
    PRECOGS_USER_TAGS["CASHBACK_LEVEL_PLUS"] = "nivel-plus";
    PRECOGS_USER_TAGS["CASHBACK_LEVEL_MAX"] = "nivel-max";
    PRECOGS_USER_TAGS["SHOW_CASHBACK_ON_PAYMENT_SERVICE"] = "show_cashback_menu_on_payment_service";
    PRECOGS_USER_TAGS["PAYMENT_SERVICE_TYC"] = "have_seen_payment_service_tyc";
    PRECOGS_USER_TAGS["PAYMENT_CARD_TRANSITION_ONBOARDING"] = "payment_card_transition_onboarding";
    PRECOGS_USER_TAGS["MIDOLAR"] = "midolar";
    PRECOGS_USER_TAGS["DOLARYA"] = "dolarya";
    PRECOGS_USER_TAGS["UNDOLAR"] = "undolar";
    PRECOGS_USER_TAGS["DOLARES"] = "dolares";
    PRECOGS_USER_TAGS["DOLAR_DEFAULT"] = "dolar-default";
    PRECOGS_USER_TAGS["CAN_SEE_FCI"] = "fci-ff";
    PRECOGS_USER_TAGS["CAN_SEE_CASHBACK_CARD_MENU"] = "can_see_cashback_card_menu";
    PRECOGS_USER_TAGS["CAN_SEE_PAYMENT_CARD_TRANSITION"] = "can_see_payment_card_transition";
    PRECOGS_USER_TAGS["HAVE_SEEN_DOLLARIZE_DISCLAIMER"] = "have_seen_dollarize_disclaimer";
    PRECOGS_USER_TAGS["HAVE_SEEN_ONBOARDING_ASESOR"] = "have_seen_onboarding_asesor";
    PRECOGS_USER_TAGS["ASESOR_NACHO"] = "asesor_nacho";
    PRECOGS_USER_TAGS["ASESOR_AGUSTINA"] = "asesor_agustina";
    PRECOGS_USER_TAGS["ASESOR_FELIPE"] = "asesor_felipe";
    PRECOGS_USER_TAGS["SHOW_OLD_PAYMENT_CARDS_DESIGN"] = "show_old_payment_cards_design";
    PRECOGS_USER_TAGS["HOME_PAGE_TOUR"] = "home-page-tour";
    PRECOGS_USER_TAGS["WITHDRAWS_WITH_NEW_CHALLENGES"] = "withdraws_with_new_challenges";
    PRECOGS_USER_TAGS["LOCAL_MARKET"] = "local_market";
    PRECOGS_USER_TAGS["ARBITRAJISTA"] = "arbitrajista";
    PRECOGS_USER_TAGS["CAN_SEE_HOME_NEWS_BUTTON"] = "can_see_home_news_button";
    PRECOGS_USER_TAGS["HAVE_SEEN_MARKETS_NEWS_HOME_BUTTON"] = "have_seen_markets_news_home_button";
})(PRECOGS_USER_TAGS || (PRECOGS_USER_TAGS = {}));
var PRECOGS_USER_ROLES;
(function (PRECOGS_USER_ROLES) {
    PRECOGS_USER_ROLES["MEMBER"] = "member";
    PRECOGS_USER_ROLES["ADMIN"] = "admin";
    PRECOGS_USER_ROLES["SUPERADMIN"] = "superadmin";
    PRECOGS_USER_ROLES["TRADER"] = "trader";
    PRECOGS_USER_ROLES["PARTNER"] = "partner";
})(PRECOGS_USER_ROLES || (PRECOGS_USER_ROLES = {}));
var PRECOGS_USER_REFERRER_TYPE;
(function (PRECOGS_USER_REFERRER_TYPE) {
    PRECOGS_USER_REFERRER_TYPE["USER_REFERRER"] = "USER_REFERRER";
    PRECOGS_USER_REFERRER_TYPE["PROMOTION_CODE"] = "PROMOTION_CODE";
})(PRECOGS_USER_REFERRER_TYPE || (PRECOGS_USER_REFERRER_TYPE = {}));
var PRECOGS_FLAG_FORM_TYPE;
(function (PRECOGS_FLAG_FORM_TYPE) {
    PRECOGS_FLAG_FORM_TYPE["RADIO_GROUP"] = "RADIO_GROUP";
    PRECOGS_FLAG_FORM_TYPE["INPUT"] = "INPUT";
    PRECOGS_FLAG_FORM_TYPE["CHECKBOX_GROUP"] = "CHECKBOX_GROUP";
    PRECOGS_FLAG_FORM_TYPE["OPTION_SELECTOR"] = "OPTION_SELECTOR";
    PRECOGS_FLAG_FORM_TYPE["TOGGLE"] = "TOGGLE";
    PRECOGS_FLAG_FORM_TYPE["DATE_PICKER"] = "DATE_PICKER";
})(PRECOGS_FLAG_FORM_TYPE || (PRECOGS_FLAG_FORM_TYPE = {}));
var PRECOGS_FLAG_TYPE;
(function (PRECOGS_FLAG_TYPE) {
    PRECOGS_FLAG_TYPE["ONE_TIME_VIEW"] = "one_time_view";
    PRECOGS_FLAG_TYPE["TIME_SEPARATED"] = "time_separated";
    PRECOGS_FLAG_TYPE["LOGIN_SEPARATED"] = "login_separated";
})(PRECOGS_FLAG_TYPE || (PRECOGS_FLAG_TYPE = {}));
var FLAG_FORM_INPUT_TYPE;
(function (FLAG_FORM_INPUT_TYPE) {
    FLAG_FORM_INPUT_TYPE["string"] = "string";
    FLAG_FORM_INPUT_TYPE["number"] = "number";
})(FLAG_FORM_INPUT_TYPE || (FLAG_FORM_INPUT_TYPE = {}));
var PRECOGS_CONFIG_ASSETS_CATEGORIES_VIEW;
(function (PRECOGS_CONFIG_ASSETS_CATEGORIES_VIEW) {
    PRECOGS_CONFIG_ASSETS_CATEGORIES_VIEW["HOME"] = "home";
    PRECOGS_CONFIG_ASSETS_CATEGORIES_VIEW["MARKET"] = "market";
})(PRECOGS_CONFIG_ASSETS_CATEGORIES_VIEW || (PRECOGS_CONFIG_ASSETS_CATEGORIES_VIEW = {}));
var PRECOGS_CONFIG_ASSETS_CATEGORIES_TYPE;
(function (PRECOGS_CONFIG_ASSETS_CATEGORIES_TYPE) {
    PRECOGS_CONFIG_ASSETS_CATEGORIES_TYPE["SECURITY"] = "security";
    PRECOGS_CONFIG_ASSETS_CATEGORIES_TYPE["CURRENCY"] = "currency";
})(PRECOGS_CONFIG_ASSETS_CATEGORIES_TYPE || (PRECOGS_CONFIG_ASSETS_CATEGORIES_TYPE = {}));

var ADJUSTMENT_STATUS;
(function (ADJUSTMENT_STATUS) {
    ADJUSTMENT_STATUS["PENDING"] = "pending";
    ADJUSTMENT_STATUS["AUTHORIZED"] = "authorized";
    ADJUSTMENT_STATUS["REJECTED"] = "rejected";
    ADJUSTMENT_STATUS["SYNC_FAILED"] = "sync_failed";
})(ADJUSTMENT_STATUS || (ADJUSTMENT_STATUS = {}));
var ADJUSTMENT_SUBCATEGORY_TYPE;
(function (ADJUSTMENT_SUBCATEGORY_TYPE) {
    ADJUSTMENT_SUBCATEGORY_TYPE["CREDIT"] = "credit";
    ADJUSTMENT_SUBCATEGORY_TYPE["DEBIT"] = "debit";
})(ADJUSTMENT_SUBCATEGORY_TYPE || (ADJUSTMENT_SUBCATEGORY_TYPE = {}));
var ADJUSTMENT_SUBCATEGORY_NAME;
(function (ADJUSTMENT_SUBCATEGORY_NAME) {
    ADJUSTMENT_SUBCATEGORY_NAME["SALARY"] = "salary";
    ADJUSTMENT_SUBCATEGORY_NAME["BONUS"] = "bonus";
    ADJUSTMENT_SUBCATEGORY_NAME["DEPOSIT_DESK"] = "deposit_desk";
    ADJUSTMENT_SUBCATEGORY_NAME["WITHDRAW_DESK"] = "withdraw_desk";
    ADJUSTMENT_SUBCATEGORY_NAME["FEE"] = "fee";
    ADJUSTMENT_SUBCATEGORY_NAME["ADJUSTMENT"] = "adjustment";
    ADJUSTMENT_SUBCATEGORY_NAME["AIRDROP"] = "airdrop";
    ADJUSTMENT_SUBCATEGORY_NAME["MIGRATION"] = "migration";
    ADJUSTMENT_SUBCATEGORY_NAME["SERVICE_COST"] = "service_cost";
    ADJUSTMENT_SUBCATEGORY_NAME["GIFT"] = "gift";
    ADJUSTMENT_SUBCATEGORY_NAME["CASHBACK"] = "cashback";
    ADJUSTMENT_SUBCATEGORY_NAME["PROMOTION"] = "promotion";
    ADJUSTMENT_SUBCATEGORY_NAME["COMPENSATION"] = "compensation";
})(ADJUSTMENT_SUBCATEGORY_NAME || (ADJUSTMENT_SUBCATEGORY_NAME = {}));

var INVESTMENT_STATE;
(function (INVESTMENT_STATE) {
    INVESTMENT_STATE["ACTIVE"] = "active";
    INVESTMENT_STATE["INACTIVE"] = "inactive";
    INVESTMENT_STATE["PENDING"] = "pending";
})(INVESTMENT_STATE || (INVESTMENT_STATE = {}));

var REWARDS_CAMPAINGS;
(function (REWARDS_CAMPAINGS) {
    REWARDS_CAMPAINGS["REFERRALS"] = "Referido";
    REWARDS_CAMPAINGS["CASHBACK"] = "Cashback";
    REWARDS_CAMPAINGS["CASHBACK_CARD"] = "Cashback Crypto";
    REWARDS_CAMPAINGS["CASHBACK_PAYMENT_SERVICE"] = "Cashback Pago de Servicios";
    REWARDS_CAMPAINGS["CASHBACK_PAYMENT_CARD_02_2024"] = "Cashback Tarjeta Febrero 2024";
    REWARDS_CAMPAINGS["CASHBACK_REFERRALS_MAY"] = "referidos_tarjetas_mayo";
    REWARDS_CAMPAINGS["CASHBACK_MERK_2025"] = "cashback-merk-2025";
})(REWARDS_CAMPAINGS || (REWARDS_CAMPAINGS = {}));
var PAYMENT_CONDITIONS_OPTIONS;
(function (PAYMENT_CONDITIONS_OPTIONS) {
    PAYMENT_CONDITIONS_OPTIONS["PER_ACTION"] = "per_action";
    PAYMENT_CONDITIONS_OPTIONS["ALL_ACTIONS"] = "all_actions";
})(PAYMENT_CONDITIONS_OPTIONS || (PAYMENT_CONDITIONS_OPTIONS = {}));
var CAMPAIGN_STATUS_OPTIONS;
(function (CAMPAIGN_STATUS_OPTIONS) {
    CAMPAIGN_STATUS_OPTIONS["ACTIVE"] = "active";
    CAMPAIGN_STATUS_OPTIONS["DISABLED"] = "disabled";
})(CAMPAIGN_STATUS_OPTIONS || (CAMPAIGN_STATUS_OPTIONS = {}));
var REWARD_PAYMENT_STATUS_OPTIONS;
(function (REWARD_PAYMENT_STATUS_OPTIONS) {
    REWARD_PAYMENT_STATUS_OPTIONS["PENDING"] = "pending";
    REWARD_PAYMENT_STATUS_OPTIONS["COMPLETED"] = "completed";
    REWARD_PAYMENT_STATUS_OPTIONS["SYNC_FAILED"] = "sync_failed";
    REWARD_PAYMENT_STATUS_OPTIONS["ADMIN_REQUIRED"] = "admin_required";
    REWARD_PAYMENT_STATUS_OPTIONS["CANCELED"] = "canceled";
    REWARD_PAYMENT_STATUS_OPTIONS["REVOKED"] = "revoked";
})(REWARD_PAYMENT_STATUS_OPTIONS || (REWARD_PAYMENT_STATUS_OPTIONS = {}));

var Platform;
(function (Platform) {
    Platform["ANDROID"] = "android";
    Platform["IOS"] = "ios";
    Platform["BROWSER"] = "browser";
})(Platform || (Platform = {}));
var LoginMethods;
(function (LoginMethods) {
    LoginMethods["WEBAUTHN"] = "webauthn";
    LoginMethods["PASSWORD"] = "password";
    LoginMethods["OIDC"] = "oidc";
    LoginMethods["OTP"] = "otp";
    LoginMethods["INVITE"] = "invite";
})(LoginMethods || (LoginMethods = {}));
var FlowTypes;
(function (FlowTypes) {
    FlowTypes["API"] = "api";
    FlowTypes["BROWSER"] = "browser";
})(FlowTypes || (FlowTypes = {}));
var AuthorizationStatus;
(function (AuthorizationStatus) {
    AuthorizationStatus["PENDING"] = "pending";
    AuthorizationStatus["ACCEPTED"] = "accepted";
    AuthorizationStatus["REJECTED"] = "rejected";
    AuthorizationStatus["EXPIRED"] = "expired";
})(AuthorizationStatus || (AuthorizationStatus = {}));
var AuthorizationAction;
(function (AuthorizationAction) {
    AuthorizationAction["ACCEPT"] = "accept";
    AuthorizationAction["REJECT"] = "reject";
})(AuthorizationAction || (AuthorizationAction = {}));
var AuthorizationType;
(function (AuthorizationType) {
    AuthorizationType["SESSION"] = "session_authorization";
    AuthorizationType["WITHDRAW"] = "withdraw_authorization";
})(AuthorizationType || (AuthorizationType = {}));
var LoginSteps;
(function (LoginSteps) {
    LoginSteps["SESSION_AUTHORIZATION"] = "session_authorization";
    LoginSteps["OTP_REQUIRED"] = "otp_required";
    LoginSteps["DEVICE_ENROLLMENT_REQUIRED"] = "device_enrollment_required";
})(LoginSteps || (LoginSteps = {}));
var PERMISSION_STATE;
(function (PERMISSION_STATE) {
    PERMISSION_STATE["VIEWER"] = "viewer";
    PERMISSION_STATE["ADMIN"] = "admin";
    PERMISSION_STATE["OWNER"] = "owner";
    PERMISSION_STATE["REVOKED"] = "revoked";
})(PERMISSION_STATE || (PERMISSION_STATE = {}));
var ACCOUNT_INVITE_STATUS;
(function (ACCOUNT_INVITE_STATUS) {
    ACCOUNT_INVITE_STATUS["SENT"] = "sent";
    ACCOUNT_INVITE_STATUS["ACCEPTED"] = "accepted";
    ACCOUNT_INVITE_STATUS["REJECTED"] = "rejected";
    ACCOUNT_INVITE_STATUS["CONFIRMED"] = "confirmed";
})(ACCOUNT_INVITE_STATUS || (ACCOUNT_INVITE_STATUS = {}));
var CHALLENGE_METHODS;
(function (CHALLENGE_METHODS) {
    CHALLENGE_METHODS["OUT_OF_BAND"] = "out_of_band";
    CHALLENGE_METHODS["WEBAUTHN"] = "webauthn";
    CHALLENGE_METHODS["OTP"] = "otp";
})(CHALLENGE_METHODS || (CHALLENGE_METHODS = {}));
var CHALLENGE_STATUS;
(function (CHALLENGE_STATUS) {
    CHALLENGE_STATUS["PENDING"] = "pending";
    CHALLENGE_STATUS["ACCEPTED"] = "accepted";
    CHALLENGE_STATUS["FAILED"] = "failed";
})(CHALLENGE_STATUS || (CHALLENGE_STATUS = {}));

var RULE_EVENT_STATUS;
(function (RULE_EVENT_STATUS) {
    RULE_EVENT_STATUS["DISABLED"] = "disabled";
    RULE_EVENT_STATUS["ACTIVE"] = "active";
})(RULE_EVENT_STATUS || (RULE_EVENT_STATUS = {}));
var RULE_STATUS;
(function (RULE_STATUS) {
    RULE_STATUS["DISABLED"] = "disabled";
    RULE_STATUS["ACTIVE"] = "active";
})(RULE_STATUS || (RULE_STATUS = {}));
var RULE_TARJET_STATUS;
(function (RULE_TARJET_STATUS) {
    RULE_TARJET_STATUS["DISABLED"] = "disabled";
    RULE_TARJET_STATUS["ACTIVE"] = "active";
})(RULE_TARJET_STATUS || (RULE_TARJET_STATUS = {}));
var RULE_EXECUTION_STATUS;
(function (RULE_EXECUTION_STATUS) {
    RULE_EXECUTION_STATUS["SUBMITTED"] = "submitted";
    RULE_EXECUTION_STATUS["SUCCEED"] = "succeed";
    RULE_EXECUTION_STATUS["FAILED"] = "failed";
})(RULE_EXECUTION_STATUS || (RULE_EXECUTION_STATUS = {}));
var RULE_EVENT_ID;
(function (RULE_EVENT_ID) {
    RULE_EVENT_ID["DEPOSITS"] = "payments_system.deposit.created";
    RULE_EVENT_ID["CASHBACK"] = "rewards_system.payment.created";
    RULE_EVENT_ID["INVEST"] = "interest_system.accounting_record.created";
})(RULE_EVENT_ID || (RULE_EVENT_ID = {}));

var events = {};

(function (exports) {

const ADJUSTMENTS_SERVICE_NAME = "caronte";
const ADJUSTMENTS_ADJUSTMENT_TYPE = `${ADJUSTMENTS_SERVICE_NAME}.adjustments`;
exports.AdjustmentsStateStates = void 0;
(function (AdjustmentsStateStates) {
    AdjustmentsStateStates["PENDING"] = "pending";
    AdjustmentsStateStates["AUTHORIZED"] = "authorized";
    AdjustmentsStateStates["REJECTED"] = "rejected";
    AdjustmentsStateStates["SUCCEED"] = "sync_failed";
})(exports.AdjustmentsStateStates || (exports.AdjustmentsStateStates = {}));

const BROKER_SERVICE_NAME = "broker";
const BROKER_SWAP_TYPE = `${BROKER_SERVICE_NAME}.swaps`;
const BROKER_AUTOSWAP_ALCHEMIST_TYPE = `${BROKER_SERVICE_NAME}.autoswaps.alchemist`;
const BROKER_AUTOSWAP_PAYMENTS_TYPE = `${BROKER_SERVICE_NAME}.autoswaps.payments`;
const BROKER_AUTOSWAP_KAIBACORP_TYPE = `${BROKER_SERVICE_NAME}.autoswaps.kaibacorp`;
const BROKER_DOLLARIZE_TYPE = `${BROKER_SERVICE_NAME}.dollarize`;
exports.BrokerSwapStates = void 0;
(function (BrokerSwapStates) {
    BrokerSwapStates["PENDING"] = "pending";
    BrokerSwapStates["PROCESSING"] = "processing";
    BrokerSwapStates["RELEASE_FUNDS"] = "release-funds";
    BrokerSwapStates["FAILED_TO_RELEASE_FUNDS"] = "failed-to-release-funds";
    BrokerSwapStates["REJECTED"] = "rejected";
    BrokerSwapStates["SUCCEED"] = "succeed";
    BrokerSwapStates["ACCEPTED"] = "accepted";
    BrokerSwapStates["FAILED_TO_EXECUTE"] = "failed-to-execute";
})(exports.BrokerSwapStates || (exports.BrokerSwapStates = {}));

const INTERESTS_SERVICE_NAME = "interests";
const INTERESTS_ACCOUNTING_RECORD_TYPE = `${INTERESTS_SERVICE_NAME}.accounting_record`;
exports.InterestsAccountingRecordStates = void 0;
(function (InterestsAccountingRecordStates) {
    InterestsAccountingRecordStates["PENDING"] = "pending";
    InterestsAccountingRecordStates["PROCESSED"] = "processed";
    InterestsAccountingRecordStates["SUCCEEDED"] = "succeeded";
    InterestsAccountingRecordStates["BELOW_MIN"] = "below_min";
})(exports.InterestsAccountingRecordStates || (exports.InterestsAccountingRecordStates = {}));

const KAIBACORP_SERVICE_NAME = "kaibacorp";
const KAIBA_TRANSACTION_TYPE = `${KAIBACORP_SERVICE_NAME}.transaction`;
exports.KaibaTransactionStates = void 0;
(function (KaibaTransactionStates) {
    KaibaTransactionStates["HELD"] = "held";
    KaibaTransactionStates["APPROVED"] = "approved";
    KaibaTransactionStates["REJECT"] = "rejected";
    KaibaTransactionStates["SYNC_FAIL"] = "sync_failed";
})(exports.KaibaTransactionStates || (exports.KaibaTransactionStates = {}));

const PAYMENTS_SERVICE_NAME = "payments";
const PAYMENTS_DEPOSIT_TYPE = `${PAYMENTS_SERVICE_NAME}.deposit`;
const PAYMENTS_WITHDRAW_TYPE = `${PAYMENTS_SERVICE_NAME}.withdraw`;
const PAYMENTS_WITHHOLDING_TYPE = `${PAYMENTS_SERVICE_NAME}.withholding`;
exports.PaymentsWithdrawStates = void 0;
(function (PaymentsWithdrawStates) {
    PaymentsWithdrawStates["PREPARED"] = "prepared";
    PaymentsWithdrawStates["SUBMITTED"] = "submitted";
    PaymentsWithdrawStates["PROCESSING"] = "processing";
    PaymentsWithdrawStates["SUCCEED"] = "succeed";
    PaymentsWithdrawStates["FAILED"] = "failed";
    PaymentsWithdrawStates["CONFIRMING"] = "confirming";
    PaymentsWithdrawStates["ERRORED"] = "errored";
    PaymentsWithdrawStates["SYNC_FAILED"] = "sync_failed";
})(exports.PaymentsWithdrawStates || (exports.PaymentsWithdrawStates = {}));
exports.PaymentsDepositStates = void 0;
(function (PaymentsDepositStates) {
    PaymentsDepositStates["SUBMITTED"] = "submitted";
    PaymentsDepositStates["BELOW_MIN"] = "below_min";
    PaymentsDepositStates["BENEFICIARY_BLOCKED"] = "beneficiary_blocked";
    PaymentsDepositStates["BLOCKED"] = "blocked";
    PaymentsDepositStates["ACCEPTED"] = "accepted";
    PaymentsDepositStates["REJECTED"] = "rejected";
    PaymentsDepositStates["SYNC_FAILED"] = "sync_failed";
    PaymentsDepositStates["NOT_ALLOWED"] = "not_allowed";
})(exports.PaymentsDepositStates || (exports.PaymentsDepositStates = {}));

const PEATIO_SERVICE_NAME = "peatio";
const PEATIO_ORDER_TYPE = `${PEATIO_SERVICE_NAME}.order`;
exports.PeatioOrderStates = void 0;
(function (PeatioOrderStates) {
    PeatioOrderStates["CANCELED"] = "canceled";
    PeatioOrderStates["COMPLETED"] = "completed";
    PeatioOrderStates["OPEN"] = "open";
    PeatioOrderStates["ERROR"] = "error";
})(exports.PeatioOrderStates || (exports.PeatioOrderStates = {}));

const REFERRAL_PROGRAM_SERVICE_NAME = "referral_program";
const REFERRAL_PROGRAM_PAYOUT_TYPE = `${REFERRAL_PROGRAM_SERVICE_NAME}.payout`;
exports.ReferralProgramPayoutStates = void 0;
(function (ReferralProgramPayoutStates) {
    ReferralProgramPayoutStates["SUBMITTED"] = "submitted";
    ReferralProgramPayoutStates["SUCCEEDED"] = "succeeded";
    ReferralProgramPayoutStates["REJECTED"] = "rejected";
})(exports.ReferralProgramPayoutStates || (exports.ReferralProgramPayoutStates = {}));

const REWARDS_SERVICE_NAME = "rewards";
const REWARDS_PAYMENTS_TYPE = `${REWARDS_SERVICE_NAME}.payment`;
exports.RewardsPaymentsStates = void 0;
(function (RewardsPaymentsStates) {
    RewardsPaymentsStates["PENDING"] = "pending";
    RewardsPaymentsStates["COMPLETED"] = "completed";
    RewardsPaymentsStates["SYNC_FAILED"] = "sync_failed";
    RewardsPaymentsStates["ADMIN_REQUIRED"] = "admin_required";
    RewardsPaymentsStates["REVOKED"] = "revoked";
    RewardsPaymentsStates["CANCELED"] = "canceled";
})(exports.RewardsPaymentsStates || (exports.RewardsPaymentsStates = {}));
exports.RewardsProviders = void 0;
(function (RewardsProviders) {
    RewardsProviders["PEATIO"] = "peatio";
    RewardsProviders["MERK"] = "merk";
})(exports.RewardsProviders || (exports.RewardsProviders = {}));

const ALCHEMIST_SERVICE_NAME = "alchemist";
const ALCHEMIST_RULE_EXECUTION_TYPE = `${ALCHEMIST_SERVICE_NAME}.rule_execution`;
exports.AlchemistRuleExecutionStates = void 0;
(function (AlchemistRuleExecutionStates) {
    AlchemistRuleExecutionStates["SUBMITTED"] = "submitted";
    AlchemistRuleExecutionStates["FAILED"] = "failed";
    AlchemistRuleExecutionStates["SUCCEED"] = "succeed";
})(exports.AlchemistRuleExecutionStates || (exports.AlchemistRuleExecutionStates = {}));

const SR_BARRIGA_SERVICE_NAME = "srbarriga";
const SR_BARRIGA_PAYMENT_TYPE = `${SR_BARRIGA_SERVICE_NAME}.payment`;
exports.SrBarrigaPaymentStates = void 0;
(function (SrBarrigaPaymentStates) {
    SrBarrigaPaymentStates["SUCCEED"] = "succeed";
    SrBarrigaPaymentStates["FAILED"] = "failed";
})(exports.SrBarrigaPaymentStates || (exports.SrBarrigaPaymentStates = {}));

const WITHHOLDINGS_SERVICE_NAME = "withholdings";
const WITHHOLDINGS_WITHHOLDINGS_TYPE = `${WITHHOLDINGS_SERVICE_NAME}.withholdings`;
exports.WITHHOLDINGS_WITHHOLDING_STATUS = void 0;
(function (WITHHOLDINGS_WITHHOLDING_STATUS) {
    WITHHOLDINGS_WITHHOLDING_STATUS["PAID"] = "paid";
    WITHHOLDINGS_WITHHOLDING_STATUS["UNPAID"] = "unpaid";
    WITHHOLDINGS_WITHHOLDING_STATUS["SYNC_FAILED"] = "sync_failed";
    WITHHOLDINGS_WITHHOLDING_STATUS["EXEMPT"] = "exempt";
})(exports.WITHHOLDINGS_WITHHOLDING_STATUS || (exports.WITHHOLDINGS_WITHHOLDING_STATUS = {}));

const MERK_SERVICE_NAME = "merk";
const MERK_ACCOUNT_MOVEMENT_RECORD_TYPE = `${MERK_SERVICE_NAME}.account_movement`;
const MERK_DEBIT_ACCOUNT_MOVEMENT_RECORD_TYPE = `${MERK_SERVICE_NAME}.account_movement.debit`;
const MERK_CREDIT_ACCOUNT_MOVEMENT_RECORD_TYPE = `${MERK_SERVICE_NAME}.account_movement.credit`;
const MERK_ORDER_TYPE = `${MERK_SERVICE_NAME}.order`;
exports.MerkAccountMovementType = void 0;
(function (MerkAccountMovementType) {
    MerkAccountMovementType["Debit"] = "debit";
    MerkAccountMovementType["Credit"] = "credit";
})(exports.MerkAccountMovementType || (exports.MerkAccountMovementType = {}));
exports.MerkApiProvider = void 0;
(function (MerkApiProvider) {
    MerkApiProvider["ALPACA"] = "alpaca";
    MerkApiProvider["ESCO"] = "esco";
})(exports.MerkApiProvider || (exports.MerkApiProvider = {}));

exports.ADJUSTMENTS_ADJUSTMENT_TYPE = ADJUSTMENTS_ADJUSTMENT_TYPE;
exports.ADJUSTMENTS_SERVICE_NAME = ADJUSTMENTS_SERVICE_NAME;
exports.ALCHEMIST_RULE_EXECUTION_TYPE = ALCHEMIST_RULE_EXECUTION_TYPE;
exports.ALCHEMIST_SERVICE_NAME = ALCHEMIST_SERVICE_NAME;
exports.BROKER_AUTOSWAP_ALCHEMIST_TYPE = BROKER_AUTOSWAP_ALCHEMIST_TYPE;
exports.BROKER_AUTOSWAP_KAIBACORP_TYPE = BROKER_AUTOSWAP_KAIBACORP_TYPE;
exports.BROKER_AUTOSWAP_PAYMENTS_TYPE = BROKER_AUTOSWAP_PAYMENTS_TYPE;
exports.BROKER_DOLLARIZE_TYPE = BROKER_DOLLARIZE_TYPE;
exports.BROKER_SERVICE_NAME = BROKER_SERVICE_NAME;
exports.BROKER_SWAP_TYPE = BROKER_SWAP_TYPE;
exports.INTERESTS_ACCOUNTING_RECORD_TYPE = INTERESTS_ACCOUNTING_RECORD_TYPE;
exports.INTERESTS_SERVICE_NAME = INTERESTS_SERVICE_NAME;
exports.KAIBACORP_SERVICE_NAME = KAIBACORP_SERVICE_NAME;
exports.KAIBA_TRANSACTION_TYPE = KAIBA_TRANSACTION_TYPE;
exports.MERK_ACCOUNT_MOVEMENT_RECORD_TYPE = MERK_ACCOUNT_MOVEMENT_RECORD_TYPE;
exports.MERK_CREDIT_ACCOUNT_MOVEMENT_RECORD_TYPE = MERK_CREDIT_ACCOUNT_MOVEMENT_RECORD_TYPE;
exports.MERK_DEBIT_ACCOUNT_MOVEMENT_RECORD_TYPE = MERK_DEBIT_ACCOUNT_MOVEMENT_RECORD_TYPE;
exports.MERK_ORDER_TYPE = MERK_ORDER_TYPE;
exports.MERK_SERVICE_NAME = MERK_SERVICE_NAME;
exports.PAYMENTS_DEPOSIT_TYPE = PAYMENTS_DEPOSIT_TYPE;
exports.PAYMENTS_SERVICE_NAME = PAYMENTS_SERVICE_NAME;
exports.PAYMENTS_WITHDRAW_TYPE = PAYMENTS_WITHDRAW_TYPE;
exports.PAYMENTS_WITHHOLDING_TYPE = PAYMENTS_WITHHOLDING_TYPE;
exports.PEATIO_ORDER_TYPE = PEATIO_ORDER_TYPE;
exports.PEATIO_SERVICE_NAME = PEATIO_SERVICE_NAME;
exports.REFERRAL_PROGRAM_PAYOUT_TYPE = REFERRAL_PROGRAM_PAYOUT_TYPE;
exports.REFERRAL_PROGRAM_SERVICE_NAME = REFERRAL_PROGRAM_SERVICE_NAME;
exports.REWARDS_PAYMENTS_TYPE = REWARDS_PAYMENTS_TYPE;
exports.REWARDS_SERVICE_NAME = REWARDS_SERVICE_NAME;
exports.SR_BARRIGA_PAYMENT_TYPE = SR_BARRIGA_PAYMENT_TYPE;
exports.SR_BARRIGA_SERVICE_NAME = SR_BARRIGA_SERVICE_NAME;
exports.WITHHOLDINGS_SERVICE_NAME = WITHHOLDINGS_SERVICE_NAME;
exports.WITHHOLDINGS_WITHHOLDINGS_TYPE = WITHHOLDINGS_WITHHOLDINGS_TYPE;

}(events));

var RECORD_STATE;
(function (RECORD_STATE) {
    RECORD_STATE["SUCCEED"] = "succeed";
    RECORD_STATE["FAILED"] = "failed";
    RECORD_STATE["PENDING"] = "pending";
    RECORD_STATE["WAITING_AUTHORIZATION"] = "waiting_authorization";
    RECORD_STATE["EXPIRED"] = "expired";
    RECORD_STATE["UNAUTHORIZED"] = "unauthorized";
})(RECORD_STATE || (RECORD_STATE = {}));

var TokenType;
(function (TokenType) {
    TokenType["ADID"] = "adjust";
    TokenType["FCM"] = "fcm";
    TokenType["ANDROID_ADID"] = "android_adid";
    TokenType["IOS_IDFA"] = "ios_idfa";
})(TokenType || (TokenType = {}));

var ORDER_SUBSCRIPTION_PAYMENTS;
(function (ORDER_SUBSCRIPTION_PAYMENTS) {
    ORDER_SUBSCRIPTION_PAYMENTS["NAME"] = "name";
    ORDER_SUBSCRIPTION_PAYMENTS["PONDERANCE"] = "ponderance";
})(ORDER_SUBSCRIPTION_PAYMENTS || (ORDER_SUBSCRIPTION_PAYMENTS = {}));
var AMOUNT_VALUE_ALLOWED_COMPANY;
(function (AMOUNT_VALUE_ALLOWED_COMPANY) {
    AMOUNT_VALUE_ALLOWED_COMPANY["EQUAL"] = "equal";
    AMOUNT_VALUE_ALLOWED_COMPANY["GREATER_OR_EQUAL"] = "greater_or_equal";
    AMOUNT_VALUE_ALLOWED_COMPANY["ANY"] = "any";
    AMOUNT_VALUE_ALLOWED_COMPANY["UNKNOWN"] = "unknown";
})(AMOUNT_VALUE_ALLOWED_COMPANY || (AMOUNT_VALUE_ALLOWED_COMPANY = {}));
var TYPE_COMPANY;
(function (TYPE_COMPANY) {
    TYPE_COMPANY["RECHARGE"] = "recharge";
    TYPE_COMPANY["DEBT"] = "debt";
    TYPE_COMPANY["ONLY_INVOICE"] = "only_invoice";
})(TYPE_COMPANY || (TYPE_COMPANY = {}));
var RECHARGE_TYPE_COMPANY;
(function (RECHARGE_TYPE_COMPANY) {
    RECHARGE_TYPE_COMPANY["CELLPHONE"] = "celular";
    RECHARGE_TYPE_COMPANY["TRANSPORT"] = "transporte";
    RECHARGE_TYPE_COMPANY["PREPAID"] = "prepagos";
})(RECHARGE_TYPE_COMPANY || (RECHARGE_TYPE_COMPANY = {}));
var PAYMENT_STATUS_SUBSCRIPTION_PAYMENT;
(function (PAYMENT_STATUS_SUBSCRIPTION_PAYMENT) {
    PAYMENT_STATUS_SUBSCRIPTION_PAYMENT["PENDING"] = "pending";
    PAYMENT_STATUS_SUBSCRIPTION_PAYMENT["PREPARED"] = "prepared";
    PAYMENT_STATUS_SUBSCRIPTION_PAYMENT["PROCESSING"] = "processing";
    PAYMENT_STATUS_SUBSCRIPTION_PAYMENT["SUCCEED"] = "succeed";
    PAYMENT_STATUS_SUBSCRIPTION_PAYMENT["FAILED"] = "failed";
    PAYMENT_STATUS_SUBSCRIPTION_PAYMENT["SYNC_FAILED"] = "sync_failed";
})(PAYMENT_STATUS_SUBSCRIPTION_PAYMENT || (PAYMENT_STATUS_SUBSCRIPTION_PAYMENT = {}));
var SUBSCRIPTION_STATUS_SUBSCRIPTION_PAYMENTS;
(function (SUBSCRIPTION_STATUS_SUBSCRIPTION_PAYMENTS) {
    SUBSCRIPTION_STATUS_SUBSCRIPTION_PAYMENTS["ARCHIVE"] = "archive";
    SUBSCRIPTION_STATUS_SUBSCRIPTION_PAYMENTS["ACTIVATE"] = "activate";
})(SUBSCRIPTION_STATUS_SUBSCRIPTION_PAYMENTS || (SUBSCRIPTION_STATUS_SUBSCRIPTION_PAYMENTS = {}));
var ACTION_TYPE_BARCODE_DEBT_DETAILS;
(function (ACTION_TYPE_BARCODE_DEBT_DETAILS) {
    ACTION_TYPE_BARCODE_DEBT_DETAILS["SELECT_COMPANY"] = "select_company";
    ACTION_TYPE_BARCODE_DEBT_DETAILS["SHOW_INVOICE"] = "show_invoice";
})(ACTION_TYPE_BARCODE_DEBT_DETAILS || (ACTION_TYPE_BARCODE_DEBT_DETAILS = {}));

var CREDIT_NOTE_STATE;
(function (CREDIT_NOTE_STATE) {
    CREDIT_NOTE_STATE["CREATED"] = "created";
    CREDIT_NOTE_STATE["WAITING"] = "waiting";
    CREDIT_NOTE_STATE["SUCCEED"] = "succeed";
    CREDIT_NOTE_STATE["FAILED"] = "failed";
    CREDIT_NOTE_STATE["SYNC_FAILED"] = "sync_failed";
})(CREDIT_NOTE_STATE || (CREDIT_NOTE_STATE = {}));
var INVOICE_WITHHOLDING_TAX_CONDITION;
(function (INVOICE_WITHHOLDING_TAX_CONDITION) {
    INVOICE_WITHHOLDING_TAX_CONDITION["FOREIGN"] = "foreign";
    INVOICE_WITHHOLDING_TAX_CONDITION["FINAL_CONSUMER"] = "final_consumer";
    INVOICE_WITHHOLDING_TAX_CONDITION["EXEMPT"] = "exempt";
    INVOICE_WITHHOLDING_TAX_CONDITION["SINGLETAX"] = "singletax";
    INVOICE_WITHHOLDING_TAX_CONDITION["REGISTERED_REPONSIBLE"] = "registered_responsible";
})(INVOICE_WITHHOLDING_TAX_CONDITION || (INVOICE_WITHHOLDING_TAX_CONDITION = {}));
var INVOICE_OPERATION_STATE;
(function (INVOICE_OPERATION_STATE) {
    INVOICE_OPERATION_STATE["ACTIVE"] = "active";
    INVOICE_OPERATION_STATE["ARCHIVE"] = "archived";
    INVOICE_OPERATION_STATE["CANCELLED"] = "cancelled";
})(INVOICE_OPERATION_STATE || (INVOICE_OPERATION_STATE = {}));
var INVOICE_WITHHOLDING_STATE;
(function (INVOICE_WITHHOLDING_STATE) {
    INVOICE_WITHHOLDING_STATE["CREATED"] = "created";
    INVOICE_WITHHOLDING_STATE["SUCCEED"] = "succeed";
    INVOICE_WITHHOLDING_STATE["FAILED"] = "failed";
    INVOICE_WITHHOLDING_STATE["SYNC_FAILED"] = "sync_failed";
    INVOICE_WITHHOLDING_STATE["PENDING"] = "pending";
})(INVOICE_WITHHOLDING_STATE || (INVOICE_WITHHOLDING_STATE = {}));
var INVOICE_WITHHOLDING_TYPE;
(function (INVOICE_WITHHOLDING_TYPE) {
    INVOICE_WITHHOLDING_TYPE["AFIP_A"] = "afip_A";
    INVOICE_WITHHOLDING_TYPE["AFIP_B"] = "afip_B";
    INVOICE_WITHHOLDING_TYPE["AFIP_C"] = "afip_C";
    INVOICE_WITHHOLDING_TYPE["FOREIGN"] = "foreign";
})(INVOICE_WITHHOLDING_TYPE || (INVOICE_WITHHOLDING_TYPE = {}));
var GET_INVOICE_TYPE;
(function (GET_INVOICE_TYPE) {
    GET_INVOICE_TYPE["CARD_TRANSACTION"] = "card_transaction";
    GET_INVOICE_TYPE["SWAP"] = "swap";
    GET_INVOICE_TYPE["DEPOSIT"] = "deposit";
    GET_INVOICE_TYPE["WITHDRAW"] = "withdraw";
})(GET_INVOICE_TYPE || (GET_INVOICE_TYPE = {}));
var GET_INVOICE_SERVICE;
(function (GET_INVOICE_SERVICE) {
    GET_INVOICE_SERVICE["KAIBACORP"] = "kaibacorp";
    GET_INVOICE_SERVICE["BROKER"] = "broker";
    GET_INVOICE_SERVICE["PAYMENTS"] = "payments";
})(GET_INVOICE_SERVICE || (GET_INVOICE_SERVICE = {}));

var ACCOUNT_PROVIDERS;
(function (ACCOUNT_PROVIDERS) {
    ACCOUNT_PROVIDERS["ALPACA"] = "alpaca";
    ACCOUNT_PROVIDERS["ESCO"] = "esco";
})(ACCOUNT_PROVIDERS || (ACCOUNT_PROVIDERS = {}));
var MERK_ORDER_SIDE;
(function (MERK_ORDER_SIDE) {
    MERK_ORDER_SIDE["BUY"] = "buy";
    MERK_ORDER_SIDE["SELL"] = "sell";
})(MERK_ORDER_SIDE || (MERK_ORDER_SIDE = {}));
var MERK_ASSET_TYPE;
(function (MERK_ASSET_TYPE) {
    MERK_ASSET_TYPE["EQUITY"] = "equity";
    MERK_ASSET_TYPE["OPTION"] = "option";
    MERK_ASSET_TYPE["FUTURE"] = "future";
    MERK_ASSET_TYPE["ETF"] = "etf";
    MERK_ASSET_TYPE["MUTUAL_FUND"] = "mutual_fund";
})(MERK_ASSET_TYPE || (MERK_ASSET_TYPE = {}));
var MERK_ORDER_TYPES;
(function (MERK_ORDER_TYPES) {
    MERK_ORDER_TYPES["MARKET"] = "market";
    MERK_ORDER_TYPES["LIMIT"] = "limit";
    MERK_ORDER_TYPES["STOP"] = "stop";
    MERK_ORDER_TYPES["STOP_LIMIT"] = "stop_limit";
    MERK_ORDER_TYPES["TRAILING_STOP"] = "trailing_stop";
})(MERK_ORDER_TYPES || (MERK_ORDER_TYPES = {}));
var MERK_ORDER_STATUS;
(function (MERK_ORDER_STATUS) {
    MERK_ORDER_STATUS["PENDING"] = "pending";
    MERK_ORDER_STATUS["FILLED"] = "filled";
    MERK_ORDER_STATUS["CANCELLED"] = "cancelled";
    MERK_ORDER_STATUS["EXPIRED"] = "expired";
    MERK_ORDER_STATUS["PARTIAL_FILLED"] = "partial_filled";
    MERK_ORDER_STATUS["REJECTED"] = "rejected";
    MERK_ORDER_STATUS["PENDING_LOCK"] = "pending_lock";
    MERK_ORDER_STATUS["LOCKED"] = "locked";
    MERK_ORDER_STATUS["FAILED_TO_LOCK"] = "failed_to_lock";
    MERK_ORDER_STATUS["FAILED_JOURNAL"] = "failed_journal";
    MERK_ORDER_STATUS["FAILED_TO_REMOVE_FUNDS"] = "failed_to_remove_funds";
    MERK_ORDER_STATUS["FUNDS_REMOVED"] = "funds_removed";
    MERK_ORDER_STATUS["AWAITING_CONFIRMATION"] = "awaiting_confirmation";
})(MERK_ORDER_STATUS || (MERK_ORDER_STATUS = {}));
var MERK_ASSET_STATUS;
(function (MERK_ASSET_STATUS) {
    MERK_ASSET_STATUS["ACTIVE"] = "active";
    MERK_ASSET_STATUS["INACTIVE"] = "inactive";
})(MERK_ASSET_STATUS || (MERK_ASSET_STATUS = {}));
var MERK_ACTIVITY_TYPE;
(function (MERK_ACTIVITY_TYPE) {
    MERK_ACTIVITY_TYPE["WITHDRAWAL"] = "withdrawal";
    MERK_ACTIVITY_TYPE["DEPOSIT"] = "deposit";
    MERK_ACTIVITY_TYPE["ORDER"] = "order";
    // From this on, this are "NON_TRADE_ACTIVITIES"
    MERK_ACTIVITY_TYPE["ACAT"] = "ACATC";
    MERK_ACTIVITY_TYPE["ACATS"] = "ACATS";
    MERK_ACTIVITY_TYPE["CIL"] = "CIL";
    MERK_ACTIVITY_TYPE["CSD"] = "CSD";
    MERK_ACTIVITY_TYPE["CSW"] = "CSW";
    MERK_ACTIVITY_TYPE["DIV"] = "DIV";
    MERK_ACTIVITY_TYPE["DIVCGL"] = "DIVCGL";
    MERK_ACTIVITY_TYPE["DIVCGS"] = "DIVCGS";
    MERK_ACTIVITY_TYPE["DIVNRA"] = "DIVNRA";
    MERK_ACTIVITY_TYPE["DIVROC"] = "DIVROC";
    MERK_ACTIVITY_TYPE["DIVTXEX"] = "DIVTXEX";
    MERK_ACTIVITY_TYPE["FEE"] = "FEE";
    MERK_ACTIVITY_TYPE["INT"] = "INT";
    MERK_ACTIVITY_TYPE["JNLS"] = "JNLS";
    MERK_ACTIVITY_TYPE["OPASN"] = "OPASN";
    MERK_ACTIVITY_TYPE["OPCA"] = "OPCA";
    MERK_ACTIVITY_TYPE["OPCSH"] = "OPCSH";
    MERK_ACTIVITY_TYPE["OPEXC"] = "OPEXC";
    MERK_ACTIVITY_TYPE["OPEXP"] = "OPEXP";
    MERK_ACTIVITY_TYPE["OPTRD"] = "OPTRD";
    MERK_ACTIVITY_TYPE["MA"] = "MA";
    MERK_ACTIVITY_TYPE["PTC"] = "PTC";
    MERK_ACTIVITY_TYPE["REORG"] = "REORG";
    MERK_ACTIVITY_TYPE["SPIN"] = "SPIN";
    MERK_ACTIVITY_TYPE["SPLIT"] = "SPLIT";
    MERK_ACTIVITY_TYPE["RECEIPT"] = "receipt";
    MERK_ACTIVITY_TYPE["COUPON"] = "coupon";
    MERK_ACTIVITY_TYPE["CHECKING_ACCOUNT_MOVEMENT"] = "checking_account_movement";
})(MERK_ACTIVITY_TYPE || (MERK_ACTIVITY_TYPE = {}));
var MERK_ACTIVITY_ORDER_STATUS;
(function (MERK_ACTIVITY_ORDER_STATUS) {
    MERK_ACTIVITY_ORDER_STATUS["NEW"] = "new";
    MERK_ACTIVITY_ORDER_STATUS["PARTIALLY_FILLED"] = "partially_filled";
    MERK_ACTIVITY_ORDER_STATUS["FILLED"] = "filled";
    MERK_ACTIVITY_ORDER_STATUS["DONE_FOR_DAY"] = "done_for_day";
    MERK_ACTIVITY_ORDER_STATUS["CANCELED"] = "canceled";
    MERK_ACTIVITY_ORDER_STATUS["EXPIRED"] = "expired";
    MERK_ACTIVITY_ORDER_STATUS["REPLACED"] = "replaced";
    MERK_ACTIVITY_ORDER_STATUS["PENDING_CANCEL"] = "pending_cancel";
    MERK_ACTIVITY_ORDER_STATUS["PENDING_REPLACE"] = "pending_replace";
    MERK_ACTIVITY_ORDER_STATUS["ACCEPTED"] = "accepted";
    MERK_ACTIVITY_ORDER_STATUS["PENDING_NEW"] = "pending_new";
    MERK_ACTIVITY_ORDER_STATUS["ACCEPTED_FOR_BIDDING"] = "accepted_for_bidding";
    MERK_ACTIVITY_ORDER_STATUS["STOPPED"] = "stopped";
    MERK_ACTIVITY_ORDER_STATUS["REJECTED"] = "rejected";
    MERK_ACTIVITY_ORDER_STATUS["SUSPENDED"] = "suspended";
    MERK_ACTIVITY_ORDER_STATUS["CALCULATED"] = "calculated";
})(MERK_ACTIVITY_ORDER_STATUS || (MERK_ACTIVITY_ORDER_STATUS = {}));
var MERK_ACTIVITY_NON_TRADE_STATUS;
(function (MERK_ACTIVITY_NON_TRADE_STATUS) {
    MERK_ACTIVITY_NON_TRADE_STATUS["EXECUTED"] = "executed";
    MERK_ACTIVITY_NON_TRADE_STATUS["CORRECT"] = "correct";
    MERK_ACTIVITY_NON_TRADE_STATUS["CANCELED"] = "canceled";
})(MERK_ACTIVITY_NON_TRADE_STATUS || (MERK_ACTIVITY_NON_TRADE_STATUS = {}));
var MERK_ACTIVITY_DEPOSIT_WITHDRAWAL_STATUS;
(function (MERK_ACTIVITY_DEPOSIT_WITHDRAWAL_STATUS) {
    MERK_ACTIVITY_DEPOSIT_WITHDRAWAL_STATUS["REFUSED"] = "refused";
    MERK_ACTIVITY_DEPOSIT_WITHDRAWAL_STATUS["EXECUTED"] = "executed";
    MERK_ACTIVITY_DEPOSIT_WITHDRAWAL_STATUS["CANCELED"] = "canceled";
    MERK_ACTIVITY_DEPOSIT_WITHDRAWAL_STATUS["CORRECT"] = "correct";
    MERK_ACTIVITY_DEPOSIT_WITHDRAWAL_STATUS["REJECTED"] = "rejected";
    MERK_ACTIVITY_DEPOSIT_WITHDRAWAL_STATUS["DELETED"] = "deleted";
    MERK_ACTIVITY_DEPOSIT_WITHDRAWAL_STATUS["QUEUED"] = "queued";
    MERK_ACTIVITY_DEPOSIT_WITHDRAWAL_STATUS["SENT_TO_CLEARING"] = "sent_to_clearing";
    MERK_ACTIVITY_DEPOSIT_WITHDRAWAL_STATUS["PENDING"] = "pending";
})(MERK_ACTIVITY_DEPOSIT_WITHDRAWAL_STATUS || (MERK_ACTIVITY_DEPOSIT_WITHDRAWAL_STATUS = {}));
var MERK_ACTIVITY_SUB_TYPE;
(function (MERK_ACTIVITY_SUB_TYPE) {
    // Dividend Subtypes
    MERK_ACTIVITY_SUB_TYPE["CDIV"] = "CDIV";
    MERK_ACTIVITY_SUB_TYPE["SDIV"] = "SDIV";
    // Fee Subtypes
    MERK_ACTIVITY_SUB_TYPE["REG"] = "REG";
    MERK_ACTIVITY_SUB_TYPE["TAF"] = "TAF";
    MERK_ACTIVITY_SUB_TYPE["LCT"] = "LCT";
    MERK_ACTIVITY_SUB_TYPE["ORF"] = "ORF";
    MERK_ACTIVITY_SUB_TYPE["OCC"] = "OCC";
    MERK_ACTIVITY_SUB_TYPE["NRC"] = "NRC";
    MERK_ACTIVITY_SUB_TYPE["NRV"] = "NRV";
    MERK_ACTIVITY_SUB_TYPE["COM"] = "COM";
    // Interest Subtypes
    MERK_ACTIVITY_SUB_TYPE["MGN"] = "MGN";
    MERK_ACTIVITY_SUB_TYPE["CDT"] = "CDT";
    MERK_ACTIVITY_SUB_TYPE["SWP"] = "SWP";
    MERK_ACTIVITY_SUB_TYPE["QII"] = "QII";
    // Merger Subtypes
    MERK_ACTIVITY_SUB_TYPE["CMA"] = "CMA";
    MERK_ACTIVITY_SUB_TYPE["SMA"] = "SMA";
    MERK_ACTIVITY_SUB_TYPE["SCMA"] = "SCMA";
    // Name Change Subtypes
    MERK_ACTIVITY_SUB_TYPE["SNC"] = "SNC";
    MERK_ACTIVITY_SUB_TYPE["CNC"] = "CNC";
    MERK_ACTIVITY_SUB_TYPE["SCNC"] = "SCNC";
    // Dividend Activity Subtypes
    MERK_ACTIVITY_SUB_TYPE["DIV_CDIV"] = "DIV.CDIV";
    MERK_ACTIVITY_SUB_TYPE["DIV_SDIV"] = "DIV.SDIV";
    // Merger Activity Subtypes
    MERK_ACTIVITY_SUB_TYPE["MA_CMA"] = "MA.CMA";
    MERK_ACTIVITY_SUB_TYPE["MA_SMA"] = "MA.SMA";
    MERK_ACTIVITY_SUB_TYPE["MA_SCMA"] = "MA.SCMA";
    // Name Change Activity Subtypes
    MERK_ACTIVITY_SUB_TYPE["NC_CNC"] = "NC.CNC";
    MERK_ACTIVITY_SUB_TYPE["NC_SNC"] = "NC.SNC";
    MERK_ACTIVITY_SUB_TYPE["NC_SCNC"] = "NC.SCNC";
    // Spinoff Activity Subtype
    MERK_ACTIVITY_SUB_TYPE["SPIN"] = "SPIN";
    // Split Activity Subtypes
    MERK_ACTIVITY_SUB_TYPE["SPLIT_FSPLIT"] = "SPLIT.FSPLIT";
    MERK_ACTIVITY_SUB_TYPE["SPLIT_RSPLIT"] = "SPLIT.RSPLIT";
    MERK_ACTIVITY_SUB_TYPE["SPLIT_USPLIT"] = "SPLIT.USPLIT";
    MERK_ACTIVITY_SUB_TYPE["WRM"] = "WRM";
    // Split Subtypes
    MERK_ACTIVITY_SUB_TYPE["FSPLIT"] = "FSPLIT";
    MERK_ACTIVITY_SUB_TYPE["RSPLIT"] = "RSPLIT";
    MERK_ACTIVITY_SUB_TYPE["USPLIT"] = "USPLIT";
    // Voluntary Activity Subtypes
    MERK_ACTIVITY_SUB_TYPE["VTND"] = "VTND";
    MERK_ACTIVITY_SUB_TYPE["VWRT"] = "VWRT";
    MERK_ACTIVITY_SUB_TYPE["VRGT"] = "VRGT";
    MERK_ACTIVITY_SUB_TYPE["VEXH"] = "VEXH";
    // Withholding Subtypes
    MERK_ACTIVITY_SUB_TYPE["SWH"] = "SWH";
    MERK_ACTIVITY_SUB_TYPE["FWH"] = "FWH";
    MERK_ACTIVITY_SUB_TYPE["SLWH"] = "SLWH";
})(MERK_ACTIVITY_SUB_TYPE || (MERK_ACTIVITY_SUB_TYPE = {}));
var MERK_DOCUMENT_TYPE;
(function (MERK_DOCUMENT_TYPE) {
    MERK_DOCUMENT_TYPE["ACCOUNT_APPLICATION"] = "account_application";
    MERK_DOCUMENT_TYPE["ACCOUNT_STATEMENT"] = "account_statement";
    MERK_DOCUMENT_TYPE["BNOTICE_1"] = "bnotice_1";
    MERK_DOCUMENT_TYPE["BNOTICE_2"] = "bnotice_2";
    MERK_DOCUMENT_TYPE["CRYPTO_ACCOUNT_STATEMENT"] = "crypto_account_statement";
    MERK_DOCUMENT_TYPE["MANDATORY_WITHHOLDING"] = "mandatory_withholding";
    MERK_DOCUMENT_TYPE["OPTIONS_APPLICATION"] = "options_application";
    MERK_DOCUMENT_TYPE["TAX_1099_B_DETAILS"] = "tax_1099_b_details";
    MERK_DOCUMENT_TYPE["TAX_1099_B_FORM"] = "tax_1099_b_form";
    MERK_DOCUMENT_TYPE["TAX_1099_DIV_DETAILS"] = "tax_1099_div_details";
    MERK_DOCUMENT_TYPE["TAX_1099_DIV_FORM"] = "tax_1099_div_form";
    MERK_DOCUMENT_TYPE["TAX_1099_INT_DETAILS"] = "tax_1099_int_details";
    MERK_DOCUMENT_TYPE["TAX_1099_INT_FORM"] = "tax_1099_int_form";
    MERK_DOCUMENT_TYPE["TAX_STATEMENT"] = "tax_statement";
    MERK_DOCUMENT_TYPE["TRADE_CONFIRMATION"] = "trade_confirmation";
})(MERK_DOCUMENT_TYPE || (MERK_DOCUMENT_TYPE = {}));
var MERK_WITHDRAW_FROM_PROVIDER_TYPE;
(function (MERK_WITHDRAW_FROM_PROVIDER_TYPE) {
    MERK_WITHDRAW_FROM_PROVIDER_TYPE["CREDIT"] = "credit";
    MERK_WITHDRAW_FROM_PROVIDER_TYPE["DEBIT"] = "debit";
})(MERK_WITHDRAW_FROM_PROVIDER_TYPE || (MERK_WITHDRAW_FROM_PROVIDER_TYPE = {}));
var MERK_WITHDRAW_FROM_PROVIDER_STATUS;
(function (MERK_WITHDRAW_FROM_PROVIDER_STATUS) {
    MERK_WITHDRAW_FROM_PROVIDER_STATUS["PENDING"] = "pending";
    MERK_WITHDRAW_FROM_PROVIDER_STATUS["FAILED"] = "failed";
    MERK_WITHDRAW_FROM_PROVIDER_STATUS["SUCCESS"] = "success";
    MERK_WITHDRAW_FROM_PROVIDER_STATUS["SWAPING"] = "swaping";
    MERK_WITHDRAW_FROM_PROVIDER_STATUS["AWAITING_CONFIRMATION"] = "awaiting_confirmation";
})(MERK_WITHDRAW_FROM_PROVIDER_STATUS || (MERK_WITHDRAW_FROM_PROVIDER_STATUS = {}));
var MERK_PROFILE_QUESTIONS_TYPES;
(function (MERK_PROFILE_QUESTIONS_TYPES) {
    MERK_PROFILE_QUESTIONS_TYPES["RADIO_BUTTON"] = "RADIO_BUTTON";
    MERK_PROFILE_QUESTIONS_TYPES["CHECKBOX"] = "CHECKBOX";
})(MERK_PROFILE_QUESTIONS_TYPES || (MERK_PROFILE_QUESTIONS_TYPES = {}));
var MERK_PROFILE_INVESTOR_TYPES;
(function (MERK_PROFILE_INVESTOR_TYPES) {
    MERK_PROFILE_INVESTOR_TYPES["MODERATE"] = "moderate";
    MERK_PROFILE_INVESTOR_TYPES["AGGRESIV"] = "aggressive";
    MERK_PROFILE_INVESTOR_TYPES["CONSERVATIVE"] = "conservative";
})(MERK_PROFILE_INVESTOR_TYPES || (MERK_PROFILE_INVESTOR_TYPES = {}));
var MERK_ACTIVITY_FILTER_BY_DATE;
(function (MERK_ACTIVITY_FILTER_BY_DATE) {
    MERK_ACTIVITY_FILTER_BY_DATE["AGREEMENT"] = "agreement";
    MERK_ACTIVITY_FILTER_BY_DATE["LIQUIDATION"] = "liquidation";
})(MERK_ACTIVITY_FILTER_BY_DATE || (MERK_ACTIVITY_FILTER_BY_DATE = {}));
var MERK_ACCOUNT_INFO_TAX_CONDITION;
(function (MERK_ACCOUNT_INFO_TAX_CONDITION) {
    MERK_ACCOUNT_INFO_TAX_CONDITION["FINAL_CONSUMER"] = "final_consumer";
    MERK_ACCOUNT_INFO_TAX_CONDITION["EXEMPT"] = "exempt";
    MERK_ACCOUNT_INFO_TAX_CONDITION["REGISTERED_RESPONSIBLE"] = "registered_responsible";
    MERK_ACCOUNT_INFO_TAX_CONDITION["SINGLE_TAX"] = "singletax";
})(MERK_ACCOUNT_INFO_TAX_CONDITION || (MERK_ACCOUNT_INFO_TAX_CONDITION = {}));
var MERK_WIZARD_TYPE;
(function (MERK_WIZARD_TYPE) {
    MERK_WIZARD_TYPE["ASSET"] = "asset";
    MERK_WIZARD_TYPE["QUESTION"] = "question";
})(MERK_WIZARD_TYPE || (MERK_WIZARD_TYPE = {}));
var FundingSource;
(function (FundingSource) {
    FundingSource["EMPLOYMENT"] = "employment_income";
    FundingSource["INVESTMENTS"] = "investments";
    FundingSource["INHERITANCE"] = "inheritance";
    FundingSource["BUSINESS"] = "business_income";
    FundingSource["SAVINGS"] = "savings";
    FundingSource["FAMILY"] = "family";
})(FundingSource || (FundingSource = {}));
var NaturalAccountStatus;
(function (NaturalAccountStatus) {
    NaturalAccountStatus["ACTIVE"] = "active";
    NaturalAccountStatus["PENDING"] = "pending";
    NaturalAccountStatus["ADMIN_REVIEW"] = "admin_review";
    NaturalAccountStatus["DELETED"] = "deleted";
    NaturalAccountStatus["REJECTED"] = "rejected";
    NaturalAccountStatus["APPROVED"] = "approved";
    NaturalAccountStatus["WAITING_FOR_EXTERNAL"] = "waiting_for_external";
    NaturalAccountStatus["FLAGGED"] = "flagged";
})(NaturalAccountStatus || (NaturalAccountStatus = {}));

var BABYLON_TIMEFRAME_CASES;
(function (BABYLON_TIMEFRAME_CASES) {
    BABYLON_TIMEFRAME_CASES["HOUR"] = "1h";
    BABYLON_TIMEFRAME_CASES["DAY"] = "1d";
    BABYLON_TIMEFRAME_CASES["WEEK"] = "1w";
    BABYLON_TIMEFRAME_CASES["MONTH"] = "1mo";
})(BABYLON_TIMEFRAME_CASES || (BABYLON_TIMEFRAME_CASES = {}));

var JARVIS_MEMBER_REPORTS_TYPE;
(function (JARVIS_MEMBER_REPORTS_TYPE) {
    JARVIS_MEMBER_REPORTS_TYPE["HOLDINGS"] = "holdings";
})(JARVIS_MEMBER_REPORTS_TYPE || (JARVIS_MEMBER_REPORTS_TYPE = {}));

var CurrencyType;
(function (CurrencyType) {
    CurrencyType["REAL_STATE"] = "real_state";
    CurrencyType["CRYPTOCURRENCY"] = "cryptocurrency";
    CurrencyType["STABLECOIN"] = "stablecoin";
    CurrencyType["FIAT"] = "fiat";
    CurrencyType["COMMODITY"] = "commodity";
})(CurrencyType || (CurrencyType = {}));

var EVENT_TRACKER_EVENT_NAMES;
(function (EVENT_TRACKER_EVENT_NAMES) {
    EVENT_TRACKER_EVENT_NAMES["API_ERROR"] = "api_error";
    EVENT_TRACKER_EVENT_NAMES["CODE_ERROR"] = "code_error";
    EVENT_TRACKER_EVENT_NAMES["USER_LOGGED_IN"] = "user_logged_in";
    EVENT_TRACKER_EVENT_NAMES["WEB_EXCEPTION"] = "web_exception";
    EVENT_TRACKER_EVENT_NAMES["UNKNOWN_ERROR"] = "unknown_error";
    EVENT_TRACKER_EVENT_NAMES["ENROLL_ERROR"] = "enroll_error";
    EVENT_TRACKER_EVENT_NAMES["LOGIN_ERROR"] = "login_error";
    EVENT_TRACKER_EVENT_NAMES["METAMAP_ERROR"] = "metamap_error";
    EVENT_TRACKER_EVENT_NAMES["ATTESTATION_ISSUE"] = "attestation_issue";
    EVENT_TRACKER_EVENT_NAMES["LEGAL_ADDRESS"] = "legal_address";
    EVENT_TRACKER_EVENT_NAMES["REGISTER"] = "register";
    EVENT_TRACKER_EVENT_NAMES["PROMOTION_CODE_SKIPPED"] = "promotion_code_skipped";
    EVENT_TRACKER_EVENT_NAMES["PROMOTION_CODE_SETTED"] = "promotion_code_setted";
    EVENT_TRACKER_EVENT_NAMES["PROMOTION_CODE_TO_SET"] = "promotion_code_to_set";
    EVENT_TRACKER_EVENT_NAMES["METAMAP_INFO"] = "metamap_info";
})(EVENT_TRACKER_EVENT_NAMES || (EVENT_TRACKER_EVENT_NAMES = {}));

class PublicServices {
    constructor(serviceConfig) {
        this.content = new ContentPublic(serviceConfig);
        this.barong = new BarongPublic(serviceConfig);
        this.payments = new PaymentsPublic(serviceConfig);
        this.peatio = new PeatioPublic(serviceConfig);
        this.compliance = new CompliancePublic(serviceConfig);
        this.interest = new InterestPublic(serviceConfig);
        this.hub = new HubPublic(serviceConfig);
        this.karasu = new KarasuPublic(serviceConfig);
        this.kaibacorp = new KaibacorpPublic(serviceConfig);
        this.coinagproxy = new CoinagproxyPublic(serviceConfig);
        this.precogs = new PrecogsPublic(serviceConfig);
        this.broker = new BrokerPublic(serviceConfig);
        this.deathnote = new DeathnotePublic(serviceConfig);
        this.hodor = new HodorPublic(serviceConfig);
        this.withholding = new WithholdingsPublic(serviceConfig);
        this.babylon = new BabylonPublic(serviceConfig);
        this.merk = new MerkPublic(serviceConfig);
    }
}
class AdminServices {
    constructor(authenticationService) {
        this.peatio = new PeatioAdmin(authenticationService);
        this.interest = new InterestAdmin(authenticationService);
        this.coinagproxy = new CoinagproxyAdmin(authenticationService);
        this.compliance = new ComplianceAdmin(authenticationService);
        this.barong = new BarongAdmin(authenticationService);
        this.caronte = new CaronteAdmin(authenticationService);
    }
}
class ManagementServices {
    constructor(authenticationService) {
        this.barong = new BarongManagement(authenticationService);
        this.compliance = new ComplianceManagement(authenticationService);
    }
}
class MemberServices {
    constructor(authenticationService) {
        this.interest = new InterestMember(authenticationService);
        this.compliance = new ComplianceMember(authenticationService);
        this.payments = new PaymentsMember(authenticationService);
        this.barong = new BarongMember(authenticationService);
        this.peatio = new PeatioMember(authenticationService);
        this.referrer = new ReferrerMember(authenticationService);
        this.hub = new HubMember(authenticationService);
        this.karasu = new KarasuMember(authenticationService);
        this.kaibacorp = new KaibacorpMember(authenticationService);
        this.coinagproxy = new CoinagproxyMember(authenticationService);
        this.precogs = new PrecogsMember(authenticationService);
        this.snake = new SnakeMember(authenticationService);
        this.caronte = new CaronteMember(authenticationService);
        this.broker = new BrokerMember(authenticationService);
        this.rewards = new RewardsMember(authenticationService);
        this.deathnote = new DeathnoteMember(authenticationService);
        this.hodor = new HodorMember(authenticationService);
        this.alchemist = new AlchemistMember(authenticationService);
        this.memento = new MementoMember(authenticationService);
        this.withholdings = new WithholdingsMember(authenticationService);
        this.subscriptionpayments = new SubscriptionPaymentsMember(authenticationService);
        this.merk = new MerkMember(authenticationService);
        this.jarvis = new JarvisMember(authenticationService);
        this.partner = new PartnerMember(authenticationService);
    }
}
class PartnerServices {
    constructor(authenticationService) {
        this.compliance = new CompliancePartner(authenticationService);
        this.payments = new PaymentsPartner(authenticationService);
    }
}
class ManagementBoundary extends ManagementServices {
    constructor(authenticationService) {
        super(authenticationService);
    }
}
class PublicBoundary extends PublicServices {
    constructor(serviceConfig) {
        super(serviceConfig);
    }
}
class AdminBoundary extends AdminServices {
    constructor(authenticationService) {
        super(authenticationService);
    }
}
class MemberBoundary extends MemberServices {
    constructor(authenticationService) {
        super(authenticationService);
    }
}
class PartnerBoundary extends PartnerServices {
    constructor(authenticationService) {
        super(authenticationService);
    }
}

var ADJUSTMENTS_ADJUSTMENT_TYPE = events.ADJUSTMENTS_ADJUSTMENT_TYPE;
var ADJUSTMENTS_SERVICE_NAME = events.ADJUSTMENTS_SERVICE_NAME;
var ALCHEMIST_RULE_EXECUTION_TYPE = events.ALCHEMIST_RULE_EXECUTION_TYPE;
var ALCHEMIST_SERVICE_NAME = events.ALCHEMIST_SERVICE_NAME;
var BROKER_AUTOSWAP_ALCHEMIST_TYPE = events.BROKER_AUTOSWAP_ALCHEMIST_TYPE;
var BROKER_AUTOSWAP_KAIBACORP_TYPE = events.BROKER_AUTOSWAP_KAIBACORP_TYPE;
var BROKER_AUTOSWAP_PAYMENTS_TYPE = events.BROKER_AUTOSWAP_PAYMENTS_TYPE;
var BROKER_DOLLARIZE_TYPE = events.BROKER_DOLLARIZE_TYPE;
var BROKER_SERVICE_NAME = events.BROKER_SERVICE_NAME;
var BROKER_SWAP_TYPE = events.BROKER_SWAP_TYPE;
var INTERESTS_ACCOUNTING_RECORD_TYPE = events.INTERESTS_ACCOUNTING_RECORD_TYPE;
var INTERESTS_SERVICE_NAME = events.INTERESTS_SERVICE_NAME;
var KAIBACORP_SERVICE_NAME = events.KAIBACORP_SERVICE_NAME;
var KAIBA_TRANSACTION_TYPE = events.KAIBA_TRANSACTION_TYPE;
var MERK_CREDIT_ACCOUNT_MOVEMENT_RECORD_TYPE = events.MERK_CREDIT_ACCOUNT_MOVEMENT_RECORD_TYPE;
var MERK_DEBIT_ACCOUNT_MOVEMENT_RECORD_TYPE = events.MERK_DEBIT_ACCOUNT_MOVEMENT_RECORD_TYPE;
var MERK_ORDER_TYPE = events.MERK_ORDER_TYPE;
var PAYMENTS_DEPOSIT_TYPE = events.PAYMENTS_DEPOSIT_TYPE;
var PAYMENTS_SERVICE_NAME = events.PAYMENTS_SERVICE_NAME;
var PAYMENTS_WITHDRAW_TYPE = events.PAYMENTS_WITHDRAW_TYPE;
var PAYMENTS_WITHHOLDING_TYPE = events.PAYMENTS_WITHHOLDING_TYPE;
var PEATIO_ORDER_TYPE = events.PEATIO_ORDER_TYPE;
var PEATIO_SERVICE_NAME = events.PEATIO_SERVICE_NAME;
var REFERRAL_PROGRAM_PAYOUT_TYPE = events.REFERRAL_PROGRAM_PAYOUT_TYPE;
var REFERRAL_PROGRAM_SERVICE_NAME = events.REFERRAL_PROGRAM_SERVICE_NAME;
var REWARDS_PAYMENTS_TYPE = events.REWARDS_PAYMENTS_TYPE;
var REWARDS_SERVICE_NAME = events.REWARDS_SERVICE_NAME;
var SR_BARRIGA_PAYMENT_TYPE = events.SR_BARRIGA_PAYMENT_TYPE;
var SR_BARRIGA_SERVICE_NAME = events.SR_BARRIGA_SERVICE_NAME;
var WITHHOLDINGS_SERVICE_NAME = events.WITHHOLDINGS_SERVICE_NAME;
var WITHHOLDINGS_WITHHOLDINGS_TYPE = events.WITHHOLDINGS_WITHHOLDINGS_TYPE;
var WITHHOLDINGS_WITHHOLDING_STATUS = events.WITHHOLDINGS_WITHHOLDING_STATUS;
export { ACCOUNT_ADVISOR_TYPE, ACCOUNT_INVITE_STATUS, ACCOUNT_PROVIDERS, ACCOUNT_TYPE, ACTION_TYPE_BARCODE_DEBT_DETAILS, ADJUSTMENTS_ADJUSTMENT_TYPE, ADJUSTMENTS_SERVICE_NAME, ADJUSTMENT_STATUS, ADJUSTMENT_SUBCATEGORY_NAME, ADJUSTMENT_SUBCATEGORY_TYPE, AIV_GENDERS, ALCHEMIST_MEMBER_EVENTS, ALCHEMIST_MEMBER_RULES, ALCHEMIST_MEMBER_RULES_DISABLE, ALCHEMIST_MEMBER_RULES_EXECUTIONS, ALCHEMIST_MEMBER_RULES_TARJET_DISABLE, ALCHEMIST_PREFIX, ALCHEMIST_RULE_EXECUTION_TYPE, ALCHEMIST_SERVICE_NAME, AMOUNT_VALUE_ALLOWED_COMPANY, API_URL_CONDITION, AdminBoundary, AlchemistMember, AuthenticationService, AuthorizationAction, AuthorizationStatus, AuthorizationType, BABYLON_API_END_POINT_CLOSURE_PRICES, BABYLON_API_END_POINT_PRICES, BABYLON_API_END_POINT_VARIATION_PERCENTAGE, BABYLON_PREFIX, BABYLON_TIMEFRAME_CASES, BANNER_TYPE, BARONG_API_END_POINT_ADMIN_ACTIVITIES, BARONG_API_END_POINT_ADMIN_IDENTITY_SESSIONS_AUTH0_LOG_IN, BARONG_API_END_POINT_ADMIN_IDENTITY_SESSIONS_AUTH0_REGISTER, BARONG_API_END_POINT_CONFIGS, BARONG_API_END_POINT_CONFIRM_USER, BARONG_API_END_POINT_EMAIL_GENERATE_CODE, BARONG_API_END_POINT_GET_ACTIVITIES, BARONG_API_END_POINT_GET_API_KEY, BARONG_API_END_POINT_GET_USER, BARONG_API_END_POINT_ME, BARONG_API_END_POINT_OTP, BARONG_API_END_POINT_PASSWORD, BARONG_API_END_POINT_PASSWORD_CONFIRM_CODE, BARONG_API_END_POINT_PASSWORD_GENERATE_CODE, BARONG_API_END_POINT_PASSWORD_VALIDATE, BARONG_API_END_POINT_PUBLIC_AUTH0_CONFIG, BARONG_API_END_POINT_RESOURCE_DATA_STORAGE, BARONG_API_END_POINT_SESSIONS, BARONG_API_END_POINT_USERS, BARONG_PREFIX, BROKER_API_END_POINT_LIST, BROKER_API_END_POINT_PAIRS, BROKER_API_END_POINT_SWAPS, BROKER_API_END_POINT_WS_PAIR, BROKER_AUTOSWAP_ALCHEMIST_TYPE, BROKER_AUTOSWAP_KAIBACORP_TYPE, BROKER_AUTOSWAP_PAYMENTS_TYPE, BROKER_DOLLARIZE_TYPE, BROKER_PREFIX, BROKER_SERVICE_NAME, BROKER_SWAP_TYPE, BabylonPublic, BarongMember, BarongPublic, BrokerMember, BrokerPublic, CAMPAIGN_STATUS_OPTIONS, CARD_AGENT, CARD_LIMIT_STATUS, CARD_LIMIT_TYPE, CARD_REASON, CARD_SHIPMENT_STATUS, CARD_SHIPMENT_STATUS_DETAIL, CARD_STATUS, CARD_TRANSACTION_DETAIL_TYPE, CARD_TRANSACTION_STATUS, CARD_TRANSACTION_TYPE, CARD_TYPE, CARD_TYPE_ID, CARD_TYPE_STATUS, CARONTE_API_END_POINT_ADMIN_ADJUSTMENTS, CARONTE_API_END_POINT_MEMBER_ADJUSTMENTS, CARONTE_PREFIX, CATEGORIES_AUTOMATED_ITEM, CHALLENGE_METHODS, CHALLENGE_STATUS, COMERCIAL_RULE_TYPE, COMPLIANCE_API_END_POINT_ADMIN_POST_TERMS_AND_CONDITIONS, COMPLIANCE_API_END_POINT_ADMIN_POST_TYCS_CONSTRAINS, COMPLIANCE_API_END_POINT_ADMIN_VERIFICATION, COMPLIANCE_API_END_POINT_ADMIN_VERIFICATION_DATA, COMPLIANCE_API_END_POINT_CUSTOMER, COMPLIANCE_API_END_POINT_DOCUMENT_TYPE, COMPLIANCE_API_END_POINT_EDIT_PHONE, COMPLIANCE_API_END_POINT_EXTRA_DOCUMENTS, COMPLIANCE_API_END_POINT_GET_MEMBER, COMPLIANCE_API_END_POINT_GET_MEMBER_INFORMATION_REQUEST, COMPLIANCE_API_END_POINT_GET_MEMBER_INFORMATION_REQUEST_TEMPLATE, COMPLIANCE_API_END_POINT_ID_DOCUMENTS, COMPLIANCE_API_END_POINT_JURIDICAL_PERSON_DATA, COMPLIANCE_API_END_POINT_MEMBER_ACCEPT_TERMS_AND_CONDITIONS, COMPLIANCE_API_END_POINT_MEMBER_APPLY_INFORMATION_REQUEST, COMPLIANCE_API_END_POINT_MEMBER_FLOW_NEW, COMPLIANCE_API_END_POINT_MEMBER_FLOW_UPLOAD, COMPLIANCE_API_END_POINT_MEMBER_GET_ACCEPTED_TERMS_AND_CONDITIONS, COMPLIANCE_API_END_POINT_MEMBER_GET_FLOW_NEXT, COMPLIANCE_API_END_POINT_MEMBER_GET_TERMS_AND_CONDITIONS, COMPLIANCE_API_END_POINT_MEMBER_GET_UPDATED_TERMS_AND_CONDITIONS, COMPLIANCE_API_END_POINT_MEMBER_STATUS, COMPLIANCE_API_END_POINT_MEMBER_ULTIMATE_BENEFICIAL_OWNERS, COMPLIANCE_API_END_POINT_MEMBER_ULTIMATE_BENEFICIAL_OWNERS_DELETE, COMPLIANCE_API_END_POINT_MEMBER_ULTIMATE_BENEFICIAL_OWNERS_DOCUMENT, COMPLIANCE_API_END_POINT_NATURAL_PERSON_DATA, COMPLIANCE_API_END_POINT_NOTIFY_MEMBER_INFORMATION_REQUEST, COMPLIANCE_API_END_POINT_PARTNER_MEMBERS, COMPLIANCE_API_END_POINT_POST_MEMBER_INFORMATION_REQUEST, COMPLIANCE_API_END_POINT_PUBLIC_GET_TERMS_AND_CONDITIONS, COMPLIANCE_API_END_POINT_VIDEO, COMPLIANCE_PREFIX, CONTACT_ADDRESS_STATE, CONTACT_STATE, CONTACT_TYPE, CONTENT_API_END_POINT_COUNTRIES, CONTENT_API_END_POINT_CURRENCY_LEGAL, CONTENT_API_END_POINT_LIMITS, CONTENT_API_END_POINT_NEWS, CONTENT_API_END_POINT_PROMOTIONS, CONTENT_PREFIX, CREDIT_NOTE_STATE, CURRENCY_FILTER_PARAMS, CURRENCY_TYPE, CaronteAdmin, CaronteMember, CoinagproxyMember, CoinagproxyPublic, ComplianceAdmin, ComplianceMember, CompliancePartner, CompliancePublic, ContentPublic, CurrencyType, DEATHNOTE_API_END_POINT_ADDRESSES, DEATHNOTE_API_END_POINT_BOOK_ADDRESS, DEATHNOTE_API_END_POINT_CONTACTS, DEATHNOTE_API_END_POINT_ENTITIES, DEATHNOTE_API_END_POINT_ENTITIES_FROM, DEATHNOTE_API_END_POINT_VALIDATE_ADDRES, DEATHNOTE_PREFIX, DEPOSIT_ADDRESS_BRIDGE_ACCOUNT_TYPE, DEPOSIT_ADDRESS_STATE, DEPOSIT_ADDRESS_TYPE, DEPOSIT_STATE_OPTIONS, DeathnoteMember, ENTITY_STATE, ENTITY_TYPE, EVENT_TRACKER_EVENT_NAMES, EXTERNAL_PAYMENT_ADDRESS_ACCOUNT_TYPES, EXTERNAL_PAYMENT_ADDRESS_STATES, EXTRA_DOCUMENT_STATUS, FEE_TYPE, FEE_TYPES, FLAG_FORM_INPUT_TYPE, FLOW_CATEGORIES, FLOW_COUNTRY, FLOW_NEXT_STEP, FlowTypes, FundingSource, GENDERS, GET_INVOICE_SERVICE, GET_INVOICE_TYPE, HISTORY_OPERATION_TYPE, HUB_API_END_POINT_ADDRESSES, HUB_API_END_POINT_BALANCES, HUB_API_END_POINT_BALANCES_TOTAL, HUB_API_END_POINT_CONTACTS, HUB_API_END_POINT_CONVERT_CURRENCIES, HUB_API_END_POINT_CURRENCIES, HUB_API_END_POINT_CURRENCY_MARKETS, HUB_API_END_POINT_FEES, HUB_API_END_POINT_HISTORY, HUB_API_END_POINT_INVESTMENTS, HUB_API_END_POINT_MEMBER_CARD_EXPIRATION_DATE, HUB_API_END_POINT_MEMBER_CLOSURE_PRICES, HUB_API_END_POINT_MEMBER_CURRENCIES, HUB_API_END_POINT_MEMBER_CURRENCIES_PREFERRED_DEPOSIT, HUB_API_END_POINT_MEMBER_CURRENCIES_PREFERRED_WITHDRAW, HUB_API_END_POINT_MEMBER_DOCKET_UPDATE_EXPIRATION_DATE, HUB_API_END_POINT_MEMBER_GET_ACCEPTED_TERMS_AND_CONDITIONS, HUB_API_END_POINT_MEMBER_GET_ACCOUNT_ADVISOR, HUB_API_END_POINT_MEMBER_GET_BANNER, HUB_API_END_POINT_MEMBER_GET_HOME_BANNER, HUB_API_END_POINT_MEMBER_GET_MARKET_ASSETS, HUB_API_END_POINT_MEMBER_GET_MONTHLY_REWARDS, HUB_API_END_POINT_MEMBER_GET_NEXT_ROUTE, HUB_API_END_POINT_MEMBER_GET_STOCK_PORTFOLIOS, HUB_API_END_POINT_MEMBER_GET_TERMS_AND_CONDITIONS, HUB_API_END_POINT_MEMBER_GET_UPDATED_TERMS_AND_CONDITIONS, HUB_API_END_POINT_MEMBER_INVESTMENTS_GET_INTEREST_PAYMENTS, HUB_API_END_POINT_MEMBER_MERK_ACCOUNT_INFO, HUB_API_END_POINT_MEMBER_OPEN_POSITIONS, HUB_API_END_POINT_MEMBER_PAIRS, HUB_API_END_POINT_MEMBER_PRICES, HUB_API_END_POINT_MEMBER_SEND_ESIGNATURE_CONTRACT, HUB_API_END_POINT_MEMBER_VERIFICATION_ENTERPRISE, HUB_API_END_POINT_PAIRS, HUB_API_END_POINT_PUBLIC_APP_MIN_VERSION_ALLOWED, HUB_API_END_POINT_PUBLIC_CITIES, HUB_API_END_POINT_PUBLIC_COUNTRIES, HUB_API_END_POINT_PUBLIC_REGIONS, HUB_API_END_POINT_REFERRER_DATA, HUB_API_END_POINT_REWARDS_CASHBACK_LEVELS, HUB_API_END_POINT_REWARDS_DATA, HUB_PREFIX, HodorMember, HubMember, HubPublic, INFORMATION_REQUEST_STATUS, INFORMATION_REQUEST_TYPE, INTERESTS_ACCOUNTING_RECORD_TYPE, INTERESTS_SERVICE_NAME, INTEREST_API_END_POINT_ADMIN_CURRENCIES, INTEREST_API_END_POINT_ADMIN_FEE_OF_DATE, INTEREST_API_END_POINT_ADMIN_RATE_OF_DATE, INTEREST_API_END_POINT_ADMIN_STRATEGIES, INTEREST_API_END_POINT_ADMIN_TOTALS, INTEREST_API_END_POINT_ALL_STRATEGIES, INTEREST_API_END_POINT_INTEREST_PAYMENTS, INTEREST_API_END_POINT_INTEREST_PAYMENTS_SUM, INTEREST_API_END_POINT_INVESTMENTS, INTEREST_API_END_POINT_INVESTMENTS_ACTIVATE, INTEREST_API_END_POINT_INVESTMENTS_ACTIVE, INTEREST_API_END_POINT_INVESTMENTS_DEACTIVATE, INTEREST_API_END_POINT_PUBLIC_CURRENCIES, INTEREST_PREFIX, INVESTMENT_STATE, INVOICE_OPERATION_STATE, INVOICE_WITHHOLDING_STATE, INVOICE_WITHHOLDING_TAX_CONDITION, INVOICE_WITHHOLDING_TYPE, InterestAdmin, InterestMember, InterestPublic, JARVIS_API_END_POINT_MEMBER_REPORTS, JARVIS_MEMBER_REPORTS_TYPE, JARVIS_PREFIX, JarvisMember, KAIBACORP_API_END_POINT_COMERCIAL_RULES_STATUS, KAIBACORP_API_END_POINT_CONFIRM_REPORT_CARD, KAIBACORP_API_END_POINT_MEMBER_CARDS, KAIBACORP_API_END_POINT_MEMBER_CARDS_ACTIVATE, KAIBACORP_API_END_POINT_MEMBER_TRANSACTIONS, KAIBACORP_API_END_POINT_PUBLIC_CARDS_TYPES, KAIBACORP_API_END_POINT_PUBLIC_FEES, KAIBACORP_API_END_POINT_REPORT_CARD, KAIBACORP_API_END_POINT_RESTRICTION_RULES_STATUS, KAIBACORP_API_END_POINT_SHIPPING_DETAILS, KAIBACORP_PREFIX, KAIBACORP_SERVICE_NAME, KAIBA_TRANSACTION_TYPE, KIND_VALUES, KaibacorpMember, KaibacorpPublic, KarasuMember, KarasuPublic, LABELS_SCOPE, LEGAL_NOTICES_COUNTRY_TYPES, LEGAL_NOTICES_CURRENCY_TYPES, LIMITS_APPLIES_TO, LIVENESS_MESSAGES, LoginMethods, LoginSteps, MARITAL_STATUSES, MEMBER_CUSTOMER_STATUS, MEMBER_PERSON_STATUS, MEMBER_PHOTO_STATUS, MEMBER_VIDEO_STATUS, MEMENTO_API_END_CSV_RESUME, MEMENTO_API_END_PDF_RESUME, MEMENTO_API_END_POINT_HISTORY, MEMENTO_PREFIX, MERK_ACCOUNT_INFO_TAX_CONDITION, MERK_ACTIVITY_DEPOSIT_WITHDRAWAL_STATUS, MERK_ACTIVITY_FILTER_BY_DATE, MERK_ACTIVITY_NON_TRADE_STATUS, MERK_ACTIVITY_ORDER_STATUS, MERK_ACTIVITY_SUB_TYPE, MERK_ACTIVITY_TYPE, MERK_ASSET_STATUS, MERK_ASSET_TYPE, MERK_CREDIT_ACCOUNT_MOVEMENT_RECORD_TYPE, MERK_DEBIT_ACCOUNT_MOVEMENT_RECORD_TYPE, MERK_DOCUMENT_TYPE, MERK_ORDER_SIDE, MERK_ORDER_STATUS, MERK_ORDER_TYPE, MERK_ORDER_TYPES, MERK_PROFILE_INVESTOR_TYPES, MERK_PROFILE_QUESTIONS_TYPES, MERK_WITHDRAW_FROM_PROVIDER_STATUS, MERK_WITHDRAW_FROM_PROVIDER_TYPE, MERK_WIZARD_TYPE, ManagementBoundary, MemberBoundary, MementoManagement, MementoMember, MerkMember, MerkPublic, NaturalAccountStatus, OCCUPATIONS, ONBOARDING_FLOW, OPERATIONS_VALUES, ORDER_SUBSCRIPTION_PAYMENTS, PARTNER_API_END_PIONT_DEPOSIT_ADDRESS, PARTNER_API_END_PIONT_HISTORY_MARKET, PARTNER_API_END_PIONT_HISTORY_PLATFORM, PARTNER_API_END_POINT_ASSOCIATED_BROKERS, PARTNER_API_END_POINT_ASSOCIATE_TO_BROKER, PARTNER_API_END_POINT_MEMBERS, PARTNER_PREFIX, PAYMENTS_DEPOSIT_TYPE, PAYMENTS_PREFIX, PAYMENTS_SERVICE_NAME, PAYMENTS_WITHDRAW_TYPE, PAYMENTS_WITHHOLDING_TYPE, PAYMENT_API_END_POINT_BANKS, PAYMENT_API_END_POINT_BATCH_WITHDRAW, PAYMENT_API_END_POINT_BATCH_WITHDRAW_DETAILS, PAYMENT_API_END_POINT_BATCH_WITHDRAW_PROCESS, PAYMENT_API_END_POINT_BENEFICIARIES, PAYMENT_API_END_POINT_COMMERCIAL_RULES, PAYMENT_API_END_POINT_CURRENCIES, PAYMENT_API_END_POINT_DEPOSITS, PAYMENT_API_END_POINT_DEPOSIT_ADDRESS, PAYMENT_API_END_POINT_DEPOSIT_DATA, PAYMENT_API_END_POINT_EXTERNAL_PAYMENT_ADDRESSES, PAYMENT_API_END_POINT_LIMITS, PAYMENT_API_END_POINT_MEMBER_LIMITS, PAYMENT_API_END_POINT_MEMBER_TRANSFER_REQUEST, PAYMENT_API_END_POINT_PARTNER_MEMBERS, PAYMENT_API_END_POINT_PUBLIC_CURRENCIES, PAYMENT_API_END_POINT_PUBLIC_LIMITS, PAYMENT_API_END_POINT_PUBLIC_WITHDRAW_CONCEPTS, PAYMENT_API_END_POINT_WITHDRAWS, PAYMENT_CONDITIONS_OPTIONS, PAYMENT_STATUS_SUBSCRIPTION_PAYMENT, PEATIO_API_END_POINT_ACCOUNT_BALANCES, PEATIO_API_END_POINT_ACCOUNT_DEPOSIT_ADDRESS, PEATIO_API_END_POINT_ACCOUNT_MEMBERS_ME, PEATIO_API_END_POINT_ADMIN_MEMBERS, PEATIO_API_END_POINT_ADMIN_ORDERS, PEATIO_API_END_POINT_MARKET_ORDERS, PEATIO_API_END_POINT_MARKET_TRADES, PEATIO_API_END_POINT_PUBLIC_CURRENCIES, PEATIO_API_END_POINT_PUBLIC_MARKETS, PEATIO_API_END_POINT_PUBLIC_TICKERS, PEATIO_API_END_POINT_PUBLIC_TRADING_FEES, PEATIO_ORDER_TYPE, PEATIO_PREFIX, PEATIO_SERVICE_NAME, PERMISSION_STATE, PERSON_TYPE, PERSON_TYPES, PRECOGS_CONFIG_ASSETS_CATEGORIES_TYPE, PRECOGS_CONFIG_ASSETS_CATEGORIES_VIEW, PRECOGS_FLAG_FORM_TYPE, PRECOGS_FLAG_TYPE, PRECOGS_USER_REFERRER_TYPE, PRECOGS_USER_ROLES, PRECOGS_USER_TAGS, PRODUCT_NAME_TERMS_AND_CONDITION, PartnerBoundary, PaymentsMember, PaymentsPartner, PaymentsPublic, PeatioAdmin, PeatioMember, PeatioPublic, Platform, PrecogsMember, PrecogsPublic, PublicBoundary, RECHARGE_TYPE_COMPANY, RECORD_STATE, REFERRAL_PROGRAM_PAYOUT_TYPE, REFERRAL_PROGRAM_SERVICE_NAME, REFERRER_API_END_POINT_REFERRER_DATA, REFERRER_API_END_POINT_REFERRER_PAYOUTS, REFERRER_PREFIX, REMAINING_LIMITS_CATEGORY, REWARDS_API_ENDPOINT_CAMPAIGNS, REWARDS_API_ENDPOINT_LIMITS, REWARDS_API_ENDPOINT_PAYMENTS, REWARDS_API_ENDPOINT_PAYMENTS_PAID, REWARDS_API_ENDPOINT_PROMOTION_CODES, REWARDS_API_ENDPOINT_REFEREES, REWARDS_API_ENDPOINT_REFERRAL, REWARDS_CAMPAINGS, REWARDS_PAYMENTS_TYPE, REWARDS_PREFIX, REWARDS_SERVICE_NAME, REWARD_PAYMENT_STATUS_OPTIONS, ROUTE_TYPE, RULE_EVENT_ID, RULE_EVENT_STATUS, RULE_EXECUTION_STATUS, RULE_STATUS, RULE_TARJET_STATUS, ReferrerMember, RewardsMember, SR_BARRIGA_PAYMENT_TYPE, SR_BARRIGA_SERVICE_NAME, SUBSCRIPTION_PAYMENTS_API_ENDPOINT_CATEGORIES, SUBSCRIPTION_PAYMENTS_API_ENDPOINT_COMPANIES, SUBSCRIPTION_PAYMENTS_API_ENDPOINT_COMPANIES_MAIN, SUBSCRIPTION_PAYMENTS_API_ENDPOINT_DEBTS, SUBSCRIPTION_PAYMENTS_API_ENDPOINT_DEBTS_DETAILS, SUBSCRIPTION_PAYMENTS_API_ENDPOINT_DEBTS_DETAILS_BARCODE, SUBSCRIPTION_PAYMENTS_API_ENDPOINT_INVOICES, SUBSCRIPTION_PAYMENTS_API_ENDPOINT_MEMBERS, SUBSCRIPTION_PAYMENTS_API_ENDPOINT_PAYMENTS, SUBSCRIPTION_PAYMENTS_API_ENDPOINT_SUBSCRIPTIONS, SUBSCRIPTION_PAYMENTS_PREFIX, SUBSCRIPTION_STATUS_SUBSCRIPTION_PAYMENTS, SWAP_STATUS, ServiceEnvEnum, SnakeMember, SubscriptionPaymentsMember, TIME_PERIOD_VALUES, TRADE_SIDES, TYPE_COMPANY, TokenType, TransferDocumentStatus, TransferRequestBankMovementStatus, TransferRequestStatus, TransferRequestType, USER_DOCUMENTATION_TYPE, USER_LEVEL, USER_STATE, VERIFICATION_ENTERPRISE_STEPS, VERIFICATION_FLOW_VERSION, VERIFICATION_REJECTED_REASONS, VERIFICATION_STEPS, WITHHOLDINGS_MEMBER_INVOICE, WITHHOLDINGS_MEMBER_TAXES, WITHHOLDINGS_PREFIX, WITHHOLDINGS_PUBLIC_TAXES, WITHHOLDINGS_SERVICE_NAME, WITHHOLDINGS_WITHHOLDINGS_TYPE, WITHHOLDINGS_WITHHOLDING_STATUS, WithdrawOperationState, WithdrawRequestDetailsState, WithdrawRequestState, WithholdingsMember, WithholdingsPublic };
//# sourceMappingURL=index.esm.js.map
